<script>
import axios from "axios";
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { sDs } from "@common/sDs/sDs.js";
import { componentStore } from "@common/componentStore/componentStore.js";
import { configStore } from "@common/configStore";
import { library } from "@common/library/library";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const editorSettings = {
    templateHash: {
        label: 'Template',
        id: 'templateHash',
        type: 'pdf',
        default: '',
        'is-inline': false,
        mode: 'editor',
        required: true
    },
    isInline: {
        label: 'Use inline',
        id: 'isInline',
        type: 'boolean',
        helpText: 'PDF viewer can be shown inline or in a popup.',
        default: false
    },
    mode: {
        label: 'Type',
        id: 'mode',
        type: 'dropdown',
        showCheckBox: false,
        items: [
            {
                'text': 'Form Editor',
                'value': 'editor'
            },
            {
                'text': 'Viewer',
                'value': 'viewer'
            }
        ],
        fields: {
            text: 'text', value: 'value'
        },
        default: 'viewer'
    },
    disableFields: {
        label: 'Disable fields',
        id: 'disableFields',
        type: 'boolean',
        helpText: 'Disable fields after PDF is saved.',
        default: true
    }
};

const pdfCfg = {
    cId: "pdf",
    cType: "pdf",
    cTitle: "PDF",
    cTemplate: `<div v-if="!destroyed" :id="'pdfControlWrapper' + realWrapperID">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Allura" >
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Tangerine">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Sacramento">
                    <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Inspiration">
                    <div v-if="!useNewModules" :id="realWrapperID" class="pdf-spinner"></div>
                    <div :id="realWrapperID + 'new'" class="pdf-spinner"></div>
                    <div v-if="realMode === 'viewer'" class="pdf-viewer-mode" :class="{'pdf-only': isPdfOnly, 'is-last-pdf': isLastPdf, 'is-initial': isInitial}" :style="{'min-width1': !isPdfOnly ? minWidth : false}">
                        <div v-if="realHash">
                            <div v-if="realIsInline">
                                <div v-if="show">
                                    <div v-if="isPdfFormPrepared && !isPdfOnly && navigationFieldsWithoutValue" class="sticker" :class="'signer-color-' + (signerIndexInt + 1)">
                                        <div @click="goToNextNavigationField">
                                            <span>{{navigationFieldsText}}</span>
                                        </div>
                                    </div>
                                    <ejs-pdfviewer
                                                   v-bind="config"
                                                   :width="isPdfOnly && !isInitial ? '100%' : (isInitial ? '100px' : config.height || 'auto')"
                                                   :height="isPdfOnly && !isInitial ? '150px' : (isInitial ? '50px' : config.height || '90vh')"
                                                   :isSignatureEditable="false"
                                                   :id="id"
                                                   :data-test-id="id"
                                                   :ref="id"
                                                   :resourceUrl="resourceUrl"
                                                   :serviceUrl="serviceUrl"
                                                   :documentLoad="documentLoad"
                                                   :documentLoadFailed="documentLoadFailed"
                                                   :formFieldMouseover="formFieldMouseover"
                                                   :ajaxRequestFailed="ajaxRequestFailed"
                                                   :downloadFileName="fileName || templateName || null"
                                                   :showNotificationDialog="false"
                                                   :enablePrint="true"
                                                   :enableBookmark="false"
                                                   :enableCommentPanel="false"
                                                   :created="created"
                                                   :signatureFieldSettings="signatureFieldSettings"
                                                   :initialFieldSettings="initialFieldSettings"
                                                   :formFieldClick="formFieldClick"
                                                   :enableAnnotation="true"
                                                   :enableAnnotationToolbar="false"
                                                   :contextMenuOption="'None'"
                                                   :zoomValue="zoomValue"
                                                   @drop="controlDragEnd($event)"
                                                   @dragover.prevent
                                                   @dragenter.prevent
                                                   :zoomChange="zoomChange"
                                                   :ajaxRequestInitiate="ajaxRequestInitiate"
                                                   @ajaxRequestSuccess="ajaxRequestSuccess($event);"
                                                   :pageRenderComplete="preparePdfForm"
                                                   :formFieldPropertiesChange="formFieldPropertiesChange"
                                                   :annotationSettings="annotationSettings"
                                                   :pageChange="pageChange"
                                                   :enableMagnification="enableMagnification"
                                                   :minZoom1="30"
                                                   :maxZoom1="200"
                                                   :zoomMode="'FitToWidth'"
                                                   :initialRenderPages="initialRenderPages"
                                                   :disableContextMenuItems="true"
                                    >
                                    </ejs-pdfviewer>
                                    <control name="SIGNATURE_CONTEXT_MENU"></control>
                                </div>
                            </div>
                            <div v-else>
                                <a :id="this.cId + '_viewDocumentLink'" v-if="!fileNotLoaded" @click="edit">{{fileName ? fileName : 'View the document'}}</a>
                            </div>
                            <div v-if="fileNotLoaded">
                                Template file is not loaded. Please contact your administrator.
                            </div>
                        </div>
                        <div v-else>
                            <div style="display: none"> PDF component not rendered because no template file is set up </div>
                        </div>
                    </div>
                    <div v-else class="is-editor">
                        <div v-show="realMode === 'editor'" class="pdf-templates-wrapper">
                            <div class="pdf-templates" v-if="templatesLoaded">
                                <control name="PDF_TEMPLATES" :items="templates" :value="realHash"></control>
                            </div>
                            <div class="pdf-templates-actions">
                                <control name="PDF_TEMPLATES_ACTIONS" :items="actions"></control>
                            </div>
                        </div>
                        <div v-if="realIsInline">
                            <div v-if="show">
                                <control name="TEMPLATE_NAME"></control>
                            </div>
                            <div id="pdf-area">
                                <div v-if="( selected || isNew ) && show" id="pdf-wrapper">
                                    <div v-if="isNew && !pdfLoaded" class="upload-file-wrapper" @click="openFilesBrowser">
                                        <a>Click here to upload PDF file.</a>
                                    </div>
                                    <teleport v-if="aligmentContainer" :to="aligmentContainer">
                                        <button @click="align('left')" class="e-tbar-btn e-tbtn-txt e-control e-btn e-lib e-pv-tbar-btn e-tooltip" type="button" id="pdf_control_align_left" tabindex="-1" aria-label="Align Left" :aria-disabled="aligmentDisabled" :disabled="aligmentDisabled">
                                            <span class="fa fa-objects-align-left" id="pdf_control_align_left"></span>
                                            <span v-show="false" class="e-tbar-btn-text" id="pdf_control_openText">Align Left</span>
                                        </button>
                                        <button @click="align('right')" class="e-tbar-btn e-tbtn-txt e-control e-btn e-lib e-pv-tbar-btn e-tooltip" type="button" id="pdf_control_align_right" tabindex="-1" aria-label="Align Right" :aria-disabled="aligmentDisabled" :disabled="aligmentDisabled">
                                            <span class="fa fa-objects-align-right" id="pdf_control_align_right"></span>
                                            <span v-show="false" class="e-tbar-btn-text" id="pdf_control_openText">Align Right</span>
                                        </button>
                                        <button @click="align('top')" class="e-tbar-btn e-tbtn-txt e-control e-btn e-lib e-pv-tbar-btn e-tooltip" type="button" id="pdf_control_align_top" tabindex="-1" aria-label="Align Top" :aria-disabled="aligmentDisabled" :disabled="aligmentDisabled">
                                            <span class="fa fa-objects-align-top" id="pdf_control_align_top"></span>
                                            <span v-show="false" class="e-tbar-btn-text" id="pdf_control_openText">Align Top</span>
                                        </button>
                                        <button @click="align('bottom')" class="e-tbar-btn e-tbtn-txt e-control e-btn e-lib e-pv-tbar-btn e-tooltip" type="button" id="pdf_control_align_bottom" tabindex="-1" aria-label="Align Bottom" :aria-disabled="aligmentDisabled" :disabled="aligmentDisabled">
                                            <span class="fa fa-objects-align-bottom" id="pdf_control_align_bottom"></span>
                                            <span v-show="false" class="e-tbar-btn-text" id="pdf_control_openText">Align Bottom</span>
                                        </button>
                                    </teleport>
                                    <div class="form-fields-selection"></div>
                                    <ejs-pdfviewer
                                                   v-bind="config"
                                                   :id="id"
                                                   :data-test-id="id"
                                                   :ref="id"
                                                   :resourceUrl="resourceUrl"
                                                   :serviceUrl="serviceUrl"
                                                   :documentLoad="documentLoad"
                                                   :documentLoadFailed="documentLoadFailed"
                                                   :ajaxRequestFailed="ajaxRequestFailed"
                                                   :downloadFileName="fileName || null"
                                                   :showNotificationDialog="false"
                                                   :enablePrint="true"
                                                   :enableAnnotation="true"
                                                   :enableAnnotationToolbar="true"
                                                   :enableBookmark="false"
                                                   :enableCommentPanel="false"
                                                   :initialDialogSettings="{hideSaveSignature: true}"
                                                   :created="created"
                                                   :formFieldClick="formFieldClick"
                                                   :formFieldSelect="formFieldSelect"
                                                   :formFieldUnselect="formFieldUnselect"
                                                   :pageClick="pageClick"
                                                   :formFieldDoubleClick="formFieldDoubleClick"
                                                   :annotationSelect="annotationSelect"
                                                   :contextMenuOption="'None'"
                                                   :zoomValue="zoomValue"
                                                   :formFieldRemove="formFieldRemove"
                                                   :formFieldAdd="formFieldAdd"
                                                   @drop="controlDragEnd($event)"
                                                   @dragover.prevent
                                                   @dragenter.prevent
                                                   :zoomChange="zoomChange"
                                                   :ajaxRequestInitiate="ajaxRequestInitiate"
                                                   :pageChange="pageChange"
                                                   @ajaxRequestSuccess="pageRenderComplete($event);"
                                                   :pageRenderComplete="preparePdfForm"
                                                   :enableMagnification="false"
                                                   :initialRenderPages="initialRenderPages"
                                                   :formFieldPropertiesChange="formFieldPropertiesChange"
                                    >
                                    </ejs-pdfviewer>
                                    <control v-if="realMode !== 'editor-popup'" name="TEMPLATE_SAVE"></control>
                                </div>
                                <div v-if="show" id="fields-wrapper">
                                    <div class="signers-wrapper">
                                        <div v-if="!editingSigners">
                                            <control name="SIGNER" :items="signerItems" :value="selectedSignerIndex"></control>
                                            <control name="SIGNERS_EDIT"></control>
                                        </div>
                                        <div v-else>
                                            <control name="SET_SIGNING_ORDER" :value="signingOrder"  class="set-signing-order-checkbox"></control>
                                            <div class="clearfix"></div>
                                            <control v-if="!reloadSigners" name="SIGNERS" :items="signers"></control>
                                            <div class="clearfix">
                                                <div v-show="tmpTemplateSigners.length < 10" class="sub-list-signer-item-container">
                                                    <div class="signers-list-main-details">
                                                        <div>
                                                            <control name="ADD_SIGNER"></control>
                                                            <div class="clearfix"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="signers-actions">
                                                <control name="SIGNERS_SAVE"></control>
                                                <control name="SIGNERS_CANCEL"></control>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!editingSigners">
                                        <control name="FIELDS_LIST" :items="fieldsItems"></control>
                                        <control name="CONTROLS_LIST" :items="controlsItems"></control>
                                        <div v-if="selectedFieldGroup === 3">
                                            <control name="INSERT_FOR_TIER" :value="insertForTierValue" @update:value="insertForTierValue = $event"></control>
                                        </div>
                                        <control name="INSERT"></control>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="controlName" v-show="false">
                        <input type="hidden" :name="controlName + '_name'" :id="controlName + '_name'" :value="fileName" />
                        <input type="hidden" :name="controlName" :id="controlName" :value="attachId" />
                        <input v-if="!disabled" type="hidden" :name="controlName + '_file'" :id="controlName + '_file'"/>
                        <input v-if="!disabled" type="hidden" :name="controlName + '_base64file'" :id="controlName + '_base64file'"/>
                        <input type="hidden" :name="controlName + '_afterSaveIsChecked'" :id="controlName + '_afterSaveIsChecked'" :value="afterSaveIsChecked"/>
                    </div>
                    <div class="line vue-pdf-forced-controls" v-for="(forceControl, index) in forceControls" :key="index">
                        <div v-if="forceControl.listType > 0">
                            <div v-if="forceControl.listType == 0">
                                <select :name="forceControl.name" :id="forceControl.name">
                                    <option :value="forceControl.value" selected="selected">{{forceControl.value}}</option>
                                </select>
                            </div>
                            <div v-if="forceControl.listType == 1">
                                <input type="radio" :name="forceControl.name" :value="forceControl.value" :id="forceControl.name + '_' + forceControl.value" checked="checked">
                            </div>
                            <div v-if="forceControl.listType == 2">
                                <input type="checkbox" :name="forceControl.name" :value="forceControl.value" :id="forceControl.name" checked="checked">
                            </div>
                        </div>
                        <div v-else>
                            <input type="hidden" :name="forceControl.name" :value="forceControl.value" />
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: "SIGNATURE_CONTEXT_MENU",
            type: "contextmenu",
            items: [
                {
                    text: 'Clear'
                }
            ],
            onclick: "clearSignature",
            animationSettings: {
                duration: 0,
                // easing: 'ease',
                effect: 'SlideDown'
            }
        },
        {
            name: "SET_SIGNING_ORDER",
            type: "checkbox",
            label: "Set signing order",
            onchange: "setSigningOrder",
            helpText: 'When "Set signing order" checkbox is checked, email will be sent sequentially, otherwise simultaneously to all signers.',
            showWithEmptyLabel: true
        },
        {
            name: "SIGNERS_EDIT",
            type: "button",
            label: "Edit",
            onclick: "editSigners",
            width: '60px'
        },
        {
            name: "SIGNERS_SAVE",
            type: "button",
            label: "Save Signers",
            onclick: "saveSigners",
            cssClass: 'e-success',
        },
        {
            name: "SIGNERS_CANCEL",
            type: "button",
            label: "Cancel",
            onclick: "cancelSigners",
            cssClass: 'e-danger',
        },
        {
            name: 'SIGNER',
            type: 'dropdown',
            label: 'Signer',
            onchange: 'changeSigner',
            fields: { text: 'text', value: 'value' },
            default: 0,
            width: '240px',
            showClearButton: false,
            removeOnDestroy: true,
            cssClass: 'pdf-signer-dd',
            itemTemplate: '<span class="e-list-item-header signer-list-item" ><div class="signer-dropdown-color" :style="{\'background-color\': data.color}"></div><div>{{data.text}}</div></span>',
            valueTemplate: '<span class="e-list-item-header signer-list-item" ><div class="signer-value-color" :style="{\'background-color\': data.color}"></div><div class="signer-value">{{data.text}}</div></span>',
        },
        {
            name: 'SIGNER_TYPE',
            type: "dropdown",
            fields: { text: 'text', value: 'value' },
            items: [
                {
                    text: 'Dynamic signer',
                    value: 0
                },
                {
                    text: 'Static signer',
                    value: 1
                },
            ],
            width: '110px',
            showClearButton: false,
            removeOnDestroy: true,
            onchange: 'changeSignerType'
        },
        {
            name: 'SIGNER_ID',
            type: "dropdowntree",
            filterType: 'Contains',
            allowFiltering: true,
            fields: {
                text: 'text',
                value: 'value',
                hasChildren: 'hasChild',
                parentValue: 'pId'
            },
            width: '270px',
            notShowMissingOptions: true,
            removeOnDestroy: true,
            onchange: 'changeSignerId'
        },
        {
            name: 'SIGNER_EMAIL',
            type: 'string',
            onchange: 'changeSignerEmail',
            removeOnDestroy: true,
            width: '270px'
        },
        {
            name: 'SIGNER_EST',
            type: "dropdowntree",
            filterType: 'Contains',
            allowFiltering: true,
            fields: {
                text: 'text',
                value: 'value',
                hasChildren: 'hasChild',
                parentValue: 'pId'
            },
            onchange: 'changeSignerEST',
            removeOnDestroy: true,
            width: '386px',
            placeholder: 'Assign email template',
            itemsSource: 'configStore.allESTOptions("vol")',
        },
        {
            name: 'SIGNER_ORDER',
            type: "button",
            label: '',
            width: '30px',
            cssClass: 'e-link',
            removeOnDestroy: true,
            iconCss: 'e-icons e-delete-icon'
        },
        {
            name: 'DELETE_SIGNER',
            type: "button",
            label: '',
            width: '30px',
            cssClass: 'e-link',
            onclick: 'deleteSigner',
            iconCss: 'e-icons e-delete-icon'
        },
        {
            name: 'ADD_SIGNER',
            type: "button",
            label: 'Add Signer',
            width: '100%',
            onclick: 'addSigner'
        },
        {
            name: 'SIGNERS',
            type: 'list',
            isDraggable: false,
            onchangeorder: 'updateSignersAfterDnD',
            itemContentTemplate: `<div v-if="data" class="sub-list-signer-item-container" :class="{'signer-current-email': (data.CONTACT_TYPE_ID == 7 && data.SIGNER_PSP === 101) || data.key === 0}">
                            <div class="signer-color" :style="{'background-color': data.color}"></div>
                            <div class="signers-list-main-details">
                                <div v-if="data.emailValue === '[CURRENT_EMAIL]'">
                                    <div v-show="controlValue('SET_SIGNING_ORDER')" class="control drag-and-drop-signers"><span class="e-btn-icon e-icons e-drag-and-drop-indicator"></span></div>
                                    <div class="hidden">
                                        <control v-show="false" name="SIGNER_TYPE" :value="data.isStatic" :actionParams="data.key" :disabled="data.disabled"></control>
                                        <control v-show="false" v-if="!data.isStatic" name="SIGNER_ID" :items="data.signers" :value="data.idValue" :actionParams="data.key" :disabled="data.disabled" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                        <control v-show="false" v-if="data.isStatic" name="SIGNER_EMAIL" :value="data.emailValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    </div>
                                    <div class="current-volunteer-email">Volunteer Email</div>
                                    <control v-if="data.isStatic && data.key !== 0" name="SIGNER_EST" :value="data.estValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <div class="clearfix"></div>
                                </div>
                                <div v-else>
                                    <div v-show="controlValue('SET_SIGNING_ORDER')" class="control drag-and-drop-signers"><span class="e-btn-icon e-icons e-drag-and-drop-indicator"></span></div>
                                    <control name="SIGNER_TYPE" :value="data.isStatic" :actionParams="data.key" :disabled="data.disabled"></control>
                                    <control v-if="!data.isStatic" name="SIGNER_ID" :items="data.signers" :value="data.idValue" :actionParams="data.key" :disabled="data.disabled" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <control v-if="data.isStatic" name="SIGNER_EMAIL" :value="data.emailValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <control name="DELETE_SIGNER" :actionParams="data.key" :display="!data.disabled"></control>
                                    <control v-show="!(data.CONTACT_TYPE_ID == 7 && data.SIGNER_PSP === 101) && data.key !== 0" name="SIGNER_EST" :value="data.estValue" :disabled="data.disabled" :actionParams="data.key"></control>
                                    <div class="clearfix"></div>
                                </div>
                            </div>
                            <div class="clearfix"></div>
                        </div>`
        },
        {
            name: 'PDF_TEMPLATES',
            type: "dropdown",
            placeholder: "Select a template",
            onchange: "templateChanged",
            filterBarPlaceholder: "Search here",
            popupHeight: "220px",
            fields: { text: 'text', value: 'value' },
            allowFiltering: true
        },
        {
            name: 'PDF_TEMPLATES_ACTIONS',
            type: 'dropdownbutton',
            onselect: 'templateActions',
            cssClass: 'e-block'
        },
        {
            name: 'TEMPLATE_NAME',
            type: 'string',
            label: 'Template Name'
        },
        {
            name: 'TEMPLATE_SAVE',
            type: 'button',
            label: 'Save',
            onclick: 'save'
        },
        {
            name: 'FIELDS_LIST',
            type: 'listview',
            allowSearch: true,
            headerTitle: 'Fields',
            fields: { text: 'text', value: 'value' },
            isDraggable: true,
            ondragstart: 'fieldDragStart',
            onclick: 'fieldSelect',
            showHeader: true,
            ondblclick: 'controlInsert',
            itemTemplate: '<span class="e-list-item-header" >{{data.text}}</span>',
            height: '300px'
        },
        {
            name: 'CONTROLS_LIST',
            type: 'listview',
            allowSearch: true,
            headerTitle: 'Controls',
            fields: { text: 'text', value: 'value' },
            isDraggable: true,
            ondragstart: 'controlDragStart',
            onclick: 'controlSelect',
            showHeader: true,
            ondblclick: 'controlInsert',
            itemTemplate: '<span class="e-list-item-header" >{{data.text}}</span>',
            height: '250px'
        },
        {
            name: 'INSERT',
            type: 'button',
            label: 'Insert',
            cssClass: "e-block e-info",
            onclick: 'controlInsert'
        },
        {
            name: 'INSERT_FOR_TIER',
            type: 'dropdown',
            label: 'Insert For Tier:',
            placeholder: "Select a tier",
            filterBarPlaceholder: "Search here",
            popupHeight: "220px",
            fields: { text: 'text', value: 'value' },
            allowFiltering: true,
            itemsSource: 'configStore.attrListOptions("org_kind")',
            additionalOptions: [
                { text: 'Default (100-700)', value: 100700, absoluteOptionPosition: 0 },
                { text: 'Subprogram (1100-1700)', value: 11001700, absoluteOptionPosition: 1 },
            ]
        }
    ]
};

const pdfCmp = {
    name: 'pdf',
    editorSettings: editorSettings,
    preInitControls: true,
    props: {
        volId: {
            default: 0
        },
        oppId: {
            default: 0
        },
        orgId: {
            default: 0
        },
        memId: {
            default: 0
        },
        slotId: {
            default: 0
        },
        accId: {
            default: 0
        },
        affId: {
            default: 0
        },
        aeId: {
            default: 0
        },
        assignmentId: {
            default: 0
        },
        endpointId: {
            default: 0
        },
        lbeId: {
            default: 0
        },
        isInline: {
            default: undefined
        },
        hash: {
            default: ''
        },
        templateHash: {
            default: ''
        },
        mode: {
            default: undefined
        },
        recordId: {
            default: 0
        },
        pspId: {
            default: 0
        },
        fileName: {
            default: ''
        },
        controlName: {
            default: ''
        },
        attachId: {
            default: 0
        },
        isNew: {
            default: false
        },
        disableFields: {
            default: true
        },
        signerIndex: {
            default: false
        },
        doNotShowAgreement: {
            default: false
        }
    },
    data() {
        return {
            editingSigners: false,
            cursorPosition: {},
            destroyed: false,
            fileNotLoaded: false,
            templates: [],
            selected: false,
            actions: [
                { text: 'Edit Template', value: 'edit', disabled: true },
                { text: 'Delete Template', value: 'delete', disabled: true },
                { text: 'New Template', value: 'new' }
            ],
            show: false,
            controlsItems: [],
            fieldsItems: [],
            emailItems: [],
            base64: false,
            selectedGroup: false,
            selectedGroupName: false,
            selectedFieldGroup: false,
            selectedFieldGroupName: false,
            selectedControl: false,
            templatesLoaded: true,
            tryToAddInitialCount: 0,
            tryToAddInitialToSignatureCount: 0,
            tryToHideDisableCount: 0,
            signatureFieldSettings: {
                typeSignatureFonts: ['Allura', 'Tangerine', 'Sacramento', 'Inspiration'],
                "signatureIndicatorSettings": {
                    "opacity": 1,
                    "backgroundColor": "orange",
                    "width": 19,
                    "height": 10,
                    "fontSize": 10,
                    "text": null,
                    "color": "black"
                },
                "signatureDialogSettings": {
                    "displayMode": 7,
                    "hideSaveSignature": true
                }
            },
            initialFieldSettings: {
                typeInitialFonts1: ['Allura', 'Tangerine', 'Sacramento', 'Inspiration'],
                "initialIndicatorSettings": {
                    "opacity": 1,
                    "backgroundColor": "orange",
                    "width": 19,
                    "height": 10,
                    "fontSize": 10,
                    "text": null,
                    "color": "black"
                },
                "initialDialogSettings": {
                    "displayMode": 7,
                    "hideSaveSignature": true
                }
            },
            annotationSettings: {
                isLock: true
            },
            contextMenuSettings: {
                contextMenuItems: ['Delete']
            },
            wrapperID: '',
            pdfLoaded: false,
            foundedFields: [],
            prevFieldsValues: {},
            prevFieldsData: [],
            allowedTypes: ['FLOAT', 'STRING', 'TEXT', 'LIST', 'DATE', 'BOOLEAN', 'INTEGER'],
            notAllowedUDFTypes: [
                217, // dropdown with values
                214, // attachments
                220, // eSignature
                207, // divider
                218 // waiver choice
            ],
            canBeInsertedMultipleTimes: ['SIGNATURE', 'INITIAL', 'PDFLEADERINITIAL', 'PDFLEADERSIGNATURE', 'CURRENT_DATE', 'CURRENT_DATE_TIME'],
            forceControls: [],
            removeForceControls: [],
            usedControls: [],
            zoomValue: 100,
            formFielClicked: {},
            templateName: false,
            fileUniqueID: false,
            pdfOtherError: false,
            initialValue: '',
            pdfRequiredFields: [],
            templateSigners: [{
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                SIGNER_PSP: 1,
                SIGNER_ORDER: 1,
                SIGNER_EMAIL: "[CURRENT_EMAIL]",
                SIGNER_EST: null
            }],
            tmpTemplateSigners: [],
            selectedSignerIndex: 0,
            reloadSigners: false,
            signingOrder: false,
            pdfIsChanged: false,
            pdfFieldsChanged: false,
            uploadMaxFilesize: 10, // in MB
            isLastPdf: false,
            combinedFieldsByPsp: {
                /* 1: [
                    {
                        text: 'VOL::Full Name',
                        value: 'COMBINED_FULL_NAME',
                        combined: ['VOL_FIRST_NAME', 'VOL_MIDDLE_INITIAL', 'VOL_LAST_NAME']
                    },
                    {
                        text: 'VOL::Street Address',
                        value: 'COMBINED_STREET_ADDRESS',
                        combined: ['VOL_ADDRESS_1', 'VOL_ADDRESS_2']
                    },
                    {
                        text: 'VOL::City, State, Zip',
                        value: 'COMBINED_CITY_STATE_ZIP',
                        combined: ['VOL_CITY', 'VOL_STATE', 'VOL_POSTAL_CODE']
                    },
                    {
                        text: 'VOL::Full Address',
                        value: 'COMBINED_FULL_ADDRESS',
                        combined: ['VOL_ADDRESS_1', 'VOL_ADDRESS_2', 'VOL_CITY', 'VOL_STATE', 'VOL_POSTAL_CODE']
                    }
                ] */
            },
            previousFileFields: [],
            selectedFields: [],
            aligmentContainer: null,
            aligmentDisabled: true,
            insertForTierValue: null,
            otherErrorCount: 0,
            heightBeforeResize: 0,
            renderedPages: [],
            isPdfFormPrepared: false,
            agreementChecked: true,
            popupDocumentViewed: false,
            navigationFields: [],
            navigationFieldsText: 'Start',
            navigationFieldsWithoutValue: 0,
            afterSaveIsChecked: 0
        };
    },
    computed: {
        minWidth() {
            let minWidth = window.innerWidth;
            let parent = window.parent;
            for (let i = 0; i < 5; i++) {
                // check all frames
                if (parent.innerWidth < minWidth) {
                    minWidth = parent.innerWidth;
                }
                if (parent.name === top.name) {
                    break;
                }
                parent = parent.parent;
            }
            if (minWidth < 350) {
                minWidth = 350; // pdf can not be < 350
            } else if (minWidth > 900) {
                minWidth = 900; // if viewport width > 900 leave 900px
            } else {
                minWidth = false; // otherwise 100%
            }
            if (minWidth) minWidth += 'px';
            return minWidth;
        },
        enableMagnification() {
            return !this.isPdfOnly; // this.useNewModules && this.realMode === 'viewer';
        },
        initialRenderPages() {
            // render all pages for new syncfusion
            return this.useNewModules ? 9999 : 2;
        },
        insertForTier() {
            let insertForTier = false;
            if (this.selectedFieldGroup === 3 && this.insertForTierValue) {
                insertForTier = this.insertForTierValue;
            }
            return insertForTier;
        },
        combinedFields() {
            let tmpCombinedFields = {};
            for (let i in this.combinedFieldsByPsp) {
                for (let j in this.combinedFieldsByPsp[i]) {
                    tmpCombinedFields[this.combinedFieldsByPsp[i][j].value] = this.combinedFieldsByPsp[i][j];
                }
            }
            return tmpCombinedFields;
        },
        signerItems() {
            let signerItems = [];
            if (configStore.state && configStore.state.cfg && configStore.state.cfg.availableFields && this.templateSigners.length) {
                for (let i in this.templateSigners) {
                    let tmpSigner = {};
                    let pspName = 'vol';
                    switch (this.templateSigners[i].SIGNER_PSP) {
                        case 1:
                            pspName = 'vol';
                            break;
                        case 2:
                            pspName = 'opp';
                            break;
                        case 3:
                            pspName = 'org';
                            break;
                        case 10:
                            pspName = 'lbe';
                            break;
                        case 101:
                            pspName = 'client';
                            break;
                    }
                    if (typeof this.templateSigners[i].SIGNER_EMAIL === 'string' || this.templateSigners[i].SIGNER_PSP === 101) {
                        let tmpText = this.templateSigners[i].SIGNER_EMAIL;
                        if (this.templateSigners[i].SIGNER_EMAIL === '[CURRENT_EMAIL]') tmpText = 'Volunteer';
                        if (this.templateSigners[i].SIGNER_PSP === 101) tmpText = 'eC User';
                        tmpSigner = {
                            text: tmpText
                        };
                    } else {
                        if (parseInt(this.templateSigners[i].CUSTOM_ATTRIBUTE_ID, 10) > 0) {
                            if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.templateSigners[i].CUSTOM_ATTRIBUTE_ID]) {
                                tmpSigner = {
                                    text: configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.templateSigners[i].CUSTOM_ATTRIBUTE_ID].displayTitle
                                };
                            }
                        } else if (this.templateSigners[i].CONTACT_TYPE_ID && this.templateSigners[i].CONTACT_TYPE_REF_TABLE) {
                            let tmpRealName = pspName.toUpperCase() + '_CONTACT#' + this.templateSigners[i].CONTACT_TYPE_ID;
                            switch (this.templateSigners[i].CONTACT_TYPE_REF_TABLE) {
                                case 'M_VOL_EMERGENCY_INFO':
                                    tmpRealName = pspName.toUpperCase() + '_EM_CONTACT#' + this.templateSigners[i].CONTACT_TYPE_ID;
                                    break;
                            }
                            if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName][tmpRealName]) {
                                tmpSigner = {
                                    text: configStore.state.cfg.availableFields[pspName][tmpRealName].displayTitle
                                };
                            }
                        }
                    }
                    if (!library.objIsEmpty(tmpSigner)) {
                        tmpSigner.value = signerItems.length;
                        signerItems.push(tmpSigner);
                    }
                }
            }
            for (let i in signerItems) {
                signerItems[i].color = this.getSignerColor(i);
            }
            return signerItems;
        },
        signers() {
            let signers = [];
            for (let i in this.tmpTemplateSigners) {
                let tmpSigner = {};
                let pspName = 'vol';
                switch (this.tmpTemplateSigners[i].SIGNER_PSP) {
                    case 1:
                        pspName = 'vol';
                        break;
                    case 2:
                        pspName = 'opp';
                        break;
                    case 3:
                        pspName = 'org';
                        break;
                    case 10:
                        pspName = 'lbe';
                        break;
                    case 101:
                        pspName = 'client';
                        break;
                }
                if (typeof this.tmpTemplateSigners[i].SIGNER_EMAIL === 'string') {
                    tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                    tmpSigner.text = this.tmpTemplateSigners[i].SIGNER_EMAIL;
                    tmpSigner.emailValue = this.tmpTemplateSigners[i].SIGNER_EMAIL;
                    tmpSigner.isStatic = 1;
                } else {
                    if (parseInt(this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID, 10) > 0) {
                        if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID]) {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID].displayTitle;
                            tmpSigner.idValue = ['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID];
                            tmpSigner.isStatic = 0;
                        } else {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.SIGNER_PSP = 1;
                            tmpSigner.text = 'CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID;
                            tmpSigner.idValue = ['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID];
                            tmpSigner.isStatic = 0;
                        }
                    } else if ((this.tmpTemplateSigners[i].CONTACT_TYPE_ID && this.tmpTemplateSigners[i].CONTACT_TYPE_REF_TABLE) || this.tmpTemplateSigners[i].SIGNER_PSP === 101) {
                        let tmpRealName = pspName.toUpperCase() + '_CONTACT#' + this.tmpTemplateSigners[i].CONTACT_TYPE_ID;
                        switch (this.tmpTemplateSigners[i].CONTACT_TYPE_REF_TABLE) {
                            case 'M_VOL_EMERGENCY_INFO':
                                tmpRealName = pspName.toUpperCase() + '_EM_CONTACT#' + this.tmpTemplateSigners[i].CONTACT_TYPE_ID;
                                break;
                        }
                        if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName][tmpRealName]) {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = configStore.state.cfg.availableFields[pspName][tmpRealName].displayTitle;
                            tmpSigner.idValue = [tmpRealName];
                            tmpSigner.isStatic = 0;
                        } else if (this.tmpTemplateSigners[i].SIGNER_PSP === 101) {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = 'eC User';
                            tmpSigner.idValue = [tmpRealName];
                            tmpSigner.isStatic = 0;
                        }
                    }
                }
                if (!library.objIsEmpty(tmpSigner)) {
                    let signerEst = this.tmpTemplateSigners[i].SIGNER_EST ? [this.tmpTemplateSigners[i].SIGNER_EST + ''] : null;
                    tmpSigner.estValue = signerEst;
                    tmpSigner.color = this.getSignerColor(signers.length);
                    tmpSigner.signers = this.emailItems;
                    signers.push(tmpSigner);
                }
            }
            return signers;
        },
        isSurvey() {
            return this.realHash.length !== 64;
        },
        isPdfOnly() {
            let templateHash = this.templateHash || '';
            templateHash = templateHash.toString();
            return templateHash.toUpperCase() === 'E4999145B295C8D55D7C5EB4D3C6BE0B69C203330E5CA69F5CFB01537FB78ED3' || templateHash.toUpperCase() === 'E3228BEF221C46F760CA07F79D095898C8C25F23A29674DE410FFCB274E9C63C';
        },
        isInitial() {
            let templateHash = this.templateHash || '';
            templateHash = templateHash.toString();
            return templateHash.toUpperCase() === 'E3228BEF221C46F760CA07F79D095898C8C25F23A29674DE410FFCB274E9C63C';
        },
        useNewModules() {
            return true;
        },
        resourceUrl() {
            let resourceUrl = (typeof VUE_URL !== 'undefined' && VUE_URL ? VUE_URL.replace('/rc/', '/') : (typeof PROJECTS_URL !== 'undefined' ? PROJECTS_URL : '') + '/common/vue/') + 'ej2-pdfviewer-lib';
            if (typeof FROM_LOCALHOST !== 'undefined' && FROM_LOCALHOST) {
                resourceUrl = location.origin + '/common/vue/ej2-pdfviewer-lib';
            }
            return resourceUrl;
        },
        serviceUrl() {
            return null;
            /* // eslint-disable-next-line no-unreachable
            let serviceUrl = "https://devdash.samaritan.com/pdfViewer/api/pdfViewer";
            if (typeof IS_DEVDASH_QA !== 'undefined' && IS_DEVDASH_QA) {
                serviceUrl = "https://devdashqa.samaritan.com/pdfViewer/api/pdfViewer";
            }
            return serviceUrl; */
        },
        realWrapperID() {
            if (this.wrapperID === '') {
                this.wrapperID = this.cId + '_wrapper' + library.getUniqueID();
            }
            return this.wrapperID;
        },
        id() {
            let ref = this.dataTestId ? this.dataTestId + '_control' : '';
            return ref.replaceAll('__', '_').replaceAll('null', ''); // pdf-viewer is not working when id contains __ or null
        },
        pspIds() {
            // eslint-disable-next-line camelcase
            let vmsVolunteerID = typeof vue_volunteerID !== 'undefined' ? vue_volunteerID : 0;
            // eslint-disable-next-line camelcase
            let vmsOpportunityID = typeof vue_opportunityID !== 'undefined' ? vue_opportunityID : 0;
            // eslint-disable-next-line camelcase
            let vmsOrganizationID = typeof vue_organizationID !== 'undefined' ? vue_organizationID : 0;
            // eslint-disable-next-line camelcase
            let vmsMemberID = typeof vue_memberID !== 'undefined' ? vue_memberID : 0;
            // eslint-disable-next-line camelcase
            let vmsSlotID = typeof vue_slotID !== 'undefined' ? vue_slotID : 0;
            // eslint-disable-next-line camelcase
            let vmsAccID = typeof vue_accountID !== 'undefined' ? vue_accountID : 0;
            // eslint-disable-next-line camelcase
            let vmsLbeID = typeof vue_lbeID !== 'undefined' ? vue_lbeID : 0;
            // eslint-disable-next-line camelcase
            let vmsAffID = typeof vue_affID !== 'undefined' ? vue_affID : 0;
            // eslint-disable-next-line camelcase
            let vmsAeID = typeof vue_aeID !== 'undefined' ? vue_aeID : 0;
            // eslint-disable-next-line camelcase
            let vmsAssignmentID = typeof vue_assignmentID !== 'undefined' ? vue_assignmentID : 0;
            // eslint-disable-next-line camelcase
            let vmsEndpointID = typeof vue_endpointID !== 'undefined' ? vue_endpointID : 0;
            let pspId = parseInt(this.pspId, 10) || 0;
            if (!vmsVolunteerID && pspId === 1 && this.recordId) {
                vmsVolunteerID = this.recordId;
            }
            if (!vmsOpportunityID && pspId === 2 && this.recordId) {
                vmsOpportunityID = this.recordId;
            }
            if (!vmsOrganizationID && pspId === 3 && this.recordId) {
                vmsOrganizationID = this.recordId;
            }
            if (!vmsMemberID && pspId === 5 && this.recordId) {
                vmsMemberID = this.recordId;
            }
            if (!vmsLbeID && pspId === 10 && this.recordId) {
                vmsLbeID = this.recordId;
            }
            if (!vmsSlotID && pspId === 11 && this.recordId) {
                vmsSlotID = this.recordId;
            }
            if (!vmsAccID && pspId === 100 && this.recordId) {
                vmsAccID = this.recordId;
            }
            if (!vmsAffID && pspId === 32 && this.recordId) {
                vmsAffID = this.recordId;
            }
            if (!vmsAeID && pspId === 8 && this.recordId) {
                vmsAeID = this.recordId;
            }
            return {
                "vol": parseInt(this.volId, 10) || vmsVolunteerID || 0,
                "opp": parseInt(this.oppId, 10) || vmsOpportunityID || 0,
                "org": parseInt(this.orgId, 10) || vmsOrganizationID || 0,
                "mem": parseInt(this.memId, 10) || vmsMemberID || 0,
                "lbe": parseInt(this.lbeId, 10) || vmsLbeID || 0,
                "slot": parseInt(this.slotId, 10) || vmsSlotID || 0,
                "acc": parseInt(this.accId, 10) || vmsAccID || 0,
                "aff": parseInt(this.affId, 10) || vmsAffID || 0,
                "ae": parseInt(this.aeId, 10) || vmsAeID || 0,
                "assignment": parseInt(this.assignmentId, 10) || parseInt(this.affId, 10) || vmsAssignmentID || vmsAffID || 0,
                "endpoint": parseInt(this.endpointId, 10) || vmsEndpointID || 0
            };
        },
        realHash() {
            let realHash = this.hash || this.templateHash || this.config.templateHash || '';
            realHash = realHash.toString();
            return realHash;
        },
        realMode() {
            return this.mode || this.config.mode;
        },
        realIsInline() {
            return this.isInline || this.config.isInline;
        },
        signerIndexInt() {
            return parseInt(this.signerIndex, 10) || 0;
        },
        alwaysSave() {
            return true;
            /* let instanceName = typeof THE_CUSTOMER !== 'undefined' ? THE_CUSTOMER : '';
            return ['KP', 'DEBUG'].indexOf(instanceName) !== -1; */
        }
    },
    methods: {
        getCurrentFieldsOnDocument() {
            let result = [];

            this.$refs[this.id].ej2Instances.formFieldCollections.forEach((value, index) => {
                result.push({
                    id: value.id,
                    isChecked: value.isChecked,
                    isMultiline: value.isMultiline,
                    isPrint: value.isPrint,
                    isReadOnly: value.isReadOnly,
                    isRequired: value.isRequired,
                    isSelected: value.isSelected,
                    name: value.name,
                    tooltip: value.tooltip,
                    type: value.type,
                    value: value.value,
                    alignment: value.alignment,
                    backgroundColor: value.backgroundColor,
                    borderColor: value.borderColor,
                    color: value.color,
                    fontFamily: value.fontFamily,
                    fontSize: value.fontSize,
                    fontStyle: value.fontStyle
                });
            });

            return result;
        },
        getControlPrefix: function (object) {
            let prefixes = ['VOL', 'OPP', 'ORG', 'ACC', 'CLI', 'LBE'];
            let nameSplit = object.value.split('::', 2);
            let textSplit = object.text.split('::', 2);
            let namePrefixIndex = nameSplit.length ? prefixes.indexOf(nameSplit[0]) : -1;
            let textPrefixIndex = textSplit.length ? prefixes.indexOf(textSplit[0]) : -1;

            return namePrefixIndex === -1 && textPrefixIndex >= 0 ? (prefixes[textPrefixIndex] + '::') : '';
        },
        checkAligmentDisabled() {
            this.aligmentDisabled = this.selectedFields.length < 2;
        },
        move(event) {
            let isArrow = false;
            let addX = 0;
            let addY = 0;
            switch (event.key) {
                case "ArrowLeft":
                    isArrow = true;
                    addX = -1;
                    break;
                case "ArrowRight":
                    isArrow = true;
                    addX = 1;
                    break;
                case "ArrowUp":
                    isArrow = true;
                    addY = -1;
                    break;
                case "ArrowDown":
                    isArrow = true;
                    addY = 1;
                    break;
            }
            if (this.selectedFields.length && isArrow) {
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (forms[i].name === 'CURSOR_POSITION') continue;
                    if (this.selectedFields.indexOf(forms[i].id) !== -1) {
                        let tmpField = forms[i];
                        let x = tmpField?.bounds?.x || tmpField?.bounds?.X;
                        let y = tmpField?.bounds?.y || tmpField?.bounds?.Y;
                        let width = tmpField?.bounds?.width || tmpField?.bounds?.Width;
                        let height = tmpField?.bounds?.height || tmpField?.bounds?.Height;
                        let tmpXY = viewer.convertPagePointToScrollingPoint({ x: x, y: y }, tmpField.pageIndex + 1);
                        let scrollTop = 0;
                        let tmpViewerContainer = this.$el.getElementsByClassName('e-pv-viewer-container');
                        if (tmpViewerContainer && tmpViewerContainer?.[0].scrollTop > 0) scrollTop = tmpViewerContainer[0].scrollTop;
                        this.localUpdateFormField(forms[i], {
                            bounds: {
                                X: tmpXY.x + width / 2 + addX, Y: tmpXY.y + height / 2 + addY - scrollTop, Width: width, Height: height
                            },
                            force: true
                        });
                    }
                }
                event.preventDefault();
            }
        },
        align(alignBy) {
            if (this.selectedFields && this.selectedFields.length) {
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                let newX = 0;
                let newY = 0;
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (forms[i].name === 'CURSOR_POSITION') continue;
                    if (this.selectedFields.indexOf(forms[i].id) !== -1) {
                        let tmpField = forms[i];
                        let x = tmpField?.bounds?.x || tmpField?.bounds?.X;
                        let y = tmpField?.bounds?.y || tmpField?.bounds?.Y;
                        let width = tmpField?.bounds?.width || tmpField?.bounds?.Width;
                        let height = tmpField?.bounds?.height || tmpField?.bounds?.Height;
                        let tmpXY = viewer.convertPagePointToScrollingPoint({ x: x, y: y }, tmpField.pageIndex + 1);
                        switch (alignBy) {
                            case "left":
                                if (!newX || newX > x) newX = x;
                                break;
                            case "right":
                                if (!newX || newX < x + width) newX = x + width;
                                break;
                            case "top":
                                if (!newY || newY > y) newY = y;
                                break;
                            case "bottom":
                                if (!newY || newY < y + height) newY = y + height;
                                break;
                        }
                    }
                }

                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (forms[i].name === 'CURSOR_POSITION') continue;
                    if (this.selectedFields.indexOf(forms[i].id) !== -1) {
                        let tmpField = forms[i];
                        let x = tmpField?.bounds?.x || tmpField?.bounds?.X;
                        let y = tmpField?.bounds?.y || tmpField?.bounds?.Y;
                        let width = tmpField?.bounds?.width || tmpField?.bounds?.Width;
                        let height = tmpField?.bounds?.height || tmpField?.bounds?.Height;
                        let tmpXY = viewer.convertPagePointToScrollingPoint({ x: x, y: y }, tmpField.pageIndex + 1);
                        let tmpX = tmpXY.x + width / 2;
                        let tmpY = tmpXY.y + height / 2;
                        if (newX) {
                            if (alignBy === 'left') tmpX = newX + width / 2;
                            if (alignBy === 'right') tmpX = newX - width / 2;
                        }
                        if (newY) {
                            if (alignBy === 'top') tmpY = newY + height / 2;
                            if (alignBy === 'bottom') tmpY = newY - height / 2;
                        }
                        this.localUpdateFormField(forms[i], {
                            bounds: {
                                X: tmpX, Y: tmpY, Width: width, Height: height
                            },
                            force: true
                        });
                    }
                }
            }
        },
        redrawSelectedFields() {
            let tmpObjs = document.getElementsByClassName('pdf-field-multiselect');
            while (tmpObjs.length) {
                tmpObjs[0].classList.remove('pdf-field-multiselect');
            }
            for (let i in this.selectedFields) {
                let tmpObj = document.getElementById(this.selectedFields[i] + '_content_html_element');
                if (tmpObj) {
                    tmpObj.classList.add('pdf-field-multiselect');
                }
            }
        },
        formFieldUnselect: function (tmpEvent) {
            if (!(event.metaKey || event.ctrlKey)) {
                let viewer = this.$refs[this.id].ej2Instances;
                if (viewer.selectedItems.formFields.length) {
                    if (this.selectedFields.indexOf(viewer.selectedItems.formFields[0].id) === -1) this.selectedFields = [viewer.selectedItems.formFields[0].id];
                } else {
                    this.selectedFields = [];
                }
            }
            this.redrawSelectedFields();
            this.checkAligmentDisabled();
        },
        formFieldSelect: function (tmpEvent) {
            if (tmpEvent.field.name === 'CURSOR_POSITION') return;
            let viewer = this.$refs[this.id].ej2Instances;
            if (!this.selectedFields.length && viewer.selectedItems.formFields.length) {
                this.selectedFields.push(viewer.selectedItems.formFields[0].id);
            }
            if (event.metaKey || event.ctrlKey) {
                if (this.selectedFields.indexOf(tmpEvent.field.id) === -1) this.selectedFields.push(tmpEvent.field.id);
            }
            this.redrawSelectedFields();
            this.checkAligmentDisabled();
        },
        changeSignerId(event, field, index) {
            if (!event.e) return;
            let contactTypeID = null;
            let contactTypeRefTable = null;
            let customAttributeID = null;
            let idValue = field.value[0];
            let signerPSP = 1;
            if (idValue) {
                let item = this.emailItems.find((tmp) => tmp.value === idValue);
                if (item && item.additionalData && item.additionalData.PERSPECTIVE_ID) {
                    signerPSP = item.additionalData.PERSPECTIVE_ID;
                }
                if (idValue.indexOf('_CONTACT#') !== -1) {
                    let tmp = idValue.split('#');
                    if (tmp && tmp.length === 2) {
                        contactTypeID = parseInt(tmp[1], 10);
                        contactTypeRefTable = item.additionalData.CONTACT_TYPE_REF_TABLE;
                    }
                } else if (idValue.indexOf('CUSTOM_ATTRIBUTE#') !== -1) {
                    let tmp = idValue.split('#');
                    if (tmp && tmp.length === 2) {
                        customAttributeID = parseInt(tmp[1], 10);
                    }
                }
            }
            if (!contactTypeID && !customAttributeID) {
                customAttributeID = 1;
            }
            this.reloadSigners = true;
            this.tmpTemplateSigners[index] = {
                CONTACT_TYPE_ID: contactTypeID,
                CONTACT_TYPE_REF_TABLE: contactTypeRefTable,
                CUSTOM_ATTRIBUTE_ID: customAttributeID,
                ESIGNATURE_TEMPLATE_ID: this.tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: null,
                SIGNER_EST: this.tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: this.tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: signerPSP
            };
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        changeSignerEST(event, field, index) {
            if (!event.e) return;
            let value = !library.objIsEmpty(field.value) ? field.value[0] : null;
            let tmpSigner = library.cloneObj(this.tmpTemplateSigners[index]);
            tmpSigner.SIGNER_EST = value;
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                this.tmpTemplateSigners[index] = tmpSigner;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        changeSignerEmail(event, field, index) {
            if (!event.event) return;
            let value = field.value || '';
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            let tmpSigner = {
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                ESIGNATURE_TEMPLATE_ID: tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: value,
                SIGNER_EST: tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: 1
            };
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                tmpTemplateSigners[index] = tmpSigner;
                this.tmpTemplateSigners = tmpTemplateSigners;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        changeSignerType(event, field, index) {
            if (!event.e) return;
            let isStatic = field.value;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            let tmpSigner = {
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: !isStatic ? 1 : null,
                ESIGNATURE_TEMPLATE_ID: tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: isStatic ? '' : null,
                SIGNER_EST: tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: tmpTemplateSigners[index].SIGNER_PSP
            };
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                tmpTemplateSigners[index] = tmpSigner;
                this.tmpTemplateSigners = tmpTemplateSigners;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        deleteSignerControls(index) {
            if (this.attachId <= 0) return;
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (forms[i].name.indexOf('::SIGNER_' + index) !== -1) {
                    viewer.formDesignerModule.deleteFormField(viewer.formFieldCollections[i]);
                }
                if (index === 0 && forms[i].name.indexOf('::SIGNER_') === -1) {
                    viewer.formDesignerModule.deleteFormField(viewer.formFieldCollections[i]);
                }
            }
        },
        validateSignersFields() {
            let valid = true;
            if (this.realMode !== 'viewer' && this && this.$refs && this.$refs[this.id] && this.$refs[this.id].ej2Instances) {
                let templateSigners = library.cloneObj(this.signerItems);
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                if (templateSigners.length > 1) {
                    for (let i = 0; i < templateSigners.length; i++) {
                        let tmpIndex = parseInt(i, 10);
                        let tmpFormField = forms.find((tmpField) => (tmpIndex === 0 ? tmpField.name.indexOf('::SIGNER') === -1 : tmpField.name.indexOf('::SIGNER_' + tmpIndex) !== -1));
                        if (!tmpFormField) {
                            valid = false;
                            library.showDialog('Signer "' + templateSigners[i].text + '" has no controls in template. Please add at least one control or remove this signer.', false, false, { title: 'Warning', showAsAlert: true });
                            break;
                        }
                    }
                }
            }
            return valid;
        },
        validateSigners() {
            let valid = true;
            let templateSigners = library.cloneObj(this.tmpTemplateSigners);
            let usedEmails = [];
            let isEcExists = false;
            for (let i in templateSigners) {
                let isStatic = this.controls['SIGNER_TYPE__' + i] && this.controls['SIGNER_TYPE__' + i].value;
                let idValue = this.controls['SIGNER_ID__' + i] && this.controls['SIGNER_ID__' + i].value[0];
                let emailValue = this.controls['SIGNER_EMAIL__' + i] && this.controls['SIGNER_EMAIL__' + i].value;
                let tmpEmail;
                if (isStatic) {
                    tmpEmail = emailValue;
                    if (!tmpEmail) {
                        valid = false;
                        library.showDialog('Please fill email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    } else if (tmpEmail !== '[CURRENT_EMAIL]' && !library.checkEmail(tmpEmail)) {
                        valid = false;
                        library.showDialog(tmpEmail + ' is not valid Email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    }
                } else {
                    if (!idValue || idValue === 'CUSTOM_ATTRIBUTE#1') {
                        valid = false;
                        library.showDialog('Please select dynamic email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    } else {
                        tmpEmail = idValue;
                    }
                }
                if (tmpEmail && usedEmails.indexOf(tmpEmail) !== -1) {
                    valid = false;
                    library.showDialog('Please remove duplicate signers', false, false, { title: 'Warning', showAsAlert: true });
                    break;
                } else {
                    usedEmails.push(tmpEmail);
                }
                if (idValue === 'CLIENT_CONTACT#7') {
                    isEcExists = true;
                    if (parseInt(i, 10) !== 0) {
                        valid = false;
                        library.showDialog('eC User can be first signer only', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    }
                }
            }
            return valid;
        },
        addSigner() {
            let templateSigners = library.cloneObj(this.tmpTemplateSigners);
            if (!this.validateSigners()) return;
            this.reloadSigners = true;
            templateSigners.push({
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: 1,
                ESIGNATURE_TEMPLATE_ID: this.attachId,
                SIGNER_EMAIL: null,
                SIGNER_EST: null,
                SIGNER_ORDER: this.signingOrder ? (templateSigners.length) : 1,
                SIGNER_PSP: 1
            });
            this.tmpTemplateSigners = templateSigners;
            this.changeSigningOrder();
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        deleteSigner(event, field, index) {
            library.showDialog('Are you sure you want to delete this signer?<br/>All controls will be removed from pdf for this signer.', () => {
                this.deleteSignerControls(parseInt(index, 10));
                this.reloadSigners = true;
                let items = library.cloneObj(this.tmpTemplateSigners);
                items.splice(index, 1);
                this.tmpTemplateSigners = items;
                this.reloadControlColors(true);
                this.changeSigningOrder();
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }, false, { name: 'deleteSigner' });
        },
        getSignerColor(index, field) {
            let selectedIndex = this.selectedSignerIndex;
            if (typeof index !== 'undefined' && index !== false) {
                selectedIndex = index;
            }
            if (field && field.name.indexOf('::SIGNER_') === -1 && field.backgroundColor !== "#daeaf7ff") {
                // do not change color for the non signer fields VMS-9635
                let tmpColor = field.backgroundColor;
                if (typeof tmpColor === 'string' && tmpColor.startsWith('#') && tmpColor.length > 7) {
                    // remove opacity
                    tmpColor = tmpColor.substring(0, 7);
                    tmpColor += 'f';
                }
                return tmpColor;
            }

            selectedIndex = parseInt(selectedIndex, 10);
            let colors = [
                '#daeaf7',
                '#ffd65b',
                '#acdce6',
                '#c0a5cf',
                '#97c9bf',
                '#f7b994',
                '#c3d5e6',
                '#cfdb7f',
                '#ff9980',
                '#e6c6e6'
            ];
            return colors[selectedIndex % 10];
        },
        changeSigner(event) {
            this.selectedSignerIndex = event.value || 0;
        },
        setSigningOrder(event) {
            this.signingOrder = event.checked;
            this.reloadSigners = true;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            for (let i in tmpTemplateSigners) {
                tmpTemplateSigners[i].SIGNER_ORDER = this.signingOrder ? parseInt(i, 10) + 1 : 1;
            }
            this.tmpTemplateSigners = tmpTemplateSigners;
            if (this.controls && this.controls['SIGNERS']) {
                this.controls['SIGNERS'].isDraggable = this.signingOrder;
            }
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        editSigners() {
            this.editingSigners = true;
            let tmpTemplateSigners = this.templateSigners.length ? library.cloneObj(this.templateSigners) : [{
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                SIGNER_PSP: 1,
                SIGNER_ORDER: 1,
                SIGNER_EMAIL: "[CURRENT_EMAIL]",
                SIGNER_EST: null
            }];
            this.tmpTemplateSigners = library.cloneObj(tmpTemplateSigners);
            if (this.controls && this.controls['SIGNERS']) {
                this.controls['SIGNERS'].isDraggable = this.signingOrder;
            }
        },
        saveSigners() {
            let tmpSigners = [];
            if (!this.validateSigners()) return;
            for (let i in this.tmpTemplateSigners) {
                let idValue = this.controls['SIGNER_ID__' + i] && this.controls['SIGNER_ID__' + i].value[0];
                let emailValue = this.controls['SIGNER_EMAIL__' + i] && this.controls['SIGNER_EMAIL__' + i].value;
                let estValue = this.controls['SIGNER_EST__' + i] && this.controls['SIGNER_EST__' + i].value;
                if (Array.isArray(estValue)) estValue = estValue[0];
                let signerEmail = null;
                let contactTypeID = null;
                let customAttributeID = null;
                if (emailValue) {
                    signerEmail = emailValue;
                } else {
                    if (idValue.indexOf('_CONTACT#') !== -1) {
                        let tmp = idValue.split('#');
                        if (tmp && tmp.length === 2) {
                            contactTypeID = parseInt(tmp[1], 10);
                        }
                    } else if (idValue.indexOf('CUSTOM_ATTRIBUTE#') !== -1) {
                        let tmp = idValue.split('#');
                        if (tmp && tmp.length === 2) {
                            customAttributeID = parseInt(tmp[1], 10);
                        }
                    }
                }

                tmpSigners.push({
                    CONTACT_TYPE_ID: contactTypeID,
                    CONTACT_TYPE_REF_TABLE: this.tmpTemplateSigners[i].CONTACT_TYPE_REF_TABLE,
                    CUSTOM_ATTRIBUTE_ID: customAttributeID,
                    ESIGNATURE_TEMPLATE_ID: this.attachId,
                    SIGNER_EMAIL: signerEmail,
                    SIGNER_EST: estValue,
                    SIGNER_ORDER: this.tmpTemplateSigners[i].SIGNER_ORDER,
                    SIGNER_PSP: this.tmpTemplateSigners[i].SIGNER_PSP
                });
            }
            if (tmpSigners.length === 1 && tmpSigners[0].SIGNER_EMAIL === "[CURRENT_EMAIL]") tmpSigners = [];
            this.templateSigners = tmpSigners;
            this.editingSigners = false;
        },
        cancelSigners() {
            this.editingSigners = false;
        },
        setCurrentDateTimeFieldsValue() {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let date = configStore.state.cfg.currentDate || "";
            let dateTime = configStore.state.cfg.currentDate + " " + configStore.state.cfg.currentTime;
            for (let i = 0; i < forms.length; i++) {
                if (forms[i].name.indexOf('CURRENT_DATE') !== -1 || forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1) {
                    if (forms[i].value) continue;
                    if (this.signerIndex !== false
                        && (
                            (this.signerIndex === 0 && forms[i].name.indexOf('::SIGNER_') !== -1)
                            || (this.signerIndex > 0 && forms[i].name.indexOf('::SIGNER_' + this.signerIndex) === -1)
                        )
                    ) {
                        continue;
                    }
                    if (this.signerIndex === false && forms[i].name.indexOf('::SIGNER_') !== -1) continue;
                    let tmpVal = forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1 ? dateTime : date;
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        value: tmpVal,
                        isReadOnly: false,
                        force: true
                    });
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        isReadOnly: true,
                        force: true
                    });
                }
            }
        },
        loadAllPages() {
            let viewer = this?.$refs[this.id]?.ej2Instances;
            for (let i = 2; i < viewer.pageCount; i++) {
                viewer.viewerBase.createRequestForRender(i);
            }
        },
        localUpdateFormField(field, options, force) {
            let viewer = this?.$refs[this.id]?.ej2Instances;
            if (options && typeof options.isReadOnly !== 'undefined') {
                let target = document.getElementById(field.id);
                if (target) target.disabled = options.isReadOnly;
            }
            if (typeof viewer?.formDesignerModule?.updateFormField === 'function' && (typeof viewer.nameTable[field.id] !== 'undefined' || force)) {
                try {
                    viewer.formDesignerModule.updateFormField(field, options);
                } catch (e) {
                    // do nothing
                }
            }
        },
        pageRenderComplete(event) {
            /* if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances || this.useNewModules) return;
            if (event.action === "RenderPdfPages") this.reloadControlColors(); */
        },
        pageChange(event) {
            /* if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances || this.useNewModules) return;
            this.reloadControlColors();
            if (event && event.currentPageNumber && this.realMode === 'viewer' && (!this.hash || this.useNewModules)) {
                configStore.updateDateTime();
                setTimeout(() => {
                    this.setCurrentDateTimeFieldsValue();
                }, 500);
                setTimeout(() => {
                    this.setCurrentDateTimeFieldsValue();
                }, 1000);
            } */
        },
        controlDragEnd(event) {
            if (this.selectedControl && this.pdfLoaded) {
                let viewer = this.$refs[this.id].ej2Instances;
                this.cursorPosition = {
                    pageNumber: viewer.currentPageNumber,
                    x: event.offsetX,
                    y: event.offsetY
                };
                this.controlInsert(false, false, false, true);
                this.cursorPosition = {};
            }
        },
        fieldDragStart(event) {
            if (this.selectedFieldGroup >= 0
                && event.additionalData
                && event.additionalData.text
                && this.fieldsItems[this.selectedFieldGroup]
                && this.fieldsItems[this.selectedFieldGroup].child)
            {
                let groupControls = this.fieldsItems[this.selectedFieldGroup].child || [];
                this.selectedControl = {
                    name: this.getControlPrefix(event.additionalData) + event.additionalData.value,
                    text: event.additionalData.text
                };
                let selectedControl = groupControls.find((elem) => elem.text === this.selectedControl.text);
                if (selectedControl) {
                    event.dataTransfer.dropEffect = 'move';
                    event.dataTransfer.effectAllowed = 'move';
                } else {
                    event.preventDefault();
                }
            } else {
                event.preventDefault();
            }
            event.stopPropagation();
        },
        controlDragStart(event) {
            if (this.selectedGroup >= 0
                && event.additionalData
                && event.additionalData.text
                && this.controlsItems[this.selectedGroup]
                && this.controlsItems[this.selectedGroup].child)
            {
                let groupControls = this.controlsItems[this.selectedGroup].child || [];
                this.selectedControl = {
                    name: event.additionalData.value,
                    text: event.additionalData.text
                };
                let selectedControl = groupControls.find((elem) => elem.text === this.selectedControl.text);
                if (selectedControl) {
                    event.dataTransfer.dropEffect = 'move';
                    event.dataTransfer.effectAllowed = 'move';
                } else {
                    event.preventDefault();
                }
            } else {
                event.preventDefault();
            }
            event.stopPropagation();
        },
        reload() {
            if (!this.isInline) return;
            if (this && this.$refs && this.$refs[this.id] && this.$refs[this.id].ej2Instances && typeof this.$refs[this.id].ej2Instances.unload === 'function') {
                let viewer = this.$refs[this.id].ej2Instances;
                try {
                    viewer.unload();
                } catch (e) {
                    /* do nothing */
                }
            }
            this.renderedPages = [];
            this.isPdfFormPrepared = false;
            this.show = false;
            this.fileUniqueID = false;
            this.$nextTick(() => {
                this.show = true;
            });
        },
        async onLoad() {
            // window.pdfSave = this.save;
            window.onbeforeprint = (event) => {
                if (!this.pdfLoaded) {
                    library.showDialog("Please wait for all document elements to load before printing.", false, false, { showAsAlert: true });
                }
            };
            // eslint-disable-next-line camelcase
            if (typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'recruiter' && typeof vue_recruiterID !== 'undefined' && parseInt(vue_recruiterID, 10) > 0) {
                // eslint-disable-next-line camelcase
                await this.sDs.post('er_setRecruiterId', vue_recruiterID);
            }
            await configStore.getAccountSettings();
            this.$emit('update:propComponentLoaded', true);
            if (this.realMode !== 'viewer') {
                this.templates = await configStore.pdfTemplateOptions();
                if (this.propTemplateHash) {
                    let template = this.templates.find((elem) => elem.ATTACH_FILE_HASH === this.propTemplateHash);
                    if (!template) {
                        this.$emit('update:propTemplateHash', null);
                    }
                }
                let promises = [
                    configStore.getAvailableFieldList('acc'),
                    configStore.getAvailableFieldList('mem'),
                    configStore.getAvailableFieldList('lbe'),
                    configStore.getAvailableFieldList('opp'),
                    configStore.getAvailableFieldList('org'),
                    configStore.getAvailableFieldList('vol')
                ];
                let tmpControls = [];
                let tmpFields = [];
                let tmpEmailControls = [];
                let response = await library.promiseAllSettled(promises);
                for (let i in response) {
                    if (i === 'length') continue;
                    let controls = response[i].value;
                    let groupTitle = '';
                    let emailGroupTitle = '';
                    let pspID = 1;
                    if (controls) {
                        let isField = true;
                        switch (i) {
                            case "0":
                                groupTitle = 'Account';
                                emailGroupTitle = 'Account';
                                pspID = 100;
                                break;
                            case "1":
                                groupTitle = 'Client';
                                emailGroupTitle = 'Client';
                                pspID = 5;
                                break;
                            case "2":
                                isField = false;
                                groupTitle = 'Logged Data';
                                emailGroupTitle = 'Logbook';
                                pspID = 10;
                                break;
                            case "3":
                                groupTitle = 'Opportunity';
                                emailGroupTitle = 'Opportunity';
                                pspID = 2;
                                break;
                            case "4":
                                groupTitle = 'Organization';
                                emailGroupTitle = 'Organization';
                                pspID = 3;
                                break;
                            case "5":
                                groupTitle = 'Volunteer';
                                emailGroupTitle = 'Volunteer';
                                pspID = 1;
                                break;
                        }
                        if (groupTitle) {
                            let childElements = [];
                            let childEmailElements = [];
                            for (let j in controls) {
                                if (j === 'length') continue;
                                if ((i === "2" || i === "3" || i === "5") && controls[j].IS_EMAIL) {
                                    if (typeof controls[j].displayTitle !== 'undefined') {
                                        let additionalData = library.cloneObj(controls[j]);
                                        additionalData.PERSPECTIVE_ID = pspID;
                                        childEmailElements.push({
                                            text: controls[j].displayTitle.replace('MEM::', 'CLI::', 'LBE::'),
                                            value: j,
                                            additionalData: additionalData,
                                            pId: groupTitle
                                        });
                                    }
                                }
                                if (i === 5
                                    && this.notAllowedUDFTypes.indexOf(controls[j].TYPE) !== -1
                                ) {
                                    continue;
                                }
                                if (controls[j].SIMPLE_TYPE && this.allowedTypes.indexOf(controls[j].SIMPLE_TYPE) === -1) continue;
                                if (typeof controls[j].displayTitle === 'undefined' || j.indexOf('#RATINGS') !== -1 || j.indexOf('#MODIF10') !== -1) continue;
                                if (controls[j].displayTitle.indexOf('EMAIL_SAVED_TEMPLATE') !== -1) continue;
                                let tmpValue = j;
                                let tmpText = controls[j].displayTitle.replace('MEM::', 'CLI::');
                                if (j.indexOf('CUSTOM_ATTRIBUTE') !== -1) {
                                    tmpValue = tmpText + '#' + tmpValue.replace('CUSTOM_ATTRIBUTE#', '');
                                }
                                childElements.push({
                                    text: tmpText,
                                    value: tmpValue,
                                    additionalData: library.cloneObj(controls[j])
                                });
                            }
                            if (this.combinedFieldsByPsp[pspID]) {
                                childElements.push(...this.combinedFieldsByPsp[pspID]);
                            }
                            let childElementsSorted = library.sortObject(childElements, 'text');
                            if (childElements.length) {
                                if (isField) {
                                    tmpFields.push({
                                        text: groupTitle,
                                        value: i,
                                        child: childElementsSorted
                                    });
                                } else {
                                    tmpControls.push({
                                        text: groupTitle,
                                        value: i,
                                        child: childElementsSorted
                                    });
                                }
                            }
                            if (childEmailElements.length) {
                                tmpEmailControls.push({
                                    text: emailGroupTitle,
                                    value: groupTitle,
                                    hasChild: true,
                                    selectable: false,
                                    htmlAttributes: { 'prevent-select': 'true' },
                                });
                                tmpEmailControls = [...tmpEmailControls, ...childEmailElements];
                            }
                        }
                    }
                }
                tmpEmailControls.push({
                    text: 'eC User',
                    value: 'CLIENT_CONTACT#7',
                    additionalData: {
                        "displayTitle": "eC User",
                        "MULTIPLE": 0,
                        "DEFAULT_WIDTH": 200,
                        "TYPE": 301,
                        "CONTACT_TYPE_REF_TABLE": "CLIENT_PROPERTY",
                        "IS_EMAIL": true,
                        "SIMPLE_TYPE": "STRING",
                        "PERSPECTIVE_ID": 101
                    }
                });
                tmpFields.push(
                    {
                        text: 'PDF',
                        value: '6',
                        child: [
                            {
                                'text': 'Current Date',
                                'value': 'CURRENT_DATE'
                            },
                            {
                                'text': 'Current Date/Time',
                                'value': 'CURRENT_DATE_TIME'
                            }
                        ]
                    }
                );
                tmpControls.push(
                    {
                        text: 'PDF',
                        value: '6',
                        child: [
                            {
                                'text': 'Initial',
                                'value': 'INITIAL'
                            },
                            {
                                'text': 'Signature',
                                'value': 'SIGNATURE'
                            },
                            {
                                'text': 'Leader Initial',
                                'value': 'PDFLEADERINITIAL'
                            },
                            {
                                'text': 'Leader Signature',
                                'value': 'PDFLEADERSIGNATURE'
                            }
                        ]
                    }
                );
                this.controlsItems = tmpControls;
                this.fieldsItems = tmpFields;
                this.emailItems = tmpEmailControls;
            }
            if (this.config.templateHash && !this.templateHash) this.templateHash = this.config.templateHash;
            if (this.isNew) {
                this.controls['PDF_TEMPLATES'].disabled = true;
                this.controls['TEMPLATE_NAME'].value = '';
                this.show = true;
            } else if (this.hash || this.templateHash) {
                let recId = 'pdfEditor';
                let pspId = 'component';
                let hash = this.templateHash || '';
                hash = hash.toString();
                let isUdf = false;
                if (this.hash) {
                    recId = this.recordId;
                    pspId = this.pspId;
                    hash = this.hash;
                    isUdf = true;
                }
                if (hash.indexOf('__') !== -1) {
                    hash = hash.split('__')[0];
                }
                library.showLoadingIndicator(this.realWrapperID);
                this.onPdfStartLoading();
                let signersResponse = await this.sDs.post('sds_getSigners', false, false, this.templateHash, this.pspIds['lbe'] || 0);
                if (signersResponse.status === 'success' && signersResponse.data) {
                    if (signersResponse.data) {
                        let tmpSigners = library.cloneObj(signersResponse.data);
                        for (let i in tmpSigners) {
                            delete tmpSigners[i].SIGNER_ID;
                        }
                        tmpSigners = library.uniqueArrayWithObjects(tmpSigners);
                        let orders = [];
                        for (let j in tmpSigners) {
                            if (orders.indexOf(tmpSigners[j].SIGNER_ORDER) === -1) {
                                orders.push(tmpSigners[j].SIGNER_ORDER);
                            }
                        }
                        if (this.signerIndex === false) this.signerIndex = 0;
                        this.signingOrder = orders.length > 1;
                        this.templateSigners = library.cloneObj(tmpSigners);
                    }
                }
                if (
                    this.signerIndex === 0
                    && typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'recruiter'
                    && Array.isArray(this.templateSigners)
                    && this.templateSigners.length
                    && this.templateSigners[0].SIGNER_PSP === 101
                ) {
                    this.fileNotLoaded = true;
                    library.hideLoadingIndicator(this.realWrapperID);
                    this.onPdfEndLoading();
                    library.showDialog('Operation cannot be completed because first signer is eC User. Please contact your administrator.', false, false, {
                        title: 'Error',
                        name: 'pdfSignerError',
                        showAsAlert: true
                    });
                    return;
                }
                if (this.hash) {
                    this.sDs.post('sds_getAttach', false, false, recId, pspId, hash, false, isUdf, true).then((response) => {
                        library.hideLoadingIndicator(this.realWrapperID);
                        if (response.status === 'success' && response.data && response.data.fcontent) {
                            if (this.isInline) library.showLoadingIndicator(this.realWrapperID);
                            if (this.controls) this.controls['TEMPLATE_NAME'].value = this.fileName || response.data.fname;
                            this.templateName = this.fileName || response.data.fname;
                            this.selected = true;
                            this.base64 = response.data.fcontent;
                            this.show = true;
                            this.fileNotLoaded = false;
                        } else {
                            this.fileNotLoaded = true;
                            // errorHandler.handle("\nRequest: sDs.post('sds_getAttach', false, false, " + recId + ", " + pspId + ", '" + hash + "', false, " + (isUdf ? 'true' : 'false') + ", true)\nResponse: " + JSON.stringify(response, false, 2), { messageSubject: "Pdf not rendered" });
                        }
                    });
                } else {
                    this.sDs.post('sds_getPdfTemplate', hash).then((response) => {
                        library.hideLoadingIndicator(this.realWrapperID);
                        if (response.status === 'success' && response.data && response.data.fcontent) {
                            if (this.isInline) library.showLoadingIndicator(this.realWrapperID);
                            if (this.controls) this.controls['TEMPLATE_NAME'].value = this.fileName || response.data.fname;
                            this.templateName = this.fileName || response.data.fname;
                            this.selected = true;
                            let additionalData = '';
                            if (this.isSurvey) {
                                if (this.pspIds['vol']) additionalData += '&volID=' + this.pspIds['vol'];
                                if (this.pspIds['mem']) additionalData += '&memID=' + this.pspIds['mem'];
                                if (this.pspIds['slot']) additionalData += '&slotID=' + this.pspIds['slot'];
                                if (this.pspIds['org']) additionalData += '&orgID=' + this.pspIds['org'];
                                if (this.pspIds['opp']) additionalData += '&oppID=' + this.pspIds['opp'];
                                // eslint-disable-next-line camelcase
                                if (typeof vue_recruiterID !== 'undefined' && parseInt(vue_recruiterID, 10) > 0) {
                                    // eslint-disable-next-line camelcase
                                    additionalData += '&recruiterID=' + vue_recruiterID;
                                }
                            }
                            this.base64 = response.data.fcontent + additionalData;
                            this.show = true;
                            this.fileNotLoaded = false;
                        } else {
                            this.fileNotLoaded = true;
                            // errorHandler.handle("\nRequest: sDs.post('sds_getPdfTemplate', '" + hash + "')\nResponse: " + JSON.stringify(response, false, 2), { messageSubject: "Pdf not rendered" });
                        }
                    });
                }
            }
            if (this.realMode === 'viewer' && !this.realIsInline) {
                this.agreementChecked = false;
                this.completeLoad();
            }
            setTimeout(() => {
                this.isLastPdf = false;
                let pdfElmnts = document.querySelectorAll('[ctype="pdf"]');
                if (pdfElmnts && pdfElmnts.length > 1) {
                    let tmpLast = pdfElmnts[pdfElmnts.length - 1];
                    if (tmpLast && tmpLast.id && this.id === tmpLast.id) {
                        this.isLastPdf = true;
                    }
                }
            }, 500);
        },
        updateSignersAfterDnD(items) {
            this.reloadSigners = true;
            let tmpItems = [];
            for (let i in items) {
                tmpItems.push({
                    "CONTACT_TYPE_ID": items[i].CONTACT_TYPE_ID,
                    "CONTACT_TYPE_REF_TABLE": items[i].CONTACT_TYPE_REF_TABLE,
                    "CUSTOM_ATTRIBUTE_ID": items[i].CUSTOM_ATTRIBUTE_ID,
                    "SIGNER_PSP": items[i].SIGNER_PSP,
                    "SIGNER_ORDER": items[i].SIGNER_ORDER,
                    "SIGNER_EMAIL": items[i].SIGNER_EMAIL,
                    "SIGNER_EST": items[i].SIGNER_EST
                });
            }
            this.tmpTemplateSigners = library.cloneObj(tmpItems);
            this.changeSigningOrder();
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        changeSigningOrder() {
            this.reloadSigners = true;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            for (let i in tmpTemplateSigners) {
                tmpTemplateSigners[i].SIGNER_ORDER = this.signingOrder ? parseInt(i, 10) + 1 : 1;
            }
            this.tmpTemplateSigners = tmpTemplateSigners;
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        toggleActions(flag) {
            let tmpActions = library.cloneObj(this.actions);
            for (let i = 0; i < 2; i++) {
                tmpActions[i].disabled = flag;
            }
            this.actions = tmpActions;
        },
        templateChanged(event) {
            if (this.realMode !== 'editor') return;
            this.selected = event && event.itemData ? event.itemData : false;
            this.templateHash = this.controls['PDF_TEMPLATES'].value;
            this.show = false;
            this.isNew = false;
            this.controls['TEMPLATE_NAME'].value = this.selected ? event.itemData.text : '';
            this.toggleActions(!this.selected);
            this.$emit('update:propTemplateHash', this.controls['PDF_TEMPLATES'].value);
        },
        async edit() {
            if (this.isInline) {
                if (this.realMode === 'editor') this.templateHash = this.controls['PDF_TEMPLATES'].value;
                this.show = true;
            } else {
                let tabListObj = document.getElementById('log_book_entry-tab-list');
                let tabListHeight;
                let tabListWidth;
                if (tabListObj) {
                    let height = tabListObj.clientHeight;
                    let width = tabListObj.clientWidth;
                    if (height < 800) {
                        tabListHeight = tabListObj.clientHeight;
                        tabListObj.style.height = 800 + 'px';
                    }
                    if (width < 950) {
                        tabListWidth = tabListObj.clientWidth;
                        tabListObj.style.width = 950 + 'px';
                    }
                    // eslint-disable-next-line camelcase
                    if (typeof _LbeProfile_updateFrame === 'function') _LbeProfile_updateFrame(1);
                    // eslint-disable-next-line camelcase
                    if (typeof lib_resizeToContent === 'function') lib_resizeToContent(1);
                }
                let hash = this.controls['PDF_TEMPLATES'].value;
                let fileName = this.selected.text;
                let attachId = this.selected.ATTACH_ID;
                this.popupDocumentViewed = true;
                let response = await actionStore.openPdf({
                    volId: this.volId,
                    oppId: this.oppId,
                    orgId: this.orgId,
                    memId: this.memId,
                    slotId: this.slotId,
                    lbeId: this.lbeId,
                    accId: this.accId,
                    affId: this.affId,
                    aeId: this.aeId,
                    mode: this.realMode,
                    fileHash: this.hash,
                    templateHash: hash || this.templateHash,
                    recordID: this.recordId,
                    pspID: this.pspId,
                    fileName: fileName || this.fileName,
                    controlName: this.controlName,
                    attachID: attachId || this.attachId,
                    isNew: this.isNew,
                    disableFields: this.disableFields || this.config.disableFields,
                    signerIndex: this.signerIndex,
                    doNotShowAgreement: this.agreementChecked,
                    dialogOptions: { width: '880px' }
                });
                if (parseInt(tabListHeight, 10) || parseInt(tabListWidth, 10)) {
                    if (parseInt(tabListHeight, 10)) {
                        document.getElementById('log_book_entry-tab-list').style.height = tabListHeight + 'px';
                    }
                    if (parseInt(tabListWidth, 10)) {
                        document.getElementById('log_book_entry-tab-list').style.width = tabListWidth + 'px';
                    }
                    // eslint-disable-next-line camelcase
                    if (typeof _LbeProfile_updateFrame === 'function') _LbeProfile_updateFrame(1);
                    // eslint-disable-next-line camelcase
                    if (typeof lib_resizeToContent === 'function') lib_resizeToContent(1);
                }
                if (response && response.funcPreSuccess && this.controlName) {
                    if (response.funcPreSuccess.base64) {
                        this.pdfLoaded = true;
                        let urltoFile = function (url, filename, mimeType) {
                            return (fetch(url)
                                .then(function (res) { return res.arrayBuffer(); })
                                .then(function (buf) { return new File([buf], filename, { type: mimeType }); })
                            );
                        };
                        let dataURI = 'data:application/pdf;base64,' + response.funcPreSuccess.base64;
                        let fName = this.fileName || this.templateName || 'sign.pdf';
                        if (!fName.endsWith('.pdf')) fName += '.pdf';
                        let fileBase64InputElement = document.getElementById(this.controlName + '_base64file');
                        if (fileBase64InputElement) {
                            fileBase64InputElement.value = dataURI;
                            this.fileName = fName;
                            let fileInputElement = document.getElementById(this.controlName + '_file');
                            if (fileInputElement) {
                                fileInputElement.value = 'fakeFile.pdf';
                                if ("createEvent" in document) {
                                    let evt = document.createEvent("HTMLEvents");
                                    evt.initEvent("change", false, true);
                                    fileInputElement.dispatchEvent(evt);
                                }
                                else {
                                    fileInputElement.fireEvent("onchange");
                                }
                            }
                        }
                        /* let file = await urltoFile(dataURI, fName, 'application/pdf');
                        if (file) {
                            let container = new DataTransfer();
                            container.items.add(file);
                            this.attachId = "";
                            this.fileName = "";
                            let fileInputElement = document.getElementById(this.controlName + '_file');
                            if (fileInputElement) {
                                fileInputElement.files = container.files;
                                if ("createEvent" in document) {
                                    let evt = document.createEvent("HTMLEvents");
                                    evt.initEvent("change", false, true);
                                    fileInputElement.dispatchEvent(evt);
                                }
                                else {
                                    fileInputElement.fireEvent("onchange");
                                }
                            }
                        } */
                    }
                }
                if (response && response.funcPreSuccess && response.funcPreSuccess.templateHash) {
                    // this.templates = [];
                    this.templatesLoaded = false;
                    this.$nextTick(async () => {
                        this.isNew = false;
                        this.templates = await configStore.pdfTemplateOptions(true);
                        let realHash = response.funcPreSuccess.templateHash;
                        for (let j = this.templates.length - 1; j >= 0; j--) {
                            if (this.templates[j] && this.templates[j].value && this.templates[j].value.indexOf(response.funcPreSuccess.templateHash) !== -1) {
                                realHash = this.templates[j].value;
                                break;
                            }
                        }
                        this.templateHash = realHash;
                        this.config.templateHash = realHash;
                        this.$nextTick(() => {
                            this.templatesLoaded = true;
                            this.toggleActions(false);
                            this.$emit('update:propTemplateHash', response.funcPreSuccess.templateHash);
                        });
                    });
                }
                if (response && response.funcPreSuccess && response.funcPreSuccess.forceControls) {
                    this.forceControls = response.funcPreSuccess.forceControls;
                    this.setForceControls();
                }
                this.agreementChecked = true;
            }
        },
        async delete(confirmed) {
            if (this.realMode !== 'editor' || !this.selected) return;
            if (this.selected === true && this.templates.length) {
                let hash = this.controls['PDF_TEMPLATES'].value;
                for (let i in this.templates) {
                    if (i === 'length') continue;
                    if (this.templates[i].value === hash) {
                        this.selected = library.cloneObj(this.templates[i]);
                    }
                }
            }
            if (this.selected === true) return;
            let fileName = this.selected.MAC_FILE_NAME;
            let attachID = this.selected.ATTACH_ID;
            let lastUsed = this.selected.LAST_USED;
            let upload = this.selected.UPLOAD_DATE;
            if (lastUsed && !confirmed) {
                let lastUsedDate = library.getMoment(lastUsed, "YYYY-MM-DD hh:mm:ss.SSS");
                let monthAgo = library.getMoment().add(-1, 'months');
                let uploadDate = library.getMoment(upload, "YYYY-MM-DD hh:mm:ss.SSS");
                if (lastUsedDate.isAfter(monthAgo) && !uploadDate.isSame(lastUsedDate)) {
                    library.showDialog("This template is used in eCoordinator attachment UDF.<br> Would you still like to delete it?", () => {
                        this.delete(true);
                    });
                    return;
                }
            }
            this.sDs.post('er_deleteAttach', attachID, fileName, 'pdfEditor', 'component').then(async (response) => {
                if (response && response.status === 'success') {
                    this.templates = [];
                    this.templatesLoaded = false;
                    this.$nextTick(async () => {
                        this.isNew = false;
                        this.templateHash = '';
                        this.templates = await configStore.pdfTemplateOptions(true);
                        this.templatesLoaded = true;
                        this.selected = false;
                        this.toggleActions(false);
                        this.$emit('update:propTemplateHash', '');
                    });
                }
            });
        },
        templateActions: function (extraCommand) {
            switch (extraCommand.item.value) {
                case 'edit':
                    this.edit();
                    break;
                case 'new':
                    this.controls['PDF_TEMPLATES'].value = null;
                    this.$nextTick(() => {
                        this.isNew = true;
                        this.edit();
                    });
                    break;
                case 'delete':
                    this.delete();
                    break;
                default:
                    break;
            }
        },
        isPdfChanged() {
            let changed = false;
            let viewer = this.$refs[this.id].ej2Instances;
            if (viewer && !library.objIsEmpty(this.prevFieldsValues)) {
                let forms = viewer.formFieldCollections;
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (i === 'length') continue;
                    if (typeof this.prevFieldsValues[forms[i].name] !== 'undefined') {
                        let prevValue = typeof this.prevFieldsValues[forms[i].name] === 'string' ? this.prevFieldsValues[forms[i].name].trim() : this.prevFieldsValues[forms[i].name];
                        let newValue = typeof forms[i].value === 'string' ? forms[i].value.trim() : forms[i].value;
                        if (prevValue !== newValue) {
                            changed = true;
                        }
                    }
                    break;
                }
            }
            return changed;
        },
        async reloadUsedControls(fromPreparePdfForm) {
            if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances) return;
            if (this.useNewModules && !fromPreparePdfForm) return;
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let usedControls = [];
            let formControls = [];
            let duplicateControls = [];
            let replaceControls = {};
            let fieldsCount = {};
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (i === 'length') continue;
                if (typeof fieldsCount[forms[i].name] === 'undefined') {
                    fieldsCount[forms[i].name] = 0;
                } else {
                    fieldsCount[forms[i].name]++;
                }
                let isSignature = forms[i].type === 'SignatureField' || forms[i].type === 'InitialField';
                if (isSignature && fieldsCount[forms[i].name]) {
                    let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        name: forms[i].name + '' + fieldsCount[forms[i].name],
                        isReadOnly: false,
                        force: true
                    });
                    if (prevIsReadOnly) {
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            isReadOnly: true,
                            force: true
                        });
                    }
                }
            }
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (i === 'length') continue;
                let controlName = forms[i].name;
                let isSignature = forms[i].type === 'SignatureField' || forms[i].type === 'InitialField';
                let id = forms[i].id;
                if (/^.+?_[a-zA-Z0-9]{12,12}$/.test(controlName) && !controlName.endsWith('_verification')) {
                    duplicateControls.push(id);
                }
                if (controlName === 'start_date_control' || controlName === 'end_date_control') {
                    if (!forms[i].tooltip) {
                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            tooltip: configStore.getDateFormat(),
                            isReadOnly: false,
                            force: true
                        });
                        if (prevIsReadOnly) {
                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                isReadOnly: true,
                                force: true
                            });
                        }
                    }
                }
                if (this.isSurvey && !isSignature) {
                    /* let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        backgroundColor: '#daeaf7',
                        borderColor: '#303030',
                        isReadOnly: false,
                        force: true
                    });
                    if (prevIsReadOnly) {
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            isReadOnly: true,
                            force: true
                        });
                    } */
                    let pageNumber = 1;
                    // eslint-disable-next-line no-loop-func
                    let tmpFieldCollection = viewer.formFieldCollection.find((tmp) => tmp.id === forms[i].id);
                    if (tmpFieldCollection) {
                        pageNumber = tmpFieldCollection.pageNumber;
                    }
                    let options = {
                        name: forms[i].name,
                        tooltip: forms[i].tooltip,
                        bounds: {
                            X: forms[i].bounds.x, Y: forms[i].bounds.y, Width: forms[i].bounds.width, Height: forms[i].bounds.height
                        },
                        pageNumber: pageNumber
                    };
                    if (controlName.toUpperCase().indexOf('PDF_SIGNATURE') !== -1) {
                        replaceControls[id] = {};
                        replaceControls[id].options = library.cloneObj(options);
                        replaceControls[id].options.name = controlName.replaceAll('_', '');// 'PDFSIGNATURE';
                        replaceControls[id].fieldType = 'SignatureField';
                    }
                    if (controlName.toUpperCase().indexOf('PDF_INITIAL') !== -1) {
                        replaceControls[id] = {};
                        replaceControls[id].options = library.cloneObj(options);
                        replaceControls[id].options.name = controlName.replaceAll('_', '');// 'PDFINITIAL';
                        replaceControls[id].fieldType = 'InitialField';
                    }
                    if (controlName.toUpperCase().indexOf('PDF_LEADER_SIGNATURE') !== -1) {
                        replaceControls[id] = {};
                        replaceControls[id].options = library.cloneObj(options);
                        replaceControls[id].options.name = controlName.replaceAll('_', '');// 'PDFLEADERSIGNATURE';
                        replaceControls[id].fieldType = 'SignatureField';
                    }
                    if (controlName.toUpperCase().indexOf('PDF_LEADER_INITIAL') !== -1) {
                        replaceControls[id] = {};
                        replaceControls[id].options = library.cloneObj(options);
                        replaceControls[id].options.name = controlName.replaceAll('_', '');// 'PDFLEADERINITIAL';
                        replaceControls[id].fieldType = 'InitialField';
                    }
                }
                formControls.push(controlName);
                if (controlName.indexOf('Logged_Data_') === -1) continue;
                if (controlName.indexOf("[") !== -1 && controlName.indexOf("]") !== -1) {
                    controlName = controlName.split('[')[0];
                }
                usedControls.push(controlName);
            }
            for (let i in duplicateControls) {
                if (i === 'length') continue;
                viewer.formDesignerModule.deleteFormField(duplicateControls[i], false);
            }
            for (let i in replaceControls) {
                if (i === 'length') continue;
                viewer.formDesignerModule.deleteFormField(i, false);
                viewer.formDesignerModule.addFormField(replaceControls[i].fieldType, replaceControls[i].options);
            }
            usedControls = library.uniqueArray(usedControls);
            let realUsedControls = {};
            if (usedControls.length) {
                let availableControls = await configStore.getAvailableFieldList('lbe');
                let tmpAvailableControls = {};
                for (let i in availableControls) {
                    if (i === 'length') continue;
                    tmpAvailableControls[availableControls[i].UDFkey] = library.cloneObj(availableControls[i]);
                }
                forms = viewer.formFieldCollections;
                for (let i in forms) {
                    // if (forms[i].isReadOnly || forms[i].visibility === 'hidden') continue;
                    if (!forms[i].name) continue;
                    if (i === 'length') continue;
                    let controlName = forms[i].name.split('::SIGNER_')[0];
                    let realControlName = controlName;
                    if (controlName.indexOf('Logged_Data_') === -1) continue;
                    if (controlName.indexOf("[") !== -1 && controlName.indexOf("]") !== -1) {
                        controlName = controlName.split('[')[0];
                    }
                    let tmpControl = library.cloneObj(tmpAvailableControls[controlName]);
                    if (!tmpControl) {
                        controlName = controlName.replace('_ampm', '').replace('_hour', '').replace('_minute', '');
                        tmpControl = library.cloneObj(tmpAvailableControls[controlName]);
                    }
                    if (tmpControl) {
                        if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 1 && document && document.getElementById(forms[i].id)) {
                            document.getElementById(forms[i].id).name = document.getElementById(forms[i].id).name + '__formField';
                        }
                        realUsedControls[controlName] = library.cloneObj(tmpControl);
                        if (this.hash) {
                            if (tmpControl.SIMPLE_TYPE === 'LIST'
                                && tmpControl.LIST_VALUES
                                && tmpControl.LIST_VALUES.length
                                && viewer.formFieldCollections[i].value === ''
                                && viewer.formFieldCollections[i].options.length
                            ) {
                                let undefinedFound = false;
                                let tmpOptions = [];
                                for (let tmpOptionsI in viewer.formFieldCollections[i].options) {
                                    if (typeof viewer.formFieldCollections[i].options[tmpOptionsI].itemName === 'undefined' || typeof viewer.formFieldCollections[i].options[tmpOptionsI].itemValue === 'undefined') {
                                        undefinedFound = true;
                                        tmpOptions[tmpOptionsI] = {
                                            itemName: '--------',
                                            itemValue: ' '
                                        };
                                    } else {
                                        tmpOptions[tmpOptionsI] = {
                                            itemName: viewer.formFieldCollections[i].options[tmpOptionsI].itemName + '',
                                            itemValue: viewer.formFieldCollections[i].options[tmpOptionsI].itemValue + ''
                                        };
                                    }
                                }
                                if (undefinedFound && tmpOptions.length) {
                                    let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                        options: tmpOptions,
                                        isReadOnly: false,
                                        force: true
                                    });
                                    if (prevIsReadOnly) {
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            isReadOnly: true,
                                            force: true
                                        });
                                    }
                                }
                            }
                            if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 1) {
                                let tmpFiltered = forms.filter((tmp) => tmp.name === forms[i].name);
                                let isRadioSelected = tmpFiltered.find((tmp) => tmp.isSelected);
                                if (!isRadioSelected && tmpControl.DEFAULT && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                    let selectedByDefaultIdObj = tmpControl.LIST_VALUES.find((tmp) => tmpControl.DEFAULT === tmp.label);
                                    if (selectedByDefaultIdObj && parseInt(selectedByDefaultIdObj.id, 10) > 0) {
                                        let selectedByDefaultId = parseInt(selectedByDefaultIdObj.id, 10);
                                        let selectedByDefaultIdField = tmpFiltered.find((tmp) => parseInt(tmp.value, 10) === selectedByDefaultId);
                                        if (typeof selectedByDefaultIdField?.id !== 'undefined') {
                                            let prevIsReadOnly = selectedByDefaultIdField.isReadOnly;
                                            this.localUpdateFormField(selectedByDefaultIdField, {
                                                isSelected: true,
                                                isReadOnly: false,
                                                force: true
                                            });
                                            if (prevIsReadOnly) {
                                                this.localUpdateFormField(selectedByDefaultIdField, {
                                                    isReadOnly: true,
                                                    force: true
                                                });
                                            }
                                        }
                                    }
                                }
                            }
                            continue;
                        }
                        if (tmpControl.SIMPLE_TYPE === 'LIST' || tmpControl.SIMPLE_TYPE === 'BOOLEAN') {
                            if (tmpControl.SIMPLE_TYPE === 'BOOLEAN') {
                                if (isNaN(parseInt(tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE, 10))) {
                                    tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE = 0;
                                }
                                if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 0) {
                                    tmpControl.LIST_VALUES = [
                                        {
                                            id: 'Yes',
                                            label: 'Yes'
                                        },
                                        {
                                            id: 'No',
                                            label: 'No'
                                        }
                                    ];
                                }
                                // tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE = 0;
                                tmpControl.DEFAULT = typeof tmpControl.DEFAULT === 'undefined' || tmpControl.DEFAULT === '0' ? 'No' : 'Yes';
                            }
                            if (tmpControl.LIST_VALUES && tmpControl.LIST_VALUES.length) {
                                // dropdown
                                if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 0) {
                                    let options = [];
                                    if (typeof tmpControl.DEFAULT === 'undefined' || tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                        options.push({
                                            itemName: '--------',
                                            itemValue: ' '
                                        });
                                    }
                                    let defaultOption;
                                    for (let i in tmpControl.LIST_VALUES) {
                                        if (i === 'length') continue;
                                        if (tmpControl.DEFAULT === tmpControl.LIST_VALUES[i].label + '' && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                            defaultOption = [{
                                                itemName: tmpControl.LIST_VALUES[i].label + '',
                                                itemValue: tmpControl.LIST_VALUES[i].label + ''
                                            }];
                                        } else {
                                            options.push({
                                                itemName: tmpControl.LIST_VALUES[i].label + '',
                                                itemValue: tmpControl.LIST_VALUES[i].label + ''
                                            });
                                        }
                                    }
                                    if (defaultOption && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                        options = [...defaultOption, ...options];
                                    }
                                    if (options.length && library.objIsEmpty(viewer.formFieldCollections[i].value)) {
                                        if (this.hash) {
                                            for (let option in options) {
                                                if (option === 'length') continue;
                                                options[option].itemName = '';
                                            }
                                        }
                                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            options: options,
                                            isReadOnly: false,
                                            force: true
                                        });
                                        if (prevIsReadOnly) {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                isReadOnly: true,
                                                force: true
                                            });
                                        }
                                    }
                                }
                            }
                        } else if (tmpControl.SIMPLE_TYPE === 'DATE') {
                            if (!forms[i].tooltip) {
                                let tmpTooltip = configStore.getDateFormat();
                                if (tmpControl.displayTitle) {
                                    tmpTooltip = tmpControl.displayTitle + ' (format ' + configStore.getDateFormat() + ')';
                                }
                                let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                    tooltip: tmpTooltip,
                                    isReadOnly: false,
                                    force: true
                                });
                                if (prevIsReadOnly) {
                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                        isReadOnly: true,
                                        force: true
                                    });
                                }
                            }
                            if (tmpControl.DEFAULT) {
                                tmpControl.DEFAULT = configStore.getDate(tmpControl.DEFAULT);
                            }
                        } else if (tmpControl.SIMPLE_TYPE === 'TIME') {
                            let tmpDefault = parseInt(tmpControl.DEFAULT, 10);
                            let isAm = viewer.formFieldCollections.find((tmp) => tmp.name === controlName + '_ampm');
                            if (tmpDefault >= 0) {
                                let hours = (tmpDefault / 60);
                                let rhours = Math.floor(hours);
                                let minutes = (hours - rhours) * 60;
                                let rminutes = Math.round(minutes);
                                if (forms[i].name.indexOf('_hour') !== -1 && rhours > 0) {
                                    tmpControl.DEFAULT = isAm && rhours > 12 ? rhours - 12 : rhours;
                                }
                                if (forms[i].name.indexOf('_minute') !== -1) {
                                    tmpControl.DEFAULT = rminutes < 10 ? '0' + rminutes : '' + rminutes;
                                }
                                if (forms[i].name.indexOf('_ampm') !== -1) {
                                    tmpControl.DEFAULT = rhours < 12 ? "AM" : "PM";
                                }
                            }
                            let tmpFieldCollection = viewer.formFieldCollection.find((tmp) => tmp.id === forms[i].id);
                            if (tmpFieldCollection) {
                                let tmpOptions = library.cloneObj(tmpFieldCollection.options);
                                let options = [];
                                let defaultOption;
                                for (let k in tmpOptions) {
                                    if (k === 'length') continue;
                                    if (tmpControl.DEFAULT + '' === tmpOptions[k].itemValue + '' && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                        defaultOption = [{
                                            itemName: tmpOptions[k].itemName + '',
                                            itemValue: tmpOptions[k].itemValue + ''
                                        }];
                                    } else {
                                        options.push({
                                            itemName: tmpOptions[k].itemName + '',
                                            itemValue: tmpOptions[k].itemValue + ''
                                        });
                                    }
                                }
                                if (defaultOption && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                    options = [...defaultOption, ...options];
                                }
                                if (options.length) {
                                    if (this.hash) {
                                        for (let option in options) {
                                            if (option === 'length') continue;
                                            options[option].itemName = '';
                                        }
                                    }
                                    let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                        options: options,
                                        isReadOnly: false,
                                        force: true
                                    });
                                    if (prevIsReadOnly) {
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            isReadOnly: true,
                                            force: true
                                        });
                                    }
                                }
                            }
                        }
                        if (tmpControl.DEFAULT && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT && !this.hash) {
                            if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 1) {
                                let selectedListElm = tmpControl.LIST_VALUES.find((tmp) => tmpControl.DEFAULT === tmp.label);
                                if (selectedListElm && parseInt(selectedListElm.id, 10) > 0) {
                                    let tmpFiltered = forms.filter((tmp) => tmp.name === forms[i].name && parseInt(tmp.value, 10) === parseInt(selectedListElm.id, 10));
                                    if (tmpFiltered && tmpFiltered[0]) tmpFiltered = tmpFiltered[0];
                                    if (tmpFiltered && tmpFiltered.id && !tmpFiltered.isSelected) {
                                        let prevIsReadOnly = tmpFiltered.isReadOnly;
                                        this.localUpdateFormField(tmpFiltered, {
                                            isSelected: true,
                                            value: tmpFiltered.value,
                                            isReadOnly: false,
                                            force: true
                                        });
                                        if (prevIsReadOnly) {
                                            this.localUpdateFormField(tmpFiltered, {
                                                isReadOnly: true,
                                                force: true
                                            });
                                        }
                                    }
                                    continue;
                                }
                                let selectedListIndex = tmpControl.LIST_VALUES.findIndex((tmp) => tmpControl.DEFAULT === tmp.label);
                                if (selectedListIndex >= 0) {
                                    let tmpFiltered = forms.filter((tmp) => tmp.name === forms[i].name);
                                    for (let k in tmpFiltered) {
                                        if (k === 'length') continue;
                                        /* let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            tooltip: '',
                                            isReadOnly: false,
                                            force: true
                                        });
                                        if (prevIsReadOnly) {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                isReadOnly: true,
                                                force: true
                                            });
                                        } */
                                        if (tmpFiltered[k].id === forms[i].id && parseInt(k, 10) === selectedListIndex) {
                                            let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                isSelected: true,
                                                value: viewer.formFieldCollections[i].value,
                                                isReadOnly: false,
                                                force: true
                                            });
                                            if (prevIsReadOnly) {
                                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                    isReadOnly: true,
                                                    force: true
                                                });
                                            }
                                        }
                                    }
                                }
                            }
                            if (tmpControl.CUSTOM_ATTRIBUTE_DD_TYPE === 2) {
                                if (tmpControl.SIMPLE_TYPE === 'BOOLEAN') {
                                    if (tmpControl.DEFAULT === 'Yes' && !tmpControl.CUSTOM_ATTRIBUTE_NO_DEFAULT) {
                                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            isChecked: true,
                                            isReadOnly: false,
                                            force: true
                                        });
                                        if (prevIsReadOnly) {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                isReadOnly: true,
                                                force: true
                                            });
                                        }
                                    }
                                } else {
                                    let checkedList = tmpControl.LIST_VALUES.find((tmp) => tmpControl.DEFAULT_SEPARATED.indexOf(tmp.label.toString()) !== -1 && realControlName === controlName + '[' + tmp.id + ']');
                                    if (!checkedList && tmpControl.CUSTOM_ATTRIBUTE_STORE_AS_TEXT) {
                                        checkedList = tmpControl.LIST_VALUES.find((tmp) => tmpControl.DEFAULT_SEPARATED.indexOf(tmp.label.toString()) !== -1 && realControlName === controlName + '[' + tmp.label + ']');
                                    }
                                    if (checkedList) {
                                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            isChecked: true,
                                            isReadOnly: false,
                                            force: true
                                        });
                                        if (prevIsReadOnly) {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                isReadOnly: true,
                                                force: true
                                            });
                                        }
                                    }
                                }
                            }
                            let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                            if (forms[i].type !== 'RadioButton' && forms[i].type !== 'Checkbox') {
                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                    value: library.trimHTML(tmpControl.DEFAULT),
                                    isReadOnly: false,
                                    force: true
                                });
                                if (prevIsReadOnly) {
                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                        isReadOnly: true,
                                        force: true
                                    });
                                }
                            }
                        }
                    } else {
                        if (controlName.indexOf('_ampm') === -1 && controlName.indexOf('_hour') === -1 && controlName.indexOf('_minute') === -1) {
                            viewer.formDesignerModule.deleteFormField(viewer.formFieldCollections[i]);
                        }
                    }
                }
            }
            this.usedControls = library.cloneObj(realUsedControls);
        },
        setForceControls() {
            let formForceControlsInput;
            let forceControlsNames = [];
            if (document
                && document.forms
                && document.forms[0]
                && document.forms[0].force_controls
            ) {
                formForceControlsInput = document.forms[0].force_controls;
                forceControlsNames = document.forms[0].force_controls.value.split(',');
            }
            if (Array.isArray(this.removeForceControls) && this.removeForceControls.length) {
                for (let i in forceControlsNames) {
                    if (this.removeForceControls.indexOf(forceControlsNames[i]) !== -1) {
                        delete forceControlsNames[i];
                    }
                }
            }
            for (let i in this.forceControls) {
                if (i === 'length') continue;
                forceControlsNames.push(this.forceControls[i].name.split('[')[0]);
            }
            if (forceControlsNames.length && formForceControlsInput) {
                forceControlsNames = library.uniqueArray(forceControlsNames);
                forceControlsNames = forceControlsNames.filter((tmp) => tmp);
                formForceControlsInput.value = forceControlsNames.join(',');
            }
        },
        async reloadForceControls() {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let forceControls = [];

            let availableControls = await configStore.getAvailableFieldList('lbe');
            let tmpAvailableControls = {};
            for (let i in availableControls) {
                if (i === 'length') continue;
                tmpAvailableControls[availableControls[i].UDFkey] = library.cloneObj(availableControls[i]);
            }

            for (let i in forms) {
                if (!forms[i].name) continue;
                if (i === 'length' || forms[i].visibility === 'hidden' || forms[i].isReadOnly === true) continue;
                let listType;
                let controlName = forms[i].name.split('::SIGNER_')[0];
                if (forms[i].type === 'InitialField' || forms[i].type === 'SignatureField') {
                    if (forms[i].type === 'InitialField') {
                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            value: ' ' + forms[i].value + ' ',
                            isReadOnly: false,
                            force: true
                        });
                        if (prevIsReadOnly) {
                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                isReadOnly: true,
                                force: true
                            });
                        }
                    }
                    continue;
                }
                if (controlName.indexOf('Logged_Data_') === -1) continue;
                let controlNameWOOptionId = "";
                if (controlName.indexOf("[") !== -1 && controlName.indexOf("]") !== -1) {
                    controlNameWOOptionId = controlName.split('[')[0];
                }
                let value = forms[i].value;
                if (typeof value === 'string') {
                    value = value.trim();
                    if (value === '----------------' || value === '--------') value = '';
                }
                if (forms[i].type === "DropdownList") {
                    listType = 0;
                    if (
                        Array.isArray(forms[i].selectedIndex)
                        && typeof forms[i].selectedIndex?.[0] !== 'undefined'
                        && Array.isArray(forms[i].options)
                        && typeof forms[i].options?.[forms[i].selectedIndex?.[0]]?.itemValue !== 'undefined'
                    ) {
                        value = forms[i].options[forms[i].selectedIndex[0]].itemValue;
                    }
                    let tmpControl = tmpAvailableControls[controlName];
                    if (tmpControl) {
                        if (tmpControl.SIMPLE_TYPE !== "BOOLEAN") {
                            let valueObj = tmpControl.LIST_VALUES.find((tmp) => tmp.label.toString() === value);
                            if (valueObj && valueObj.id) {
                                value = valueObj.id;
                            }
                        }
                    }
                }
                if (forms[i].type === "Checkbox" && controlNameWOOptionId) {
                    value = false;
                    listType = 2;
                    let tmpControl = tmpAvailableControls[controlNameWOOptionId];
                    let tmpFieldCollection = viewer.formFieldCollection.find((tmp) => tmp.id === forms[i].id);
                    if (tmpFieldCollection && tmpFieldCollection.isChecked) {
                        value = forms[i].name.split('::SIGNER_')[0].split('[')[1].replace(']', '');
                        if (tmpControl.CUSTOM_ATTRIBUTE_STORE_AS_TEXT && tmpControl.LIST_VALUES && tmpControl.LIST_VALUES.length) {
                            let textValue = tmpControl.LIST_VALUES.find((tmp) => tmp.id.toString() === value.toString());
                            if (textValue) {
                                value = textValue.label;
                            }
                        }
                    }
                    if (!value && tmpControl && tmpControl.DEFAULT_SEPARATED && !this.formFielClicked[controlNameWOOptionId]) {
                        for (let k in tmpControl.LIST_VALUES) {
                            if (k === 'length') continue;
                            if (tmpControl.DEFAULT_SEPARATED.indexOf(tmpControl.LIST_VALUES[k].label.toString()) !== -1) {
                                forceControls.push({
                                    name: controlNameWOOptionId + '[' + tmpControl.LIST_VALUES[k].id + ']',
                                    value: tmpControl.LIST_VALUES[k].id,
                                    listType: listType,
                                    realName: controlNameWOOptionId || controlName
                                });
                            }
                        }
                    }
                }
                if (forms[i].type === "RadioButton") {
                    listType = 1;
                    let filteredRadio = forms.filter((tmp) => tmp.name === forms[i].name);
                    let selectedIndex;
                    let tmpControl = tmpAvailableControls[controlName];
                    value = false;
                    for (let k in filteredRadio) {
                        if (k === 'length') continue;
                        let tmpFieldCollection = viewer.formFieldCollection.find((tmp) => tmp.id === filteredRadio[k].id);
                        if (tmpFieldCollection && tmpFieldCollection.isSelected) {
                            value = tmpFieldCollection.value;
                            break;
                        }
                    }
                    if (value && parseInt(value, 10) > 0) {
                        selectedIndex = tmpControl.LIST_VALUES.findIndex((tmp) => tmp.id === parseInt(value, 10));
                    }

                    if (tmpControl && selectedIndex >= 0) {
                        if (selectedIndex > (tmpControl.LIST_VALUES.length - 1)) selectedIndex = tmpControl.LIST_VALUES.length - 1;
                        value = tmpControl.CUSTOM_ATTRIBUTE_STORE_AS_TEXT ? tmpControl.LIST_VALUES[selectedIndex].label : tmpControl.LIST_VALUES[selectedIndex].id;
                    }
                    /* if (document && document.getElementById(forms[i].id) && forms[i].value) {
                        document.getElementById(forms[i].id).value = value;
                    } */
                    if (!value && tmpControl && tmpControl.DEFAULT_SEPARATED && !this.formFielClicked[forms[i].name]) {
                        for (let k in tmpControl.LIST_VALUES) {
                            if (k === 'length') continue;
                            if (tmpControl.DEFAULT_SEPARATED.indexOf(tmpControl.LIST_VALUES[k].label.toString()) !== -1) {
                                value = tmpControl.CUSTOM_ATTRIBUTE_STORE_AS_TEXT ? tmpControl.LIST_VALUES[k].label : tmpControl.LIST_VALUES[k].id;
                            }
                        }
                    }
                }
                if (value) {
                    let alreadyExists = forceControls.findIndex((tmp) => tmp.name === controlName);
                    if (alreadyExists === -1) {
                        forceControls.push({
                            name: controlName,
                            value: value,
                            listType: listType,
                            realName: controlNameWOOptionId || controlName
                        });
                    }
                }
            }
            this.removeForceControls = [];
            for (let i in this.forceControls) {
                let missedForceControl = forceControls.find((tmp) => tmp.name === this.forceControls[i].name);
                if (typeof missedForceControl === 'undefined') this.removeForceControls.push(this.forceControls[i].name.split('[')[0]);
            }
            this.forceControls = library.cloneObj(forceControls);
            this.setForceControls();
        },
        removeWrongFields() {
            let viewer = this.$refs[this.id].ej2Instances;
            let removeIDs = [];
            if (viewer.formDesignerModule) {
                let forms = [];
                for (let i in viewer.formFieldCollection) {
                    if (viewer.formFieldCollection[i].name === 'CURSOR_POSITION') continue;
                    forms.push(viewer.formFieldCollection[i]);
                }
                if (forms.length) {
                    for (let i = 0; i < forms.length; i++) {
                        let pageNumber = parseInt(forms[i].pageNumber, 10) || -1;
                        if (pageNumber < 0) {
                            removeIDs.push(forms[i].id);
                        }
                    }
                }
                for (let i in removeIDs) {
                    if (i === 'length') continue;
                    viewer.formDesignerModule.deleteFormField(removeIDs[i]);
                }
            }
        },
        prepareValidJsonToSave(tmpJson) {
            let validJson = library.cloneObj(tmpJson);
            if (validJson['formDesigner'] && library.isJson(validJson['formDesigner'])) {
                let formDesignerJson = JSON.parse(validJson['formDesigner']);
                let formValidDesignerJson = [];
                for (let i in formDesignerJson) {
                    if (i === 'length' || formDesignerJson[i].FormField.name.indexOf('CURSOR_POSITION') !== -1) continue;
                    if (library.objIsEmpty(formDesignerJson[i].FormField.value)) {
                        formDesignerJson[i].FormField.value = "";
                    }
                    if (typeof formDesignerJson[i].FormField.visibility !== "undefined") {
                        formDesignerJson[i].FormField.visibility = "visible";
                    }
                    if (formDesignerJson[i].FormField
                        && formDesignerJson[i].FormField.pageNumber > 0
                        && formDesignerJson[i].FormField.lineBound
                        && formDesignerJson[i].FormField.lineBound.X > 0
                        && formDesignerJson[i].FormField.lineBound.Y > 0
                        && formDesignerJson[i].FormField.lineBound.Height > 0
                        && formDesignerJson[i].FormField.lineBound.Width > 0
                    ) {
                        formValidDesignerJson.push(formDesignerJson[i]);
                    }
                }
                validJson['formDesigner'] = JSON.stringify(formValidDesignerJson);
            } else {
                validJson['formDesigner'] = "[]";
            }
            return validJson;
        },
        returnFieldsVisibility: function () {
            if (!this || !this.$refs || !this.$refs[this.id]) return;
            let viewer = this.$refs[this.id].ej2Instances;
            if (viewer.formDesignerModule) {
                let forms = [];
                for (let i in viewer.formFieldCollection) {
                    if (viewer.formFieldCollection[i].name === 'CURSOR_POSITION') continue;
                    forms.push(viewer.formFieldCollection[i]);
                }
                if (forms.length) {
                    for (let i = 0; i < forms.length; i++) {
                        if (typeof viewer.formFieldCollections[i] === 'undefined') continue;
                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            visibility: 'visible',
                            fontFamily: viewer.formFieldCollections[i].fontFamily,
                            force: true,
                            isReadOnly: false,
                        });
                        if (prevIsReadOnly) {
                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                isReadOnly: true,
                                force: true
                            });
                        }
                        let tmpEl = document.getElementById(forms[i].id + '_input_label');
                        if (tmpEl) {
                            if (forms[i].visibility === 'hidden') {
                                tmpEl.closest('.foreign-object').classList.add('pdf-element-hidden-visibility');
                            } else {
                                tmpEl.closest('.foreign-object').classList.remove('pdf-element-hidden-visibility');
                            }
                        }
                    }
                }
            }
        },
        manualIsValid: function () {
            return this.formIsValid();
        },
        manualSave: function (force) {
            return this.save(force);
        },
        manualAfterSaveCheck() {
            if (this.disabled) return 0;
            this.afterSaveIsChecked = 1;
            let fileBase64InputElement = document.getElementById(this.controlName + '_base64file');
            if (!fileBase64InputElement || !fileBase64InputElement?.value) {
                this.otherErrorCount = 0;
                library.showDialog("Document is not saved and will be reloaded.", () => {
                    this.reload();
                    this.pdfOtherError = false;
                },
                false,
                {
                    title: 'Error',
                    name: 'pdfOtherError',
                    showAsAlert: true
                });
                return 1;
            }
            // check signatures for current signer
            /* let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            for (let i in forms) {

            } */
            return 0;
        },
        renamePdfInputs() {
            document.querySelectorAll('.e-pv-main-container .foreign-object input, .e-pv-main-container .foreign-object select').forEach((tmp) => { tmp.name += '_pdf'; });
        },
        checkLoggedDataPropertiesForEditor() {
            if (!this || !this.$refs || !this.$refs[this.id]) return;
            let viewer = this.$refs[this.id].ej2Instances;
            if (viewer.formDesignerModule) {
                let forms = viewer.formFieldCollections;
                for (let i in forms) {
                    if (forms[i].name.indexOf('Logged_Data_') !== -1) {
                        let returnProperties = {};
                        if (forms[i].isChecked) {
                            returnProperties['isChecked'] = false;
                        }
                        if (forms[i].isSelected) {
                            returnProperties['isSelected'] = false;
                        }
                        if (!library.objIsEmpty(returnProperties)) {
                            returnProperties.isReadOnly = false;
                            returnProperties.force = true;
                            let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                            this.localUpdateFormField(viewer.formFieldCollections[i], returnProperties);
                            if (prevIsReadOnly) {
                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                    isReadOnly: true,
                                    force: true
                                });
                            }
                        }
                    }
                }
            }
        },
        save: async function (force) {
            if (this.disabled) return {};
            if (!this.$refs[this.id] || !this.$refs[this.id].ej2Instances) return {};
            if (this.realMode === 'viewer' && (!this.pdfLoaded || this.fileNotLoaded) && this.realIsInline) return {};
            this.removeCursor();
            this.removeWrongFields();
            await this.reloadForceControls();
            if (this.isPdfOnly && this.realMode === 'viewer' && this.hash && !this.alwaysSave) {
                return { base64: '', templateHash: '', forceControls: this.forceControls };
            }
            library.showLoadingIndicator(this.realWrapperID);
            let currentAttachId = library.cloneObj(this.attachId);
            let isValid = this.realMode !== 'viewer' || force || this.formIsValid();
            isValid = await isValid;
            if (isValid) {
                if (!force) {
                    this.renamePdfInputs();
                    this.returnFieldsVisibility();
                }
                if (this.realMode === 'viewer') {
                    let popupOkButton = document.querySelectorAll('.e-pv-createbtn');
                    if (popupOkButton && popupOkButton.length) {
                        for (let tmpI in popupOkButton) {
                            if (typeof popupOkButton[tmpI].click === 'function' && !popupOkButton[tmpI].disabled) {
                                popupOkButton[tmpI].click();
                            }
                        }
                        await library.sleep(1000);
                    }
                    if (!this.pdfIsChanged && !this.alwaysSave) {
                        library.hideLoadingIndicator(this.realWrapperID);
                        return { base64: '', templateHash: '', forceControls: this.forceControls };
                    }
                } else {
                    this.checkLoggedDataPropertiesForEditor();
                }
                let viewer = this.$refs[this.id].ej2Instances;
                let base64Data;
                if (this.useNewModules) {
                    let pdfBlob = await viewer.saveAsBlob();
                    if (pdfBlob && pdfBlob.size > 0) {
                        base64Data = await library.fileToBase64(pdfBlob);
                        if (base64Data) base64Data = base64Data.split('base64,')[1];
                    }
                }
                if (base64Data) {
                    this.pdfIsChanged = false;
                    this.base64 = base64Data;
                    let dataURI = 'data:application/pdf;base64,' + this.base64;
                    let fName = this.fileName || this.templateName || 'sign.pdf';
                    if (!fName.endsWith('.pdf')) fName += '.pdf';
                    let fileBase64InputElement = document.getElementById(this.controlName + '_base64file');
                    if (fileBase64InputElement) {
                        fileBase64InputElement.value = dataURI;
                        this.fileName = fName;
                        let fileInputElement = document.getElementById(this.controlName + '_file');
                        if (fileInputElement) {
                            fileInputElement.value = 'fakeFile.pdf';
                            if ("createEvent" in document) {
                                let evt = document.createEvent("HTMLEvents");
                                evt.initEvent("change", false, true);
                                fileInputElement.dispatchEvent(evt);
                            }
                            else {
                                fileInputElement.fireEvent("onchange");
                            }
                        }
                    } else {
                        library.hideLoadingIndicator(this.realWrapperID);
                        library.showDialog('Document is not saved, please try again.', false, false, { showAsAlert: true });
                        return false;
                    }
                } else {
                    library.hideLoadingIndicator(this.realWrapperID);
                    library.showDialog('Document is not saved, please try again.', false, false, { showAsAlert: true });
                    return false;
                }
            }
            let tmpFileHash = '';
            let withErrorStatus = false;

            if (this.mode !== 'viewer') {
                if (this.base64) {
                    let base64 = 'data:application/pdf;base64,' + this.base64;
                    let attachID = this.isNew ? -1 : currentAttachId;
                    let signers = library.cloneObj(this.templateSigners);
                    let prevFieldsData = library.cloneObj(this.prevFieldsData);
                    let savedFieldsData = this.getCurrentFieldsOnDocument();

                    let response = await this.sDs.post(
                        'sds_updatePdfTemplate', attachID, base64, this.controls['TEMPLATE_NAME'].value, signers, prevFieldsData, savedFieldsData
                    );

                    if (response && response.status === 'success') {
                        let fileUrl = response.data;
                        if (typeof fileUrl === 'string' && fileUrl.indexOf('sDs_ga=') !== -1) {
                            let fileHash = fileUrl.split('sDs_ga=');
                            if (fileHash.length === 2 && fileHash[1].length) {
                                this.templates = await configStore.pdfTemplateOptions(true);
                                let realHash = fileHash[1];
                                for (let j = this.templates.length - 1; j >= 0; j--) {
                                    if (this.templates[j] && this.templates[j].value && this.templates[j].value.indexOf(fileHash[1]) !== -1) {
                                        realHash = this.templates[j].value;
                                        break;
                                    }
                                }
                                tmpFileHash = realHash;
                                this.$nextTick(() => {
                                    this.isNew = false;
                                    this.templateHash = realHash;
                                });
                            }
                        }
                    } else if (response?.error && typeof response.error === 'object' && response.error.length) {
                        withErrorStatus = true;
                        library.showDialog(response.error[0], false, false, { title: 'Warning', showAsAlert: true });
                    }
                }
            }

            library.hideLoadingIndicator(this.realWrapperID);

            if (withErrorStatus) {
                return false;
            }

            return { base64: this.base64, templateHash: tmpFileHash, forceControls: this.forceControls };
        },
        disableFieldsFunc: function (isFromTimeout) {
            if (!this.hash || this.signerIndex !== false) return;
            if (this && this.$refs && this.$refs[this.id] && this.$refs[this.id].ej2Instances) {
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                if (forms.length) {
                    let inputElements = document.querySelectorAll('.e-pv-viewer-container input, .e-pv-viewer-container select, .e-pv-viewer-container textarea');
                    for (let i = 0; i < inputElements.length; i++) {
                        inputElements[i].disabled = true;
                        if (inputElements[i].type === 'radio' || inputElements[i].type === 'checkbox') {
                            inputElements[i].style.opacity = '0';
                        } else {
                            inputElements[i].style.opacity = '1';
                        }
                    }
                    this.$nextTick(() => {
                        for (let i = 0; i < forms.length; i++) {
                            if (forms[i].type === "SignatureField" || forms[i].type === "InitialField" || forms[i].type === "Checkbox") {
                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                    isReadOnly: true,
                                    force: true
                                });
                                if (forms[i].type === "Checkbox") {
                                    let id = forms[i].id;
                                    let tmpCheckbox = document.getElementById(id + '_input');
                                    if (tmpCheckbox) {
                                        tmpCheckbox.style['background-color'] = '#dae9f7';
                                    }
                                }
                            }
                        }
                    });
                }
                if (!isFromTimeout) {
                    setTimeout(() => {
                        this.disableFieldsFunc(true);
                    }, 500);
                    setTimeout(() => {
                        this.disableFieldsFunc(true);
                    }, 1000);
                    setTimeout(() => {
                        this.disableFieldsFunc(true);
                    }, 1500);
                }
            }
        },
        showToolbarButtons(flag) {
            if (this && this.$refs && this.$refs[this.id] && this.$refs[this.id].ej2Instances) {
                let viewer = this.$refs[this.id].ej2Instances;
                if (viewer && viewer.toolbarSettings && viewer.toolbarSettings.toolbarItems && viewer.toolbarSettings.toolbarItems.length) {
                    if (this.realMode === 'viewer') {
                        viewer.toolbar.showToolbarItem(viewer.toolbarSettings.toolbarItems, flag);
                    }
                    // viewer.toolbar.showToolbarItem(["MagnificationTool"], false);
                }
            }
        },
        async openNewFileClickHandler() {
            // on upload file click
            for (let i = 0; i < 30; i++) {
                let buttonEl = document.querySelector('#' + this.id + '_toolbarItemsContainer');
                if (!buttonEl) {
                    // eslint-disable-next-line no-await-in-loop
                    await library.sleep(1000);
                } else {
                    buttonEl?.addEventListener("click", (tmpEvent) => {
                        if (tmpEvent && tmpEvent.target && tmpEvent.target.id.indexOf(this.id + '_open') !== -1) {
                            this.savePreviousFileFields();
                        }
                    }, true);
                    break;
                }
            }
        },
        created: function () {
            let updateViewerContainer = () => {
                if (typeof this?.$refs?.[this.id]?.ej2Instances?.magnificationModule?.fitToWidth === 'function'
                    && !this.$el.querySelectorAll('.e-pv-signature-window').length
                ) {
                    this.$refs[this.id].ej2Instances.magnificationModule.fitToWidth();
                }
            };
            let updateViewerContainerTimeout = 0;
            window.addEventListener('resize', () => {
                if (updateViewerContainerTimeout) clearTimeout(updateViewerContainerTimeout);
                updateViewerContainerTimeout = setTimeout(updateViewerContainer, 500);
            }, true);
            // rewrite PdfViewerBase.prototype.initialPagesRenderedForSign
            if (typeof this.$refs?.[this.id]?.ej2Instances?.viewerBase?.initialPagesRenderedForSign !== 'undefined') {
                let oldInitialPagesRenderedForSign = this.$refs[this.id].ej2Instances.viewerBase.initialPagesRenderedForSign;
                this.$refs[this.id].ej2Instances.viewerBase.initialPagesRenderedForSign = function (data) {
                    /* try { // do not remove for now
                        let formFieldList = JSON.parse(data.formFieldList);
                        for (let i in formFieldList) {
                            if (typeof formFieldList[i].FieldName === 'string' && formFieldList[i].FieldName?.indexOf(' ') !== -1) {
                                formFieldList[i].FieldName = formFieldList[i].FieldName.replace(' ', '');
                            }
                        }
                        data.formFieldList = JSON.stringify(formFieldList);
                    } catch (e) {
                        // do nothing
                    } */
                    try {
                        oldInitialPagesRenderedForSign.call(this, data);
                    } catch (e) {
                        try {
                            data.formFieldName = data.formFieldName.replace('::', '').replace('SIGNER_', 'SIGNER'); // .replace(' ', ''); do not remove for now
                            oldInitialPagesRenderedForSign.call(this, data);
                        } catch (e) {
                            // do nothing
                        }
                    }
                };
            }
            // on upload file click
            this.openNewFileClickHandler();
            if (!this.useNewModules) this.showToolbarButtons(false);
            // eslint-disable-next-line camelcase
            if (typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'recruiter' && typeof vue_recruiterID !== 'undefined' && parseInt(vue_recruiterID, 10) > 0) {
                // eslint-disable-next-line camelcase
                this.sDs.post('er_setRecruiterId', vue_recruiterID);
                this.sDs.post('er_getLoggedUsers', 'vol').then((response) => {
                    if (response.status === 'success' && parseInt(this.pspIds['vol'], 10) !== parseInt(response.data, 10)) {
                        try {
                            // eslint-disable-next-line camelcase
                            vue_volunteerID = parseInt(response.data, 10);
                        } catch (e) {
                            // do nothing
                        }
                    }
                });
            }
            if (this.base64) {
                let pdfLoadInterval = setInterval(() => {
                    if (this.$refs[this.id].ej2Instances?.viewerBase?.viewerContainer && typeof this.base64 === 'string' && this.base64) {
                        clearInterval(pdfLoadInterval);
                        this.$refs[this.id].ej2Instances.load(this.base64);
                    }
                }, 500);
            }
            if (this.$el && this.$el.parentElement && this.$el.parentElement.id === 'dialog-template-pdf') {
                let resizeDialogFunc = () => {
                    let dlgContent = this.$el.closest('.e-dlg-content');
                    if (dlgContent) {
                        let dlgContentHeight = this.$el.closest('.e-dlg-content').clientHeight;
                        let pdfContentEl = this.$el.querySelector('.e-pv-main-container');
                        if (dlgContentHeight && pdfContentEl) {
                            let minusHeight = this.realMode === 'viewer' ? 30 : 100;
                            pdfContentEl.style.minHeight = dlgContentHeight - minusHeight + 'px';
                        }
                    }
                };
                window.addEventListener('resize', () => {
                    resizeDialogFunc();
                }, true);
                resizeDialogFunc();
            }
        },
        setInitialFieldsValues: function () {
            if (this.realMode !== 'viewer') return;
            let viewer = this?.$refs?.[this.id]?.ej2Instances;
            if (viewer) {
                let forms = viewer.formFieldCollections;
                let prevValues = {};
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (i === 'length') continue;
                    if (typeof forms[i].value !== 'undefined') {
                        prevValues[forms[i].name] = forms[i].value;
                        if (forms[i].type === "InitialField" || forms[i].type === "SignatureField") {
                            prevValues[forms[i].name] = '';
                        }
                    }
                }
                this.prevFieldsValues = library.cloneObj(prevValues);
            }
        },
        pdfErrorHandler: function (event) {
            let viewer = this.$refs[this.id].ej2Instances;
            library.hideLoadingIndicator(this.realWrapperID);
            let tmpPdfLoadingIndicator = document.getElementById(this.id + '_loadingIndicator');
            if (tmpPdfLoadingIndicator) {
                tmpPdfLoadingIndicator.remove();
            }
            if (
                (typeof event === 'object' && event.action && typeof event.retryCount === 'undefined')
                || (typeof event === 'object' && typeof event.response !== 'undefined')
            ) {
                let actionFailed = event.action;
                if (typeof event === 'object' && typeof event.response !== 'undefined') actionFailed = 'default';
                switch (actionFailed) {
                    case 'Load':
                        try {
                            viewer.unload();
                        } catch (e) {
                            /* do nothing */
                        }
                        if (this.isNew) {
                            library.showDialog("PDF file is corrupt, please load the other one.", () => {
                                this.openFilesBrowser();
                            },
                            false,
                            {
                                title: 'Error',
                                name: 'pdfLoadError',
                                showAsAlert: true
                            });
                        }
                        break;
                    case 'default':
                    default:
                        if (!this.pdfOtherError) {
                            this.pdfOtherError = true;
                            if (this.otherErrorCount < 4) {
                                this.otherErrorCount++;
                                this.reload();
                                this.pdfOtherError = false;
                            } else {
                                this.otherErrorCount = 0;
                                library.showDialog("Oops! Sorry, there's a problem with PDF service. Our developers have been notified. PDF file will be reloaded.", () => {
                                    this.reload();
                                    this.pdfOtherError = false;
                                },
                                false,
                                {
                                    title: 'Error',
                                    name: 'pdfOtherError',
                                    showAsAlert: true
                                });
                            }
                        }
                        break;
                }
            }
        },
        documentLoadFailed: function (event) {
            this.pdfErrorHandler(event);
        },
        ajaxRequestFailed: function (event) {
            let showPdfError = true;
            /* if (!this.useNewModules && event && event.action === 'Load' && event.errorMessage.indexOf('Index was out of range.') !== -1 && this.base64) {
                // VMS-9549, try to fix corrupted file
                let fileContent = library.base64ToFileContent(this.base64);
                if (fileContent.indexOf('NaN') !== -1) {
                    fileContent = fileContent.replaceAll('NaN', '0');
                    this.base64 = library.fileContentToBase64(fileContent);
                    this.$refs[this.id].ej2Instances.load(this.base64);
                    showPdfError = false;
                }
            } */
            if (showPdfError) this.pdfErrorHandler(event);
        },
        uploadingFileIsValid: function (tmpEvent) {
            let tmpFileInput = document.getElementById(this.id + '_fileUploadElement');
            if (tmpFileInput) {
                let files = tmpFileInput.files;
                if (this.uploadMaxFilesize && files.length && files[0] && files[0].size > (this.uploadMaxFilesize * 1024 * 1024)) { // NOSONAR
                    tmpFileInput.value = null;
                    library.hideLoadingIndicator(this.realWrapperID);
                    library.showDialog("The maximum file size limit is " + this.uploadMaxFilesize + "Mb.",
                        false,
                        false,
                        {
                            title: 'Error',
                            name: 'pdfSizeError',
                            showAsAlert: true
                        });
                    if (tmpEvent) {
                        tmpEvent.stopPropagation();
                    } else {
                        return false;
                    }
                }
            }
            return true;
        },
        openFilesBrowser: function (event) {
            let tmpFileInput = document.getElementById(this.id + '_fileUploadElement');
            if (tmpFileInput) {
                tmpFileInput.removeEventListener("change", this.uploadingFileIsValid, true);
                tmpFileInput.addEventListener("change", this.uploadingFileIsValid, true);
            }

            let openBtn = document.getElementById(this.id + '_open');
            if (openBtn) openBtn.click();
        },
        removeCombinedFields: function (fromPreparePdfForm) {
            if (this.useNewModules && !fromPreparePdfForm) return;
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let combinedFieldsRealFieldNames = {};
            combinedFieldsRealFieldNames['COMBINED_FULL_NAME'] = 'VOL_FULL_NAME';
            combinedFieldsRealFieldNames['COMBINED_STREET_ADDRESS'] = 'VOL_FULL_STREET_ADDRESS';
            combinedFieldsRealFieldNames['COMBINED_CITY_STATE_ZIP'] = 'VOL_FULL_CITY_ADDRESS';
            combinedFieldsRealFieldNames['COMBINED_FULL_ADDRESS'] = 'VOL_FULL_ADDRESS';
            for (let i in forms) {
                if (!forms[i].name) continue;
                let tmpName = forms[i].name.split(':');
                if (combinedFieldsRealFieldNames[tmpName[0]]) {
                    tmpName[0] = combinedFieldsRealFieldNames[tmpName[0]];
                    let realName = tmpName.join(':');
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        name: realName,
                        force: true
                    });
                }
            }
        },
        savePreviousFileFields() {
            this.isPdfFormPrepared = false;
            this.removeCursor();
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let previousFields = [];
            for (let i in forms) {
                if (!forms[i].name) continue;
                let tmpField = {};
                for (let j in forms[i]) {
                    if (j !== 'bounds') {
                        tmpField[j] = library.cloneObj(forms[i][j]);
                    }
                }
                let x = forms[i]?.bounds?.x || forms[i]?.bounds?.X;
                let y = forms[i]?.bounds?.y || forms[i]?.bounds?.Y;
                let width = forms[i]?.bounds?.width || forms[i]?.bounds?.Width;
                let height = forms[i]?.bounds?.height || forms[i]?.bounds?.Height;
                if (!x || !y || !width || !height) continue;
                tmpField.bounds = {
                    X: x, Y: y, Width: width, Height: height
                };
                tmpField.pageNumber = tmpField.pageIndex + 1;
                delete tmpField.pageIndex;
                delete tmpField.id;
                previousFields.push(tmpField);
            }
            this.previousFileFields = library.cloneObj(previousFields);
        },
        returnPreviousFileFields(fromPreparePdfForm) {
            if (this.useNewModules && !fromPreparePdfForm) return;
            let viewer = this.$refs[this.id].ej2Instances;
            if (!library.objIsEmpty(this.previousFileFields)) {
                viewer.formFieldsModule.removeExistingFormFields();
                let truncatedFields = [];
                for (let i in this.previousFileFields) {
                    if (this.previousFileFields[i].pageNumber <= viewer.pageCount) {
                        let tmpType;
                        switch (this.previousFileFields[i].type.toLowerCase()) {
                            case 'textbox':
                                tmpType = 'Textbox';
                                break;
                            case 'password':
                                tmpType = 'Password';
                                break;
                            case 'checkbox':
                                tmpType = 'CheckBox';
                                break;
                            case 'radiobutton':
                                tmpType = 'RadioButton';
                                break;
                            case 'dropdown':
                            case 'dropdownlist':
                                tmpType = 'DropDown';
                                break;
                            case 'listbox':
                                tmpType = 'ListBox';
                                break;
                            case 'signaturefield':
                                tmpType = 'SignatureField';
                                break;
                            case 'initialfield':
                                tmpType = 'InitialField';
                                break;
                        }
                        if (tmpType) {
                            viewer.formDesignerModule.addFormField(tmpType, this.previousFileFields[i]);
                        }
                    } else {
                        if (typeof truncatedFields[this.previousFileFields[i].pageNumber] === "undefined") truncatedFields[this.previousFileFields[i].pageNumber] = "Page " + this.previousFileFields[i].pageNumber + ': <br/>';
                        truncatedFields[this.previousFileFields[i].pageNumber] += (this.previousFileFields[i].tooltip || this.previousFileFields[i].name) + "<br/>";
                    }
                }
                if (truncatedFields.length) {
                    truncatedFields = truncatedFields.filter(function (element) {
                        return element !== undefined;
                    });
                    let msg = 'Following fields will be truncated because current file has less pages than previous.<br/><br/>' + truncatedFields.join('<br/><br/>');
                    library.showDialog(msg, false, () => {
                        this.previousFileFields = [];
                        this.reload();
                    }, {
                        okButtonLabel: 'Continue',
                        cancelButtonLabel: 'Roll back file'
                    });
                }
            }
        },
        selectFieldsByDrag(tmpSelX, tmpSelY, tmpSelWidth, tmpSelHeight) {
            if (!tmpSelWidth || !tmpSelHeight) {
                if (!(event.metaKey || event.ctrlKey)) {
                    this.selectedFields = [];
                    this.redrawSelectedFields();
                    this.checkAligmentDisabled();
                }
                return;
            }
            this.selectedFields = [];
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let tmpSelXY = viewer.convertClientPointToPagePoint({ x: tmpSelX, y: tmpSelY - 84 }, viewer.currentPageNumber);
            let selX = tmpSelXY.x;
            let selY = tmpSelXY.y;
            tmpSelXY = viewer.convertClientPointToPagePoint({ x: tmpSelX + tmpSelWidth, y: tmpSelY - 84 + tmpSelHeight }, viewer.currentPageNumber);
            let selWidth = tmpSelXY.x;
            let selHeight = tmpSelXY.y;
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (forms[i].name === 'CURSOR_POSITION') continue;
                let tmpField = forms[i];
                let x = tmpField?.bounds?.x || tmpField?.bounds?.X;
                let y = tmpField?.bounds?.y || tmpField?.bounds?.Y;
                let width = tmpField?.bounds?.width || tmpField?.bounds?.Width;
                let height = tmpField?.bounds?.height || tmpField?.bounds?.Height;
                let tmpXY = viewer.convertPagePointToScrollingPoint({ x: x, y: y }, tmpField.pageIndex + 1);
                if (tmpXY.x >= selX && tmpXY.x <= selWidth && tmpXY.y >= selY && tmpXY.y <= selHeight) {
                    if (this.selectedFields.indexOf(tmpField.id) === -1) this.selectedFields.push(tmpField.id);
                }
            }
            this.redrawSelectedFields();
            this.checkAligmentDisabled();
        },
        documentLoad: function () {
            /* if (typeof this.$refs[this.id].ej2Instances.viewerBase.viewerContainer.addEventListener === 'function') {
                // do not prevent default browser pinch zoom
                this.$refs[this.id].ej2Instances.viewerBase.viewerContainer.addEventListener('touchmove', function (e) {
                    if (e && e?.scale && e.scale !== 1) e.stopImmediatePropagation();
                }, true);
            } */
            this.loadAllPages();
            library.hideLoadingIndicator(this.realWrapperID);
            this.pdfLoaded = true;
            this.completeLoad();
            this.pdfIsChanged = false;
            this.pdfFieldsChanged = false;
            this.afterSaveIsChecked = 0;
            this.showToolbarButtons(true);
            this.removeCombinedFields();
            this.reloadControlColors();
            if (this.useNewModules) {
                this.preparePdfForm({ name: "pageRenderComplete" });
            }
            if (!this.enableMagnification) {
                let dropDownInput = document.getElementById(this.id + '_zoomDropDown');
                if (dropDownInput) {
                    dropDownInput.disabled = true;
                }
            }

            if (this.realMode !== 'viewer') {
                this.prevFieldsData = this.getCurrentFieldsOnDocument();
            }

            if (this.realMode === 'viewer') this.reloadUsedControls();
            if (this.realMode === 'editor' || this.realMode === 'editor-popup') {
                let tmpToolbarObj = document.getElementById(this.id + '_toolbarItemsContainer');
                if (tmpToolbarObj) {
                    let tmpToolbarLeftObj = tmpToolbarObj.querySelector('.e-toolbar-left');
                    if (tmpToolbarLeftObj) {
                        tmpToolbarLeftObj.innerHTML += '<div id="' + this.id + '_toolbarAligmentContainer" class="e-toolbar-item e-template aligment-container"></div>';
                        this.aligmentContainer = document.getElementById(this.id + '_toolbarAligmentContainer');
                    }
                }
                let tmpPageObj = document.getElementById(this.id + '_viewerContainer');
                if (tmpPageObj) {
                    document.addEventListener("keydown", this.move, true);
                    const mouseSelectDom = document.querySelector('.form-fields-selection');
                    let draggingMouse = false;
                    let leftMouseDrag;
                    let topMouseDrag;
                    const startDrag = (e) => {
                        if (e.type === 'mousedown') {
                            draggingMouse = true;
                            const offset = mouseSelectDom.getBoundingClientRect();
                            leftMouseDrag = e.pageX;
                            topMouseDrag = e.pageY - 84;
                            mouseSelectDom.style.top = topMouseDrag + 'px';
                            mouseSelectDom.style.left = leftMouseDrag + 'px';
                        }
                    };
                    let width;
                    let height;
                    const stopDrag = () => {
                        if (typeof width !== 'undefined' && typeof height !== 'undefined') {
                            this.selectFieldsByDrag(leftMouseDrag, topMouseDrag, width, height);
                        }
                        draggingMouse = false;
                        leftMouseDrag = 0;
                        topMouseDrag = 0;
                        width = undefined;
                        height = undefined;
                        document.querySelector('.form-fields-selection').removeAttribute('style');
                    };
                    const getDragSize = (e) => {
                        if (draggingMouse) {
                            const top = e.pageY - topMouseDrag - 84;
                            const left = e.pageX - leftMouseDrag;

                            width = Math.abs(left);
                            height = Math.abs(top);

                            if (top < 0) {
                                mouseSelectDom.style.top = e.pageY + 'px';
                            } else {
                                mouseSelectDom.style.top = topMouseDrag + 'px';
                            }

                            if (left < 0) {
                                mouseSelectDom.style.left = e.pageX + 'px';
                            } else {
                                mouseSelectDom.style.left = leftMouseDrag + 'px';
                            }
                            mouseSelectDom.style.width = width + 'px';
                            mouseSelectDom.style.height = height + 'px';
                        }
                    };
                    tmpPageObj.addEventListener('mousedown', startDrag);
                    tmpPageObj.addEventListener('mouseup', stopDrag);
                    tmpPageObj.addEventListener('mousemove', getDragSize);
                }

                return;
            }
            if ((this.config.disableFields || this.disableFields) && this.hash) this.disableFieldsFunc();
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let formFields = [];
            let pspFormFields = {};
            // configStore.updateDateTime();
            // let date = configStore.state.cfg.currentDate || "";
            // let dateTime = configStore.state.cfg.currentDate + " " + configStore.state.cfg.currentTime;
            for (let i = 0; i < forms.length; i++) {
                /* if (forms[i].name.indexOf('CURRENT_DATE') !== -1 || forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1) {
                    if (forms[i].value) continue;
                    if (this.signerIndex !== false
                        && (
                            (this.signerIndex === 0 && forms[i].name.indexOf('::SIGNER_') !== -1)
                            || (this.signerIndex > 0 && forms[i].name.indexOf('::SIGNER_' + this.signerIndex) === -1)
                        )
                    ) {
                        continue;
                    }
                    if (this.signerIndex === false && forms[i].name.indexOf('::SIGNER_') !== -1) continue;
                    let tmpVal = forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1 ? dateTime : date;
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        value: tmpVal,
                        isReadOnly: false,
                        force: true
                    });
                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                        isReadOnly: true,
                        force: true
                    });
                } */
                if (forms[i].value) continue;
                if (forms[i].name.indexOf("[") !== -1 && forms[i].name.indexOf("]") !== -1) {
                    let fieldNameWOOptionId = forms[i].name.split('[')[0];
                    formFields.push(fieldNameWOOptionId);
                }
                let formFieldName = forms[i].name;
                if (formFieldName.indexOf('#') !== -1 && formFieldName.indexOf('_CONTACT#') === -1) {
                    let isCA = formFieldName.split('#')[0].indexOf('/') !== -1;
                    if (isCA) {
                        let tmpFormFieldName = formFieldName.split('#');
                        tmpFormFieldName[0] = 'CUSTOM_ATTRIBUTE';
                        formFieldName = tmpFormFieldName.join('#');
                    }
                }
                if (formFieldName.indexOf('::SIGNER_') !== -1) {
                    let tmp = forms[i].name.split('::SIGNER_');
                    let index = tmp && tmp[1] && parseInt(tmp[1], 10) >= 0 ? parseInt(tmp[1], 10) : false;
                    if (index !== false && this.signerIndex !== false && index === this.signerIndex) {
                        formFieldName = formFieldName.split('::SIGNER_')[0];
                    } else {
                        continue;
                    }
                }
                if (formFieldName.startsWith('SUBPROGRAM')) {
                    if (typeof pspFormFields['assignment'] === 'undefined') pspFormFields['assignment'] = [];
                    pspFormFields['assignment'].push(this.removePrefixFromName(formFieldName));
                } else {
                    formFields.push(this.removePrefixFromName(formFieldName));
                }
                if (forms[i].type === "InitialField") {
                    formFields.push('VOL_FIRST_NAME');
                    formFields.push('VOL_LAST_NAME');
                }
                if (this.combinedFields[forms[i].name] && Array.isArray(this.combinedFields[forms[i].name].combined)) {
                    formFields.push(...this.combinedFields[forms[i].name].combined);
                }
                if (forms[i].name === 'PDFLEADERSIGNATURE' || forms[i].name === 'PDFLEADERINITIAL') {
                    formFields.push('IS_LEADER');
                }
            }
            if (this.isPdfOnly && !forms.length && !this.hash) {
                this.pdfErrorHandler({ action: 'FormFieldsEmpty' });
                return;
            }
            if (formFields.length) {
                formFields = formFields.filter((v, i, a) => a.indexOf(v) === i);
                this.foundedFields = [];
                for (let pspName in this.pspIds) {
                    if (pspName === 'length') continue;
                    if (pspName === 'lbe' || pspName === 'slot' || pspName === 'endpoint') continue;
                    let pspId = this.pspIds[pspName];
                    if (pspId) {
                        // eslint-disable-next-line no-loop-func
                        let responseFnc = async (response) => {
                            for (let i = 0; i < forms.length; i++) {
                                if (typeof PROJECT_NAME !== 'undefined'
                                    && PROJECT_NAME === 'RECRUITER'
                                    && pspName === 'vol'
                                    && (forms[i].type === "InitialField" || forms[i].type === "SignatureField"))
                                {
                                    if (response['IS_LEADER']) {
                                        if (forms[i].name !== 'PDFLEADERSIGNATURE' && forms[i].name !== 'PDFLEADERINITIAL') {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], { /* visibility: 'hidden' */ isReadOnly: true });
                                        }
                                    } else {
                                        if (forms[i].name === 'PDFLEADERSIGNATURE' || forms[i].name === 'PDFLEADERINITIAL') {
                                            this.localUpdateFormField(viewer.formFieldCollections[i], { /* visibility: 'hidden' */ isReadOnly: true });
                                        }
                                    }
                                }

                                let fieldName = this.removePrefixFromName(forms[i].name);
                                let realFieldName = forms[i].name;
                                if (fieldName.indexOf('#') !== -1 && fieldName.indexOf('_CONTACT#') === -1) {
                                    let isCA = fieldName.split('#')[0].indexOf('/') !== -1;
                                    if (isCA) {
                                        let tmpFormFieldName = fieldName.split('#');
                                        tmpFormFieldName[0] = 'CUSTOM_ATTRIBUTE';
                                        fieldName = tmpFormFieldName.join('#');
                                    }
                                }
                                if (fieldName.indexOf('::SIGNER_') !== -1) {
                                    let tmp = forms[i].name.split('::SIGNER_');
                                    let index = tmp && tmp[1] && parseInt(tmp[1], 10) >= 0 ? parseInt(tmp[1], 10) : false;
                                    if (index !== false && this.signerIndex !== false && index === this.signerIndex) {
                                        fieldName = fieldName.split('::SIGNER_')[0];
                                    } else {
                                        continue;
                                    }
                                } else if (this.signerIndex > 0) {
                                    continue;
                                }
                                if (this.combinedFields[fieldName] && Array.isArray(this.combinedFields[fieldName].combined)) {
                                    let tmpCombinedValue = [];
                                    for (let tmpCombinedI in this.combinedFields[fieldName].combined) {
                                        let tmpCombinedFieldName = this.combinedFields[fieldName].combined[tmpCombinedI];
                                        if (response[tmpCombinedFieldName]) {
                                            tmpCombinedValue.push(response[tmpCombinedFieldName]);
                                        }
                                    }
                                    if (tmpCombinedValue.length) {
                                        response[fieldName] = tmpCombinedValue.join(', ');
                                    }
                                }
                                let fieldNameWOOptionId = '';
                                // eslint-disable-next-line no-await-in-loop
                                let availableControls = await configStore.getAvailableFieldList(pspName);
                                if (forms[i].name.indexOf("[") !== -1 && forms[i].name.indexOf("]") !== -1) {
                                    fieldNameWOOptionId = forms[i].name.split('[')[0];
                                }
                                if (response
                                    && response['VOL_FIRST_NAME']
                                    && response['VOL_LAST_NAME']
                                    && !forms[i].value
                                ) {
                                    this.initialValue = ' ' + response['VOL_FIRST_NAME'].charAt(0) + '. ' + response['VOL_LAST_NAME'].charAt(0) + '. ';
                                }
                                if (typeof response[fieldName] === 'undefined' && typeof response[fieldNameWOOptionId] === 'undefined') continue;
                                if (this.foundedFields.indexOf(realFieldName) === -1) this.foundedFields.push(realFieldName);
                                if (forms[i].value) continue;
                                if (forms[i].type === "Checkbox" || forms[i].type === "RadioButton") {
                                    let tmpAvailableControls = library.cloneObj(availableControls);
                                    if (forms[i].type === "Checkbox"
                                        && fieldNameWOOptionId
                                        && typeof response[fieldNameWOOptionId] !== 'undefined'
                                        && tmpAvailableControls
                                        && typeof tmpAvailableControls[fieldNameWOOptionId] !== 'undefined'
                                    ) {
                                        let tmpControl = library.cloneObj(tmpAvailableControls[fieldNameWOOptionId]);
                                        if (tmpControl) {
                                            for (let i in tmpControl.LIST_VALUES) {
                                                if (typeof tmpControl.LIST_VALUES[i].label === 'string') {
                                                    tmpControl.LIST_VALUES[i].label = tmpControl.LIST_VALUES[i].label.toLowerCase();
                                                }
                                            }
                                            if (typeof response[fieldNameWOOptionId] === 'string') {
                                                response[fieldNameWOOptionId] = [response[fieldNameWOOptionId]];
                                            }
                                            for (let i in response[fieldNameWOOptionId]) {
                                                if (typeof response[fieldNameWOOptionId][i] === 'string') {
                                                    response[fieldNameWOOptionId][i] = response[fieldNameWOOptionId][i].toLowerCase();
                                                }
                                            }
                                            let checkedList = tmpControl.LIST_VALUES.find((tmp) => response[fieldNameWOOptionId].indexOf(tmp.label.toString()) !== -1 && forms[i].name === fieldNameWOOptionId + '[' + tmp.id + ']');
                                            if (!checkedList && tmpControl.CUSTOM_ATTRIBUTE_STORE_AS_TEXT) {
                                                checkedList = tmpControl.LIST_VALUES.find((tmp) => response[fieldNameWOOptionId].indexOf(tmp.label.toString()) !== -1 && forms[i].name === fieldNameWOOptionId + '[' + tmp.label + ']');
                                            }
                                            if (checkedList) {
                                                let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                                this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                    isChecked: true,
                                                    isReadOnly: false,
                                                    force: true
                                                }, true);
                                                if (prevIsReadOnly) {
                                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                        isReadOnly: true,
                                                        force: true
                                                    }, true);
                                                }
                                            }
                                        }
                                    }
                                    if (forms[i].type === "RadioButton"
                                        && typeof response[fieldName] !== 'undefined'
                                        && tmpAvailableControls
                                        && typeof tmpAvailableControls[fieldName] !== 'undefined'
                                    ) {
                                        let tmpControl = library.cloneObj(tmpAvailableControls[fieldName]);
                                        if (tmpControl) {
                                            let selectedListIndex = tmpControl.LIST_VALUES.findIndex((tmp) => response[fieldName] === tmp.label.toString());
                                            if (selectedListIndex >= 0) {
                                                let tmpFiltered = forms.filter((tmp) => tmp.name === fieldName);
                                                for (let k in tmpFiltered) {
                                                    if (k === 'length') continue;
                                                    if (tmpFiltered[k].id === forms[i].id && parseInt(k, 10) === selectedListIndex) {
                                                        let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                            isSelected: true,
                                                            isReadOnly: false,
                                                            force: true
                                                        }, true);
                                                        if (prevIsReadOnly) {
                                                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                                                isReadOnly: true,
                                                                force: true
                                                            }, true);
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                } else {
                                    let prevIsReadOnly = viewer.formFieldCollections[i].isReadOnly;
                                    this.localUpdateFormField(viewer.formFieldCollections[i], {
                                        value: response[fieldName],
                                        isReadOnly: false,
                                        force: true
                                    }, true);
                                    if (prevIsReadOnly) {
                                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                                            isReadOnly: true,
                                            force: true
                                        }, true);
                                    }
                                }
                            }
                            this.$nextTick(() => {
                                this.setInitialFieldsValues();
                            });
                        };
                        // eslint-disable-next-line no-loop-func
                        if (pspName !== 'assignment') {
                            this.sDs.post('er_' + pspName + 'Field', formFields, pspId, false).then((response) => {
                                if (response.status === 'success' && response.data && response.data[pspId]) responseFnc(response.data[pspId]);
                            });
                        } else if (pspName === 'assignment' && pspFormFields['assignment'] && pspFormFields['assignment'].length) {
                            this.sDs.post('sds_getAffiliationSubProgramData', pspId, pspFormFields['assignment']).then((response) => {
                                if (response.status === 'success' && response.data) responseFnc(response.data);
                            });
                        }
                    }
                }
            }
            this.$nextTick(() => {
                this.setInitialFieldsValues();
                // eslint-disable-next-line camelcase
                if (typeof _LbeProfile_updateFrame === 'function') _LbeProfile_updateFrame(1);
                // eslint-disable-next-line camelcase
                if (typeof lib_resizeToContent === 'function') lib_resizeToContent(1);
                if (typeof drrAttachMissingHandlers === 'function') {
                    drrAttachMissingHandlers();
                    if (this.realMode === 'viewer' && parseInt(this.attachId, 10) > 0) {
                        let fileInputElement = document.getElementById(this.controlName + '_file');
                        if (fileInputElement) {
                            if ("createEvent" in document) {
                                let evt = document.createEvent("HTMLEvents");
                                evt.initEvent("change", false, true);
                                fileInputElement.dispatchEvent(evt);
                            }
                            else {
                                fileInputElement.fireEvent("onchange");
                            }
                        }
                    }
                }
            });
        },
        formFieldsRequiredValidation: function () {
            let errorMessage = "Please complete all required field(s)";
            let errorFields = '';
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (i === 'length') continue;
                if (typeof this.usedControls[forms[i].name] !== 'undefined' && this.usedControls[forms[i].name].IS_REQUIRED && !forms[i].value) {
                    errorFields += "<br>" + forms[i].name;
                }
            }
            if (errorFields) {
                return errorMessage + ":<br>" + errorFields;
            }
            return '';
        },
        formFieldsValidation: function () {
            let errorMessage = '';
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (forms[i].visibility === 'hidden') continue;
                if (i === 'length') continue;
                if (typeof this.usedControls[forms[i].name] === 'undefined' || !forms[i].value) continue;
                let controlLabel = this.usedControls[forms[i].name].CUSTOM_ATTRIBUTE_LABEL || this.usedControls[forms[i].name].CUSTOM_ATTRIBUTE_NAME || '';
                if (this.usedControls[forms[i].name].SIMPLE_TYPE === 'DATE') {
                    let controlValue = configStore.getDate(forms[i].value);
                    let dateFormat = configStore.getDateFormat();
                    if (controlValue === 'Invalid date' || controlValue.length !== dateFormat.length) {
                        errorMessage += controlLabel + " has the wrong format. Please enter in <strong>" + dateFormat + "</strong> format.<br>";
                    } else if (typeof this.usedControls[forms[i].name].MAXVALUE !== 'undefined' || typeof this.usedControls[forms[i].name].MINVALUE !== 'undefined') {
                        let maxValue = typeof this.usedControls[forms[i].name].MAXVALUE !== 'undefined' ? this.usedControls[forms[i].name].MAXVALUE : false;
                        let minValue = typeof this.usedControls[forms[i].name].MINVALUE !== 'undefined' ? this.usedControls[forms[i].name].MINVALUE : false;
                        let _moment = library.getMomentObj();
                        if (maxValue && minValue) {
                            if (!_moment(controlValue).isBetween(minValue, maxValue)) {
                                errorMessage += controlLabel + " must be a date between " + configStore.getDate(minValue) + " and " + configStore.getDate(maxValue) + ".<br>";
                            }
                        } else if (maxValue) {
                            if (_moment(controlValue).isAfter(maxValue)) {
                                errorMessage += controlLabel + " must be a date before " + configStore.getDate(maxValue) + ".<br>";
                            }
                        } else if (minValue) {
                            if (_moment(controlValue).isBefore(minValue)) {
                                errorMessage += controlLabel + " must be a date after " + configStore.getDate(minValue) + ".<br>";
                            }
                        }
                    }
                }
                if (this.usedControls[forms[i].name].SIMPLE_TYPE === 'INTEGER') {
                    let controlValue = parseInt(forms[i].value, 10);
                    let maxValue = typeof this.usedControls[forms[i].name].MAXVALUE !== 'undefined' ? this.usedControls[forms[i].name].MAXVALUE : 9999999999;
                    let minValue = typeof this.usedControls[forms[i].name].MINVALUE !== 'undefined' ? this.usedControls[forms[i].name].MINVALUE : -9999999999;
                    if (isNaN(controlValue) || controlValue > maxValue || controlValue < minValue || controlValue % 1 !== 0) {
                        errorMessage += controlLabel + " must be a whole number between " + minValue + " and " + maxValue + ".<br>";
                    }
                }
                if (this.usedControls[forms[i].name].SIMPLE_TYPE === 'FLOAT') {
                    let controlValue = parseFloat(forms[i].value);
                    let maxValue = typeof this.usedControls[forms[i].name].MAXVALUE !== 'undefined' ? this.usedControls[forms[i].name].MAXVALUE : 999999999999999;
                    let minValue = typeof this.usedControls[forms[i].name].MINVALUE !== 'undefined' ? this.usedControls[forms[i].name].MINVALUE : -999999999999999;
                    if (isNaN(controlValue) || controlValue > maxValue || controlValue < minValue) {
                        errorMessage += controlLabel + " must be a number between " + minValue + " and " + maxValue + ".<br>";
                    } else if (this.usedControls[forms[i].name].CUSTOM_ATTRIBUTE_WIDTH && controlValue.toString().length > this.usedControls[forms[i].name].CUSTOM_ATTRIBUTE_WIDTH) {
                        errorMessage += "The length of " + controlLabel + " cannot be more than " + this.usedControls[forms[i].name].CUSTOM_ATTRIBUTE_WIDTH + " characters.<br>";
                    }
                }
            }
            return errorMessage;
        },
        fixCorruptedSignatures: function () {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let errorMessage = '';
            for (let i = 0; i < forms.length; i++) {
                if (
                    (
                        forms[i].type === "SignatureField"
                        || forms[i].type === "InitialField"
                    )
                    && typeof forms[i].value === 'string'
                    && forms[i].value
                    && forms[i].value.indexOf(';base64,') === -1 // handwritten signature
                    && forms[i].value.indexOf('NaN') !== -1 // do not allow NaN in handwritten signatures
                ) {
                    errorMessage = "Handwriting signature data corrupted. Please fill in again.";
                    this.clearSignature(forms[i]);
                }
            }
            return errorMessage;
        },
        requiredValidation: function () {
            let errorMessage = "Please complete all required field(s)";
            let errorFields = '';
            let corruptedSignatures = this.fixCorruptedSignatures();
            if (corruptedSignatures) return corruptedSignatures;
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let flag = false;
            let radioGroupName = "";
            for (let i = 0; i < forms.length; i++) {
                if (forms[i].visibility === 'hidden') continue;
                let text = "";
                let isRequired = (this.isPdfOnly && (forms[i].type === "SignatureField" || forms[i].type === "InitialField")) ? false : forms[i].isRequired;
                // if (forms[i].type !== "SignatureField" && forms[i].type !== "InitialField" && this.foundedFields.indexOf(forms[i].name) === -1) continue;
                if (isRequired === true)
                {
                    if (forms[i].type.toString() === "Checkbox" && viewer.formFieldCollection[i].isChecked === false) {
                        text = forms[i].tooltip || forms[i].name;
                    }
                    else if (forms[i].type === "RadioButton" && flag === false) {
                        radioGroupName = forms[i].tooltip || forms[i].name;
                        if (forms[i].isSelected === true) flag = true;
                    }
                    else if (forms[i].type.toString() !== "Checkbox" && forms[i].type !== "RadioButton" && forms[i].value.trim() === "") {
                        text = forms[i].tooltip || forms[i].name;
                        if (text.toUpperCase().indexOf('PDFSIGNATURE') !== -1) {
                            text = 'Signature';
                        }
                        if (text.toUpperCase().indexOf('PDFLEADERSIGNATURE') !== -1) {
                            text = 'Leader Signature';
                        }
                        if (text.toUpperCase().indexOf('PDFINITIAL') !== -1) {
                            text = 'Initial';
                        }
                        if (text.toUpperCase().indexOf('PDFLEADERINITIAL') !== -1) {
                            text = 'Leader Initial';
                        }
                    }
                    if (text !== "")
                    {
                        errorFields += "<br>" + text;
                    }
                }
            }
            if (!flag && radioGroupName !== "")
            {
                errorFields += "<br>" + radioGroupName;
            }
            if (this.pdfRequiredFields && this.pdfRequiredFields.length) {
                for (let i in this.pdfRequiredFields) {
                    let tmpForceControl = this.forceControls.find((tmp) => tmp.realName === this.pdfRequiredFields[i]);
                    let controlLabel = this.usedControls && this.usedControls[this.pdfRequiredFields[i]] ? this.usedControls[this.pdfRequiredFields[i]].CUSTOM_ATTRIBUTE_LABEL || this.usedControls[this.pdfRequiredFields[i]].CUSTOM_ATTRIBUTE_NAME : this.pdfRequiredFields[i];
                    let simpleType = this.usedControls && this.usedControls[this.pdfRequiredFields[i]] ? this.usedControls[this.pdfRequiredFields[i]].SIMPLE_TYPE : false;
                    if (simpleType === 'TIME') {
                        let tmpForceControlHour = this.forceControls.find((tmp) => tmp.realName === this.pdfRequiredFields[i] + '_hour');
                        let tmpForceControlMinute = this.forceControls.find((tmp) => tmp.realName === this.pdfRequiredFields[i] + '_minute');
                        if (!tmpForceControlHour || !tmpForceControlMinute) {
                            errorFields += "<br>" + controlLabel;
                        }
                    } else {
                        if (!tmpForceControl) {
                            errorFields += "<br>" + controlLabel;
                        }
                    }
                }
            }
            if (errorFields) {
                return errorMessage + ":<br>" + errorFields;
            }
            return '';
        },
        /* backendValidation: async function () {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let fieldToValidate = {};
            let errorMessage = '';
            for (let i = 0; i < forms.length; i++) {
                if (forms[i].type === "SignatureField" || forms[i].type === "InitialField") continue;
                fieldToValidate[forms[i].name] = forms[i].value;
            }
            for (let pspName in this.pspIds) {
                // eslint-disable-next-line no-await-in-loop
                let response = await this.sDs.post('sds_validateFields', fieldToValidate, pspName, false);
                if (response.status === 'success' && response.data && response.data.addInfo) {
                    errorMessage += response.data.addInfo;
                }
            }
            return errorMessage;
        }, */
        formIsValid: async function (showMessage = true) {
            if (this.disabled) return true;
            if (this.realMode === 'viewer' && this.fileNotLoaded) return true;
            if (this.realMode === 'viewer' && (!this.pdfLoaded || this.fileNotLoaded) && this.realIsInline) return true;
            if (this.realMode === 'viewer' && !this.isInline && (this.pdfLoaded || this.hash)) return true;
            if (this.realMode === 'viewer' && !this.realIsInline && !this.popupDocumentViewed) {
                library.showDialog('Please view the document.', false, false, { showAsAlert: true });
                try {
                    document.getElementById(this.cId + '_viewDocumentLink').focus();
                } catch (e) {
                    // do nothing
                }
                return false;
            }
            if (!this.pdfLoaded && this.realIsInline) {
                if (this.realMode === 'viewer') library.showDialog('Please wait for file load.', false, false, { showAsAlert: true });
                else library.showDialog('Please upload file.', false, false, { showAsAlert: true });
                return false;
            }
            if (this.realMode === 'editor-popup' && this.controls['TEMPLATE_NAME'].value.trim() === '') {
                library.showDialog('Template Name is required.', false, false, { showAsAlert: true });
                return false;
            }
            if (this.realMode === 'editor-popup' && this.controls['TEMPLATE_NAME'].value.indexOf('.pdf') === -1) {
                this.controls['TEMPLATE_NAME'].value += '.pdf';
            }
            if (this.realMode !== 'viewer' && !this.validateSignersFields()) {
                return false;
            }
            if (this.realMode !== 'viewer') {
                let requiredAndReadOnly = [];
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (forms[i].isReadOnly && forms[i].isRequired) {
                        requiredAndReadOnly.push(forms[i].name);
                    }
                }
                if (requiredAndReadOnly.length) {
                    library.showDialog('Control cannot be required and readonly at the same time.<br/>Please change settings for the following control' + (requiredAndReadOnly.length > 1 ? 's' : '') + ':<br/>' + requiredAndReadOnly.join('<br/>'), false, false, {
                        title: 'Warning', showAsAlert: true
                    });
                    return false;
                }
            }
            if (this.realMode === 'editor' || this.realMode === 'editor-popup' || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances) return true;
            if (this.realMode === 'viewer' && !this.agreementChecked) return false;
            let viewer = this.$refs[this.id].ej2Instances;
            await this.reloadForceControls();
            let requiredMessage = this.requiredValidation();
            if (requiredMessage) {
                if (showMessage) {
                    library.showDialog(requiredMessage, false, false, {
                        title: 'Warning', name: this.id + 'pdfValidationWarning', cssClass: 'ui-dialog', showAsAlert: true
                    });
                    // viewer.showNotificationPopup(requiredMessage);
                }
                return false;
            }
            let formFieldsRequiredValidation = this.formFieldsRequiredValidation();
            if (formFieldsRequiredValidation) {
                if (showMessage) {
                    library.showDialog(formFieldsRequiredValidation, false, false, {
                        title: 'Warning', name: this.id + 'pdfValidationWarning', cssClass: 'ui-dialog', showAsAlert: true
                    });
                    // viewer.showNotificationPopup(formFieldsRequiredValidation);
                }
                return false;
            }
            let formFieldValidation = this.formFieldsValidation();
            if (formFieldValidation) {
                if (showMessage) {
                    library.showDialog(formFieldValidation, false, false, {
                        title: 'Warning', name: this.id + 'pdfValidationWarning', cssClass: 'ui-dialog', showAsAlert: true
                    });
                    // viewer.showNotificationPopup(formFieldValidation);
                }
                return false;
            }
            if (this.isPdfOnly) {
                let viewer = this.$refs[this.id].ej2Instances;
                let forms = viewer.formFieldCollections;
                if (this.isPdfOnly && !forms.length && !this.hash) {
                    this.pdfErrorHandler({ action: 'FormFieldsEmpty' });
                    return false;
                }
            }
            /* let backendMessage = await this.backendValidation();
            if (backendMessage) {
                if (showMessage) viewer.showNotificationPopup(backendMessage);
                return false;
            } */
            return true;
        },
        fieldSelect: function (event) {
            this.selectedControl = false;
            if (event && event.data && event.data.value) {
                this.selectedFieldGroup = event.index;
                this.selectedFieldGroupName = event.text;
            } else if (
                event
                && this.fieldsItems
                && this.selectedFieldGroup >= 0
                && this.fieldsItems[this.selectedFieldGroup].child
                && this.fieldsItems[this.selectedFieldGroup].child[event.index - 1]
            ) {
                let prefix = this.getControlPrefix(this.fieldsItems[this.selectedFieldGroup].child[event.index - 1]);
                let text = this.fieldsItems[this.selectedFieldGroup].child[event.index - 1].text
                    .replace('VOL::', '')
                    .replace('OPP::', '')
                    .replace('ORG::', '')
                    .replace('ACC::', '')
                    .replace('CLI::', '')
                    .replace('LBE::', '');
                this.selectedControl = {
                    name: prefix + this.fieldsItems[this.selectedFieldGroup].child[event.index - 1].value,
                    text: text
                };
            } else {
                this.selectedFieldGroup = false;
            }
        },
        getGroupIndex: function (text) {
            let tmpSelectedGroup;
            switch (text) {
                case 'Account':
                    tmpSelectedGroup = 0;
                    break;
                case 'Client':
                    tmpSelectedGroup = 1;
                    break;
                case 'Opportunity':
                    tmpSelectedGroup = 2;
                    break;
                case 'Organization':
                    tmpSelectedGroup = 3;
                    break;
                case 'Volunteer':
                    tmpSelectedGroup = 4;
                    break;
                case 'Logged Data':
                    tmpSelectedGroup = 5;
                    break;
                case 'PDF':
                    tmpSelectedGroup = 6;
                    break;
                default:
                    tmpSelectedGroup = false;
                    break;
            }
            return tmpSelectedGroup;
        },
        controlSelect: function (event) {
            this.selectedControl = false;
            if (event && event.data && event.data.value) {
                this.selectedGroup = event.index;
                this.selectedGroupName = event.text;
            } else if (
                event
                && this.controlsItems
                && this.selectedGroup >= 0
                && this.controlsItems[this.selectedGroup].child
                && this.controlsItems[this.selectedGroup].child[event.index - 1]
            ) {
                let text = this.controlsItems[this.selectedGroup].child[event.index - 1].text
                    .replace('VOL::', '')
                    .replace('OPP::', '')
                    .replace('ORG::', '')
                    .replace('ACC::', '')
                    .replace('CLI::', '')
                    .replace('LBE::', '');
                this.selectedControl = {
                    name: this.controlsItems[this.selectedGroup].child[event.index - 1].value,
                    text: text
                };
            }
        },
        removePrefixFromName(name) {
            return name.replace('VOL::', '')
                .replace('OPP::', '')
                .replace('ORG::', '')
                .replace('ACC::', '')
                .replace('CLI::', '')
                .replace('LBE::', '');
        },
        controlInsert: function (event, control, props, warningShown, insertInCurrentPosition) {
            if (this.selectedControl && this.pdfLoaded) {
                let viewer = this.$refs[this.id].ej2Instances;
                if (viewer.formDesignerModule && typeof viewer.formDesignerModule.addFormField === 'function') {
                    /*
                    // VMS-8101
                    viewer.annotation.addAnnotation("FreeText", {
                        offset: { x: 150, y: 200 },
                        width: 150,
                        height: 24,
                        isLock: false,
                        defaultText: this.selectedControl.text + ' :',
                        pageNumber: viewer.currentPageNumber
                    });
                    */
                    let currentPosition = {
                        x: 300,
                        y: 200,
                        pageNumber: viewer.currentPageNumber
                    };
                    let cursorPosition;
                    if (!library.objIsEmpty(this.cursorPosition) && !insertInCurrentPosition) {
                        cursorPosition = library.cloneObj(this.cursorPosition);
                    } else {
                        cursorPosition = library.cloneObj(currentPosition);
                    }
                    if (!warningShown && viewer.currentPageNumber !== cursorPosition.pageNumber) {
                        library.showDialog('The cursor to insert is on another page of the PDF document, would you like to insert the control on the current page or at the place where the cursor is?',
                            () => {
                                this.controlInsert(false, false, false, true, true);
                            },
                            () => {
                                this.controlInsert(false, false, false, true);
                                let forms = viewer.formFieldCollections;
                                if (forms.length) {
                                    for (let i = 0; i < forms.length; i++) {
                                        if (forms[i].name === "CURSOR_POSITION") {
                                            viewer.focusFormField(viewer.formFieldCollections[i]);
                                        }
                                    }
                                }
                            },
                            {
                                name: 'cursorOnOtherPage',
                                okButtonLabel: 'Add to the current page',
                                cancelButtonLabel: 'Add at the place where the cursor is'
                            });
                        return;
                    }
                    let fieldsToInsert = [];
                    let annotationsToInsert = [];
                    let fieldType = "Textbox";
                    if (this.selectedControl.name === 'INITIAL' || this.selectedControl.name === 'PDFLEADERINITIAL') fieldType = "InitialField";
                    if (this.selectedControl.name === 'SIGNATURE' || this.selectedControl.name === 'PDFLEADERSIGNATURE') fieldType = "SignatureField";
                    let height = 16;
                    let width = 150;
                    if (fieldType === "SignatureField") {
                        height = 48;
                    } else if (fieldType === "InitialField") {
                        height = 24;
                        width = 50;
                    }
                    fieldsToInsert[0] = {
                        fieldType: fieldType,
                        options: {
                            name: this.selectedControl.name,
                            tooltip: this.selectedControl.text,
                            bounds: {
                                X: cursorPosition.x, Y: cursorPosition.y, Width: width, Height: height
                            },
                            pageNumber: cursorPosition.pageNumber
                        },
                        additionalOptions: {}
                    };
                    let tmpSelectedGroup = this.getGroupIndex(this.selectedGroupName);
                    let field = tmpSelectedGroup !== false ? this.controlsItems[this.selectedGroup].child.find((tmp) => tmp.value === this.selectedControl.name) : false;
                    let isField = false;
                    if (!field) {
                        isField = true;
                        tmpSelectedGroup = this.getGroupIndex(this.selectedFieldGroupName);
                        field = this.fieldsItems[this.selectedFieldGroup].child.find((tmp) => tmp.value === this.selectedControl.name);
                    }
                    if (field) field = library.cloneObj(field);
                    if (field && field.additionalData) {
                        let name = tmpSelectedGroup === 5 ? field.additionalData.UDFkey : this.selectedControl.name;
                        fieldsToInsert[0].options.name = name;
                        switch (field.additionalData.SIMPLE_TYPE) {
                            case "BOOLEAN":
                                if (field.additionalData.CUSTOM_ATTRIBUTE_DD_TYPE === 2 && typeof field.additionalData.LIST_VALUES === 'undefined') {
                                    field.additionalData.LIST_VALUES = [];
                                    field.additionalData.LIST_VALUES.push({
                                        id: 'Yes',
                                        label: 'Yes'
                                    });
                                }
                            // eslint-disable-next-line no-fallthrough
                            case 'LIST':
                                fieldsToInsert[0].fieldType = "DropDown";
                                if (field.value === "VOL_GEO_AREA") {
                                    field.additionalData.CUSTOM_ATTRIBUTE_DD_TYPE = 2;
                                    name = "VOL_GEO_AREA";
                                    fieldsToInsert[0].options.name = "VOL_GEO_AREA";
                                }

                                switch (field.additionalData.CUSTOM_ATTRIBUTE_DD_TYPE) {
                                    case 1:
                                        fieldType = "RadioButton";
                                        break;
                                    case 2:
                                        fieldType = "CheckBox";
                                        break;
                                    default:
                                        fieldType = "DropDown";
                                        break;
                                }
                                if (fieldType !== "CheckBox" && field.additionalData.MULTI_VALUE === 1) {
                                    fieldsToInsert[0].fieldType = "CheckBox";
                                    fieldType = "CheckBox";
                                }
                                if (fieldType !== 'DropDown') {
                                    width = 16;
                                    let tmpAnnotationWidth = 100;
                                    fieldsToInsert = [];
                                    annotationsToInsert = [];
                                    for (let k in field.additionalData.LIST_VALUES) {
                                        if (k === 'length') continue;
                                        let posX = cursorPosition.x;
                                        if ((posX + width + tmpAnnotationWidth) > 800) {
                                            posX -= 150;
                                        }
                                        let posY = cursorPosition.y;
                                        let optionName = name;
                                        if (fieldType === 'CheckBox') {
                                            optionName += '[' + field.additionalData.LIST_VALUES[k].id + ']';
                                        }
                                        if (fieldsToInsert.length) {
                                            posX = fieldsToInsert[k - 1].options.bounds.X;
                                            posY = fieldsToInsert[k - 1].options.bounds.Y + height + 5;
                                        }
                                        let fieldSettings = {
                                            fieldType: fieldType,
                                            options: {
                                                name: optionName,
                                                bounds: {
                                                    X: posX, Y: posY, Width: width, Height: height
                                                },
                                                pageNumber: cursorPosition.pageNumber
                                            }
                                        };
                                        if (fieldType === 'RadioButton') {
                                            fieldSettings.options.value = field.additionalData.LIST_VALUES[k].id;
                                        }
                                        fieldsToInsert.push(fieldSettings);
                                        annotationsToInsert.push({
                                            fieldType: 'annotation',
                                            options: {
                                                offset: { x: posX + width + 5, y: posY },
                                                width: tmpAnnotationWidth,
                                                height: height,
                                                isLock: false,
                                                defaultText: field.additionalData.LIST_VALUES[k].label + '',
                                                pageNumber: cursorPosition.pageNumber,
                                                enableAutoFit: true
                                            }
                                        });
                                    }
                                }
                                break;
                            case 'DATE':
                                delete fieldsToInsert[0].options.tooltip;
                                break;
                            case 'TEXT':
                                fieldsToInsert[0].additionalOptions['isMultiline'] = true;
                                fieldsToInsert[0].options.bounds.Height *= 3;
                            // eslint-disable-next-line no-fallthrough
                            default:
                                fieldsToInsert[0].fieldType = "Textbox";
                                break;
                        }
                    }
                    /*
                    // disabled for now
                    let pageNumber = viewer.getPageNumberFromClientPoint({ x: cursorPosition.x + width * 2, y: cursorPosition.y + height * 2 });
                    if (pageNumber >= 1 || true) {}
                    */
                    let forms = viewer.formFieldCollections;
                    let alreadyExists = false;
                    if (forms.length && !isField) {
                        for (let i = 0; i < forms.length; i++) {
                            if (forms[i].name === fieldsToInsert[0].options.name && this.canBeInsertedMultipleTimes.indexOf(fieldsToInsert[0].options.name) === -1) {
                                alreadyExists = true;
                                library.showDialog(this.selectedControl.text + ' already inserted in the template.', false, false, { showAsAlert: true });
                            }
                        }
                    }
                    if (!alreadyExists) {
                        for (let i in fieldsToInsert) {
                            if (i === 'length') continue;
                            let options = library.cloneObj(fieldsToInsert[i].options);
                            if (isField) {
                                options.isReadOnly = true;
                                if (parseInt(this.insertForTier, 10) > 0) {
                                    options.name = 'SUBPROGRAM' + this.insertForTier + ':' + options.name;
                                }
                            }
                            if (parseInt(this.selectedSignerIndex, 10) > 0) {
                                options.name += '::SIGNER_' + this.selectedSignerIndex;
                            }

                            viewer.formDesignerModule.addFormField(fieldsToInsert[i].fieldType, options);
                            if (!library.objIsEmpty(fieldsToInsert[i].additionalOptions)) {
                                let addedField = viewer.formFieldCollections.find((tmp) => tmp.name === fieldsToInsert[i].name || tmp.name === this.selectedControl.name);
                                if (addedField) {
                                    this.localUpdateFormField(addedField, fieldsToInsert[i].additionalOptions);
                                }
                            }
                        }
                        for (let i in annotationsToInsert) {
                            if (i === 'length') continue;
                            let options = library.cloneObj(annotationsToInsert[i].options);
                            viewer.annotation.addAnnotation("FreeText", options);
                        }
                    }
                    this.reloadControlColors(true);
                    this.removeCursor();
                    this.reloadForceControls();
                }
            }
        },
        onPdfStartLoading() {
            if (!this.useNewModules || !this.realIsInline) return false;
            library.showLoadingIndicator(this.realWrapperID + 'new', false, 'Please wait while document is loading, do not refresh the page.', 'spinner-with-background');
            return true;
        },
        onPdfEndLoading() {
            if (!this.useNewModules || !this.realIsInline) return false;
            library.hideLoadingIndicator(this.realWrapperID + 'new');
            return true;
        },
        isPdfFullyLoaded() {
            if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances || !this.useNewModules || !this.realIsInline) return false;
            if (this.$refs[this.id].ej2Instances.pageCount === this.renderedPages.length) {
                return true;
            }
            return false;
        },
        async preparePdfForm(event) {
            if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances || !this.useNewModules || !this.realIsInline) return;
            if (event?.name === "pageRenderComplete") {
                if (typeof event?.data?.pageNumber !== "undefined" && parseInt(event.data.pageNumber, 10) >= 0) {
                    if (this.renderedPages.indexOf(1) === -1 && event.data.pageNumber > 1) { this.renderedPages.push(1); }
                    if (this.renderedPages.indexOf(event.data.pageNumber) === -1) this.renderedPages.push(event.data.pageNumber);
                }
                if (this.isPdfFullyLoaded() && this.pdfLoaded && !this.isPdfFormPrepared) {
                    // console.log(library.cloneObj(this?.$refs[this.id]?.ej2Instances.nameTable))
                    this.isPdfFormPrepared = true;
                    if (this.realMode === 'editor' || this.realMode === 'editor-popup') this.returnPreviousFileFields(true);
                    this.$nextTick(async () => {
                        this.removeCombinedFields();
                        if (this.realMode === 'viewer') {
                            configStore.updateDateTime();
                            this.setCurrentDateTimeFieldsValue();
                        }
                        this.reloadControlColors(true);
                        if (this.realMode === 'viewer') {
                            await this.reloadUsedControls(true);
                        }
                        this.showSignatureAgreement();
                        if (this.disabled) {
                            this.disableDocument();
                        }
                        this.onPdfEndLoading();
                        if (this.realMode === 'editor' || this.realMode === 'editor-popup') {
                            let formdesignerBtn = document.getElementById(this.id + '_formdesigner');
                            if (formdesignerBtn && formdesignerBtn.classList && !formdesignerBtn.classList.contains('e-pv-select')) {
                                formdesignerBtn.click();
                            }
                        }
                        this.hideFormFieldNameIfValueIsSet();
                        this.checkNavigationFields();
                    });
                }
            }
        },
        goToNextNavigationField() {
            this.navigationFieldsText = 'Start';
            if (!this.currentNavigationField) {
                this.currentNavigationField = this.navigationFields[0].id;
            } else {
                let tmpIndex = this.navigationFields.findIndex((tmp) => tmp.id === this.currentNavigationField);
                let finded = false;
                for (let i = tmpIndex + 1; i < this.navigationFields.length; i++) {
                    if (!this.navigationFields[i].value) {
                        tmpIndex = i;
                        finded = true;
                        break;
                    }
                }
                if (!finded) {
                    for (let i = 0; i < tmpIndex; i++) {
                        if (!this.navigationFields[i].value) {
                            tmpIndex = i;
                            finded = true;
                            break;
                        }
                    }
                }
                if (tmpIndex >= this.navigationFields.length) tmpIndex = 0;
                else if (tmpIndex < 0) tmpIndex = this.navigationFields.length - 1;
                this.currentNavigationField = this.navigationFields[tmpIndex].id;
            }
            if (this.currentNavigationField) {
                let viewer = this.$refs[this.id].ej2Instances;
                let currentNavigationField = viewer.formFieldCollections.find((tmp) => tmp.id === this.currentNavigationField);
                // this.$refs[this.id].ej2Instances.focusFormField(currentNavigationField);
                this.navigationFieldsText = currentNavigationField.type === 'SignatureField' || currentNavigationField.type === 'InitialField' ? 'Sign' : 'Fill In';
                let scrollElement = document.getElementById(currentNavigationField.id + '_content_html_element');
                scrollElement.style.scrollMarginTop = '50px';
                scrollElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        checkNavigationFields() {
            if (this.realMode !== 'viewer') return;
            let forms = this.getForms();
            let navigationFields = [];
            for (let i in forms) {
                if (forms[i].isRequired && !forms[i].isReadOnly && forms[i].visibility !== 'hidden') {
                    navigationFields.push({
                        id: forms[i].id,
                        weight: forms[i].pageNumber * 100000000 + forms[i].bounds.y * 10000 + forms[i].bounds.x,
                        value: forms[i].isChecked || forms[i].isSelected || (Array.isArray(forms[i].value) && forms[i].value.length) || (!Array.isArray(forms[i].value) && forms[i].value)
                    });
                }
            }
            navigationFields = library.sortObject(navigationFields, 'weight');
            this.navigationFields = library.cloneObj(navigationFields);
            this.navigationFieldsWithoutValue = navigationFields.filter((tmp) => !tmp.value).length;
        },
        disableDocument() {
            let forms = this.getForms();
            for (let i in forms) {
                this.localUpdateFormField(forms[i], {
                    isReadOnly: true,
                    force: true
                });
            }
            let inputElements = this.$el.querySelectorAll('.e-pv-viewer-container input, .e-pv-viewer-container select, .e-pv-viewer-container textarea');
            for (let i = 0; i < inputElements.length; i++) {
                inputElements[i].disabled = true;
            }
        },
        getForms() {
            return this?.$refs?.[this.id]?.ej2Instances?.formFieldCollections || [];
        },
        async showSignatureAgreement() {
            if (
                this.realMode !== 'viewer'
                || (typeof this?.$el?.checkVisibility === 'function' && !this.$el.checkVisibility())
                || this.doNotShowAgreement
                || typeof this.accountSettings['ACCOUNT_ENABLE_ESIGNATURE_POPUP'] === 'undefined'
                || parseInt(this.accountSettings['ACCOUNT_ENABLE_ESIGNATURE_POPUP'], 10) !== 1
            ) {
                return;
            }

            let forms = this.getForms();
            let signatureExists = forms.find((tmp) => (tmp.type === 'SignatureField' || tmp.type === 'InitialField') && tmp.visibility !== 'hidden' && !tmp.isReadOnly);
            // let isSignersExists = this.templateSigners.length;
            let isCurrentSignerAgreed = false;
            let isFromCoordinator = typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'coordinator';
            if (typeof this.templateSigners?.[this.signerIndex]?.IS_AGREE_TO_USE_ESIGNATURE !== 'undefined') {
                isCurrentSignerAgreed = this.templateSigners[this.signerIndex]?.IS_AGREE_TO_USE_ESIGNATURE === 1;
            }
            if (!isFromCoordinator && signatureExists /* && isSignersExists */ && !isCurrentSignerAgreed) {
                this.agreementChecked = false;
                let dialogPrimaryButtonEle;
                let dialogComponent = {
                    name: 'esignature-agreement',
                    template: `
                        <control
                            name="ESIGNATURE_AGREEMENT"
                            :prop-config="esignatureAgreementConfig"
                            change="localChange"
                        ></control>
                    `,
                    data() {
                        return {
                            esignatureAgreementConfig: {
                                name: 'ESIGNATURE_AGREEMENT',
                                type: 'checkbox',
                                label: `I have read, understand, and agree to the provisions of the Consumer Disclosure For Electronic Signature Service linked
                                        <a href="https://samaritan.com/wp-content/uploads/Consumer_Disclosure_for_Electronic_Signature_Service.pdf" target="_blank">here</a>.`,
                                onchange: 'localChange'
                            }
                        };
                    },
                    methods: {
                        localChange(tmpEvent) {
                            if (dialogPrimaryButtonEle) {
                                if (tmpEvent.checked) {
                                    dialogPrimaryButtonEle.removeAttribute('disabled');
                                } else {
                                    dialogPrimaryButtonEle.setAttribute('disabled', true);
                                }
                            }
                        }
                    },
                    components: {
                        control: controlComponent
                    }
                };

                let dialogResponse = await library.showDialog(dialogComponent,
                    false,
                    false,
                    {
                        name: 'esignature-agreement',
                        showAsAlert: true,
                        okButtonLabel: 'Continue',
                        cssClass: 'hide-header-content-popup fullwidth-dialog mt0 one-line-content',
                        useSingletonPromise: true
                    }, false, false, (tmpDialog) => {
                        dialogPrimaryButtonEle = tmpDialog.primaryButtonEle;
                        dialogPrimaryButtonEle.setAttribute('disabled', true);
                        // disable angular submit
                        let reportServiceFrame = parent?.document?.getElementById('reportserviceframe');
                        let tmpAngular = parent?.angular;
                        if (typeof tmpAngular?.element(reportServiceFrame)?.scope()?.vm?.disableSubmit !== 'undefined') tmpAngular.element(reportServiceFrame).scope().vm.disableSubmit = true;
                    });
                if (dialogResponse && dialogResponse.status === 'success') {
                    this.agreementChecked = true;
                    // enable angular submit
                    let reportServiceFrame = parent?.document?.getElementById('reportserviceframe');
                    let tmpAngular = parent?.angular;
                    if (typeof tmpAngular?.element(reportServiceFrame)?.scope()?.vm?.disableSubmit !== 'undefined') tmpAngular.element(reportServiceFrame).scope().vm.disableSubmit = false;
                }
            }
        },
        reloadControlColors(fromPreparePdfForm) {
            if (this.useNewModules && !fromPreparePdfForm) return;
            if ((!this.useNewModules && !this.pdfLoaded) || this.isPdfOnly) return;
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            for (let i in forms) {
                if (!forms[i].name) continue;
                // if (viewer.formFieldCollections[i].visibility === 'hidden' && !this.useNewModules) continue;
                /* if (
                    (viewer.formFieldCollections[i].type === 'SignatureField' || viewer.formFieldCollections[i].type === 'InitialField')
                    && (
                        (viewer.formFieldCollection && viewer.formFieldCollection[i] && viewer.formFieldCollection[i].signatureType === 'Text')
                        || (viewer.formFieldCollection && viewer.formFieldCollection[i] && viewer.formFieldCollection[i].signatureType === 'Image'))) {
                    if (viewer.formFieldCollections[i].value) {
                        let tmpID = viewer.formFieldCollections[i].id;
                        let tmpWrapper = document.getElementById(tmpID);
                        if (tmpWrapper) {
                            tmpWrapper.style.backgroundColor = 'transparent';
                            tmpWrapper.style.border = 'none';
                            tmpWrapper.parentElement.style.backgroundColor = 'transparent';
                            tmpWrapper.parentElement.style.border = 'transparent';
                        }
                    }
                    continue;
                } */
                if (forms[i].name.indexOf('::SIGNER_') !== -1 || (this.signerIndex === 0 && forms[i].name.indexOf('::SIGNER_') === -1)) {
                    let tmp = forms[i].name.split('::SIGNER_');
                    let index = tmp && tmp[1] && parseInt(tmp[1], 10) >= 0 ? parseInt(tmp[1], 10) : 0;
                    // if no signers or only volunteer, do not change the color and disable
                    if (Array.isArray(this.templateSigners) && (this.templateSigners.length === 0 || (this.templateSigners.length === 1 && this.templateSigners[0].SIGNER_EMAIL === '[CURRENT_EMAIL]'))) index = false;
                    if (index !== false) {
                        let prevIsReadOnly = this.realMode === 'viewer' && this.signerIndex !== false && this.signerIndex >= 0 && index !== this.signerIndex;
                        if (this.realMode !== 'viewer') prevIsReadOnly = forms[i].isReadOnly;
                        if (index === 0
                            && this.realMode === 'viewer'
                            && typeof PROJECT_NAME !== 'undefined'
                            && PROJECT_NAME === 'coordinator'
                            && typeof this.templateSigners?.[this.signerIndex]?.SIGNER_PSP !== 'undefined'
                            && parseInt(this.templateSigners[this.signerIndex].SIGNER_PSP, 10) !== 101
                        ) {
                            prevIsReadOnly = true;
                        }
                        let visibility = 'visible';
                        let isFromCoordinator = typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME === 'coordinator';
                        let hideFollowingFromCoordinator = isFromCoordinator
                            && typeof this.templateSigners?.[this.signerIndex]?.SIGNER_PSP !== 'undefined'
                            && parseInt(this.templateSigners[this.signerIndex].SIGNER_PSP, 10) === 101
                            && !this.templateSigners?.[index]?.IS_SIGNED; // eC user will see ony signed following fields
                        // hide all parent fields or signature of child
                        if (
                            this.realMode === 'viewer' && this.signerIndex !== false && this.signerIndex >= 0
                            && (
                                (
                                    (viewer.formFieldCollections[i].type === 'SignatureField' || viewer.formFieldCollections[i].type === 'InitialField')
                                    && index < this.signerIndex
                                    && !viewer.formFieldCollections[i].value
                                )
                                || (index > this.signerIndex && (!isFromCoordinator || hideFollowingFromCoordinator))
                            )
                        ) {
                            visibility = 'hidden';
                        }
                        this.localUpdateFormField(viewer.formFieldCollections[i], { isReadOnly: false, force: true });
                        this.localUpdateFormField(viewer.formFieldCollections[i], { visibility: visibility, force: true });
                        this.localUpdateFormField(viewer.formFieldCollections[i], { backgroundColor: this.getSignerColor(index, forms[i]), force: true });
                        if (forms[i].name.indexOf('CURRENT_DATE') !== -1 || forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1) prevIsReadOnly = true;
                        if (prevIsReadOnly) {
                            this.localUpdateFormField(viewer.formFieldCollections[i], {
                                isReadOnly: true,
                                force: true
                            });
                            /* this.localUpdateFormField(viewer.formFieldCollections[i], {
                                backgroundColor: this.getSignerColor(index, forms[i]),
                                force: true
                            }); */
                        }
                    }
                } else if (this.realMode === 'viewer' && this.signerIndex > 0) {
                    let prevIsReadOnly = true;// viewer.formFieldCollections[i].isReadOnly;
                    let visibility = 'visible';
                    if (
                        !this.useNewModules
                        && (viewer.formFieldCollections[i].type === 'SignatureField' || viewer.formFieldCollections[i].type === 'InitialField')
                    ) {
                        visibility = 'hidden';
                    }
                    let tmpOptions = {
                        backgroundColor: this.getSignerColor(false, forms[i]),
                        isReadOnly: false,
                        visibility: visibility,
                        force: true
                    };
                    this.localUpdateFormField(viewer.formFieldCollections[i], { isReadOnly: false, force: true });
                    this.localUpdateFormField(viewer.formFieldCollections[i], { visibility: visibility, force: true });
                    this.localUpdateFormField(viewer.formFieldCollections[i], { backgroundColor: this.getSignerColor(false, forms[i]), force: true });
                    if (forms[i].name.indexOf('CURRENT_DATE') !== -1 || forms[i].name.indexOf('CURRENT_DATE_TIME') !== -1) prevIsReadOnly = true;
                    if (prevIsReadOnly) {
                        this.localUpdateFormField(viewer.formFieldCollections[i], {
                            isReadOnly: true,
                            force: true
                        });
                        /* this.localUpdateFormField(viewer.formFieldCollections[i], {
                            backgroundColor: this.getSignerColor(false, forms[i]),
                            force: true
                        }); */
                    }
                }
                if (forms[i].isRequired && !forms[i].isReadOnly) {
                    this.localUpdateFormField(viewer.formFieldCollections[i], { borderColor: '#ff0000', force: true });
                } else {
                    this.localUpdateFormField(viewer.formFieldCollections[i], { borderColor: '#000000', force: true });
                }
                let tmpEl = document.getElementById(forms[i].id + '_input_label');
                if (tmpEl) {
                    if (forms[i].visibility === 'hidden') {
                        tmpEl.closest('.foreign-object').classList.add('pdf-element-hidden-visibility');
                    } else {
                        tmpEl.closest('.foreign-object').classList.remove('pdf-element-hidden-visibility');
                    }
                    if (forms[i].type === 'RadioButton') tmpEl.closest('.foreign-object').classList.add('pdf-element-unset-background');
                }
                if (forms[i].type === 'DropdownList' && !forms[i].isReadOnly) {
                    let tmpOptions = document.querySelectorAll('select#' + forms[i].id + ' option');
                    for (let i in tmpOptions) {
                        if (typeof tmpOptions[i].removeAttribute === 'function') tmpOptions[i].removeAttribute('disabled');
                    }
                }
            }
        },
        addCursor() {
            let viewer = this.$refs[this.id].ej2Instances;
            if (viewer.formDesignerModule && typeof viewer.formDesignerModule.addFormField === 'function') {
                let forms = viewer.formFieldCollections;
                this.removeCursor();
                viewer.formDesignerModule.addFormField('Textbox', {
                    name: 'CURSOR_POSITION',
                    tooltip: this.selectedControl.text,
                    bounds: {
                        X: this.cursorPosition.x, Y: this.cursorPosition.y, Width: 16, Height: 16
                    },
                    pageNumber: this.cursorPosition.pageNumber
                });
                if (forms.length) {
                    for (let i = 0; i < forms.length; i++) {
                        if (forms[i].name === "CURSOR_POSITION") {
                            let id = forms[i].id;
                            let formElmt = document.getElementById(id + '_content_html_element');
                            if (formElmt) {
                                for (let i = 0; i < formElmt.children.length; i++) {
                                    formElmt.children[i].style.display = 'none';
                                }
                                formElmt.insertAdjacentHTML('beforeend', '<span class="pdf-blinking-cursor">|</span>');
                            }
                        }
                    }
                }
            }
        },
        removeCursor() {
            let viewer = this.$refs[this.id].ej2Instances;
            let cursorIDs = [];
            if (viewer.formDesignerModule && typeof viewer.formDesignerModule.deleteFormField === 'function') {
                let forms = viewer.formFieldCollections;
                if (forms.length) {
                    for (let i = 0; i < forms.length; i++) {
                        if (forms[i].name === "CURSOR_POSITION") {
                            cursorIDs.push(forms[i].id);
                        }
                    }
                }
                for (let i in cursorIDs) {
                    if (i === 'length') continue;
                    viewer.formDesignerModule.deleteFormField(cursorIDs[i]);
                }
            }
        },
        tryToAddInitial: function (event) {
            let signText = document.getElementById(this.id + '_e-pv-Signtext-box');
            if (!signText && this.tryToAddInitialCount > 10) {
                this.tryToAddInitialCount = 0;
            } else if (!signText) {
                this.$nextTick(() => {
                    this.tryToAddInitialCount++;
                    this.tryToAddInitial(event);
                });
            }
            if (!this.isInitial) {
                let signLabel = document.getElementById('label_' + this.id + '_e-pv-Signtext-box');
                if (signLabel) {
                    signLabel.innerHTML = 'Enter your signature';
                }
            }
            if (signText) {
                signText.addEventListener("keyup", () => {
                    if (!document.querySelectorAll('.e-pv-selected-fontdiv').length) {
                        let firstFontObj = document.getElementById('_font_signature0');
                        if (firstFontObj) {
                            firstFontObj.click();
                        }
                    }
                });
            }
            /* else if (this.initialValue) {
                signText.focus();
                signText.ej2_instances[0].value = this.initialValue;
                signText.ej2_instances[0].respectiveElement.value = this.initialValue;
                signText.__eventList.events[3].listener.call(signText.ej2_instances[0], new Event('keypress'));
            } */
        },
        tryToAddInitialToSignature: function (event) {
            let buttons = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window .e-toolbar-item');
            if (!buttons.length && this.tryToAddInitialToSignatureCount > 10) {
                this.tryToAddInitialToSignatureCount = 0;
            } else if (!buttons.length) {
                this.$nextTick(() => {
                    this.tryToAddInitialToSignatureCount++;
                    this.tryToAddInitialToSignature(event);
                });
            } else {
                if (this.isPdfOnly) {
                    let dialogElmnts = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window');
                    if (dialogElmnts && dialogElmnts[0]) {
                        dialogElmnts[0].parentElement.classList.remove('e-dlg-center-center');
                        let boundingClientRect = this.$el.getBoundingClientRect();
                        let tmpTop = boundingClientRect.top - dialogElmnts[0].clientHeight + 150;
                        if (tmpTop < 0) tmpTop = 0;
                        dialogElmnts[0].style.top = tmpTop + 'px';
                    }
                }
                if (this.isInitial) {
                    buttons[1].click();
                    let tmpTabsHeader = buttons[0].closest('.e-tab-header');
                    if (tmpTabsHeader) {
                        tmpTabsHeader.style.display = "none";
                    }
                    setTimeout(this.tryToAddInitial, 100);
                    let tmpCloseIcon = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window .e-dlg-closeicon-btn');
                    if (tmpCloseIcon && tmpCloseIcon[0]) {
                        tmpCloseIcon[0].onclick = () => {
                            let tmpElmnts = this.$el.querySelectorAll('.is-initial [ctype="pdf"]');
                            if (tmpElmnts && tmpElmnts[0]) {
                                tmpElmnts[0].classList.remove('signature-popup-opened');
                            }
                        };
                    }
                }
                let tmpCloseIcon = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window .e-dlg-closeicon-btn');
                if (tmpCloseIcon && tmpCloseIcon[0]) {
                    tmpCloseIcon[0].addEventListener('click', () => {
                        this.resizeForSignaturePopup(true);
                    });
                }
                let saveBtnElmnts = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window .e-pv-createbtn');
                if (saveBtnElmnts && saveBtnElmnts.length && saveBtnElmnts[0] && typeof saveBtnElmnts[0].innerHTML !== 'undefined') {
                    saveBtnElmnts[0].innerHTML = 'Save';
                    saveBtnElmnts[0].addEventListener('click', () => {
                        this.resizeForSignaturePopup(true);
                    });
                }
                let footerElmnts = document.querySelectorAll('#' + this.id + ' .e-pv-signature-window .e-footer-content');
                if (footerElmnts && footerElmnts.length) {
                    let tmpParent = footerElmnts[0].parentNode;
                    if (tmpParent) {
                        let tmpMessage = document.createElement("div");
                        tmpMessage.classList.add('message-for-save-btn');
                        tmpMessage.innerHTML = this.isInitial ? 'Click to Save Your Initial' : 'Click to Save Your Signature';
                        tmpParent.insertBefore(tmpMessage, footerElmnts[0]);
                    }
                }
                for (let i = 0; i < buttons.length; i++) {
                    buttons[i].onclick = () => {
                        setTimeout(this.tryToAddInitial, 100);
                        let tmpItems = document.querySelectorAll('#pdf_controlSignature_tab .e-item');
                        for (let i = 0; i < tmpItems.length; i++) {
                            tmpItems[i].classList.remove('e-active');
                        }
                    };
                }
                let isElementInViewport = function (el) {
                    let rect = el.getBoundingClientRect();
                    return (
                        rect.top >= 0
                        && rect.left >= 0
                        && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
                        && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
                    );
                };
                let tmpDialogObj = document.querySelectorAll('#' + this.id + ' .e-dlg-container');
                if (tmpDialogObj && tmpDialogObj[0] && !isElementInViewport(tmpDialogObj[0])) {
                    this.isLastPdf = true;
                }
            }
        },
        disableHidePropertyForSignature: function (event, show) {
            let hiddenDDElements = document.getElementsByClassName('e-pv-properties-visibility-edit-prop');
            let hiddenDD = hiddenDDElements && hiddenDDElements.length ? hiddenDDElements[0] : false;

            if (!hiddenDD && this.tryToHideDisableCount > 5) {
                this.tryToHideDisableCount = 0;
            } else if (!hiddenDD) {
                this.$nextTick(() => {
                    this.tryToHideDisableCount++;
                    this.disableHidePropertyForSignature(event, show);
                });
            } else {
                if (event?.field?.name.indexOf('Logged_Data_') !== -1) {
                    // do not allow to change properties for logged data
                    let hideElementForLoggedData = document.querySelectorAll('.e-pv-properties-checkbox-checked-input');
                    for (let i in hideElementForLoggedData) {
                        if (typeof hideElementForLoggedData[i].closest === 'function') {
                            let tmpHideElement = hideElementForLoggedData[i].closest('.e-checkbox-wrapper');
                            if (tmpHideElement) tmpHideElement.style.display = 'none';
                        }
                    }
                    let disableElementForLoggedData = document.querySelectorAll('.e-pv-properties-name-edit-input, .e-pv-properties-value-input');
                    for (let i in disableElementForLoggedData) {
                        if (typeof disableElementForLoggedData?.[i]?.setAttribute === 'function') {
                            disableElementForLoggedData[i].setAttribute('disabled', true);
                        }
                    }
                }
                hiddenDD.style = "display: " + (show ? 'block' : 'none');
            }
        },
        formFieldAdd: function (event) {
            if (this.realMode !== 'editor' && this.realMode !== 'editor-popup') return;
            if (!event || !event.field || isNaN(event.pageIndex)) return;
            let viewer = this.$refs[this.id].ej2Instances;
            if (parseInt(this.selectedSignerIndex, 10) > 0 && event.field.name.indexOf('::SIGNER_') === -1 && event.field.name !== 'CURSOR_POSITION') {
                let tmpName = event.field.name + '::SIGNER_' + this.selectedSignerIndex;
                this.localUpdateFormField(event.field, { name: tmpName });
                this.reloadControlColors(true);
                this.removeCursor();
            }
        },
        formFieldRemove: function (event) {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            if (Array.isArray(forms) && forms.length) {
                for (let i in forms) {
                    if (!forms[i].name) continue;
                    if (i === 'length') continue;
                    let controlName = event.field.name;
                    if (controlName.indexOf("[") !== -1 && controlName.indexOf("]") !== -1) {
                        controlName = controlName.split('[')[0];
                    }
                    if (forms[i].name.indexOf(controlName) !== -1 && event.field.name !== controlName) {
                        viewer.formDesignerModule.deleteFormField(viewer.formFieldCollections[i]);
                    }
                }
            }
        },
        formFieldDoubleClick: function (event) {
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            if (Array.isArray(forms) && forms.length) {
                let tmpElem = forms.find((elem) => elem.id === event.field.id);
                let type = tmpElem ? tmpElem.type : false;
                if (type === "InitialField" || type === "SignatureField") {
                    this.disableHidePropertyForSignature(event);
                } else {
                    this.disableHidePropertyForSignature(event, true);
                }
            }
        },
        clearSignature: function (selectedSignature) {
            if (!selectedSignature || !selectedSignature.id) selectedSignature = this.selectedSignature;
            if (selectedSignature) {
                let viewer = this.$refs[this.id].ej2Instances;
                let annotation = viewer.annotations.find((tmp) => tmp.id === selectedSignature.id + '_content');
                if (typeof annotation?.annotationSettings?.isLock !== 'undefined') {
                    annotation.annotationSettings.isLock = false;
                }
                viewer.clearFormFields(selectedSignature);

                this.fileName = "";
                this.$nextTick(() => {
                    if (this.isPdfOnly) this.hash = '';
                    let fileInputElement = document.getElementById(this.controlName + '_file');
                    if (fileInputElement) {
                        fileInputElement.value = null;
                        if ("createEvent" in document) {
                            let evt = document.createEvent("HTMLEvents");
                            evt.initEvent("change", false, true);
                            fileInputElement.dispatchEvent(evt);
                        }
                        else {
                            fileInputElement.fireEvent("onchange");
                        }
                    }
                    this.checkNavigationFields();
                });
            }
        },
        resizeForSignaturePopup(returnBackHeight) {
            if (this.realMode === 'viewer') {
                let tabListObj = document.getElementById('log_book_entry-tab-list');
                if (tabListObj) {
                    let currentHeight = tabListObj.clientHeight;
                    let resizeHeight;
                    if (currentHeight < 800) {
                        this.heightBeforeResize = currentHeight;
                        resizeHeight = 800;
                    }
                    if (returnBackHeight && this.heightBeforeResize) {
                        resizeHeight = this.heightBeforeResize;
                        this.heightBeforeResize = 0;
                    }
                    if (resizeHeight) {
                        tabListObj.style.height = resizeHeight + 'px';
                        // eslint-disable-next-line camelcase
                        if (typeof _LbeProfile_updateFrame === 'function') _LbeProfile_updateFrame();
                        // eslint-disable-next-line camelcase
                        if (typeof lib_resizeToContent === 'function') lib_resizeToContent(1);
                    }
                }
            }
        },
        closeCtxMenu() {
            let ctx = document.getElementById('SIGNATURE_CONTEXT_MENU_' + this.dataTestId);
            if (ctx && typeof ctx?.ej2_instances?.[0]?.close === 'function') {
                ctx.ej2_instances[0].close();
            }
        },
        formFieldClick: function (event) {
            this.formFielClicked[event.field.name] = true;
            if (event.field.name && event.field.name.indexOf("[") !== -1 && event.field.name.indexOf("]") !== -1) {
                let controlNameWOOptionId = event.field.name.split('[')[0];
                this.formFielClicked[controlNameWOOptionId] = true;
            }
            if (this.realMode === 'viewer' && event && event.field && (event.field.type === "InitialField" || event.field.type === "SignatureField")) {
                if (this.disabled) {
                    event.cancel = true;
                    return;
                }
                let tabListObj = document.getElementById('log_book_entry-tab-list');
                this.tryToAddInitialToSignature();
                if (this.isInitial) {
                    let tmpElmnts = this.$el.querySelectorAll('.is-initial [ctype="pdf"]');
                    if (tmpElmnts && tmpElmnts[0]) {
                        tmpElmnts[0].classList.add('signature-popup-opened');
                    }
                }
                if (!event.field.value) this.resizeForSignaturePopup();
                let viewer = this.$refs[this.id].ej2Instances;
                if (event.field.value && viewer && viewer.nameTable[event.field.id] && !viewer.nameTable[event.field.id].isReadonly) {
                    let formFieldElmt = document.getElementById(event.field.id);
                    if (formFieldElmt) {
                        setTimeout(() => {
                            let formFieldElmtPos = formFieldElmt.getBoundingClientRect();
                            this.selectedSignature = event.field;
                            document.getElementById('SIGNATURE_CONTEXT_MENU_' + this.dataTestId).ej2_instances[0].open(formFieldElmtPos.top, formFieldElmtPos.left);
                            let viewerContainer = document.getElementById(this.id + '_viewerContainer');
                            if (viewerContainer) {
                                viewerContainer.addEventListener('scroll', this.closeCtxMenu);
                            }
                        }, 100);
                    }
                }
            }
            if (this.realMode === 'viewer') return;
            let formdesignerBtn = document.getElementById(this.id + '_formdesigner');
            if (formdesignerBtn && formdesignerBtn.classList && !formdesignerBtn.classList.contains('e-pv-select')) {
                formdesignerBtn.click();
            }
            if (event.field.type === "InitialField" || event.field.type === "SignatureField") {
                event.cancel = true;
            }
        },
        annotationSelect: function () {
            if (this.realMode === 'viewer') return;
            let annotationBtn = document.getElementById(this.id + '_annotation');
            if (annotationBtn && annotationBtn.classList && !annotationBtn.classList.contains('e-pv-select')) {
                annotationBtn.click();
            }
        },
        pageClick(event) {
            let annotationBtn = document.getElementById(this.id + '_annotation');
            if (annotationBtn && annotationBtn.classList && annotationBtn.classList.contains('e-pv-select')) {
                return;
            }
            let viewer = this.$refs[this.id].ej2Instances;
            let forms = viewer.formFieldCollections;
            let clickPosition = {
                x: event.x,
                y: event.y
            };
            let formFieldIsClicked = false;
            for (let i in forms) {
                if (!forms[i].name) continue;
                if (i === 'length') continue;
                if (forms[i]
                    && forms[i].bounds
                    && forms[i].bounds.x
                    && forms[i].bounds.y
                    && forms[i].bounds.width
                    && forms[i].bounds.height)
                {
                    let fieldPosition = {
                        left: forms[i].bounds.x,
                        right: forms[i].bounds.x + forms[i].bounds.width,
                        top: forms[i].bounds.y,
                        bottom: forms[i].bounds.y + forms[i].bounds.height
                    };
                    if (library.isPointInsideRectangle(clickPosition, fieldPosition)) {
                        formFieldIsClicked = true;
                    }
                }
            }
            if (event && !formFieldIsClicked) {
                this.cursorPosition = {
                    pageNumber: event.pageNumber,
                    x: event.x,
                    y: event.y
                };
                this.addCursor();
            }
        },
        zoomChange(event) {
            /* if (this.enableMagnification) {
                // this.zoomValue = Math.round(parseInt(event.zoomValue, 10) / 10) * 10;
                library.showLoadingIndicator('body');
                setTimeout(() => {
                    library.hideLoadingIndicator('body');
                }, 3000);
                return;
            }
            let currentZoomValue = event.zoomValue;
            this.zoomValue = 100;
            this.$nextTick(() => {
                this.zoomValue = currentZoomValue;
                this.$nextTick(() => {
                    if ((this.config.disableFields || this.disableFields) && this.hash) this.disableFieldsFunc();
                });
            }); */
        },
        formFieldMouseover() {
            for (let timeout = 10; timeout <= 500; timeout += 10) {
                setTimeout(() => {
                    let emptyToolTips = document.querySelectorAll('.e-tip-content:empty');

                    for (let i = 0; i < emptyToolTips.length; i++) {
                        emptyToolTips[i].parentElement.style.display = 'none';
                    }
                }, timeout);
            }
        },
        ajaxRequestInitiate(request) {
            if (!this.fileUniqueID) {
                let tmpFileUniqueID = "pdfFileUniqueID_" + library.getUniqueID();
                if (typeof THE_CUSTOMER !== 'undefined') tmpFileUniqueID += '_' + THE_CUSTOMER + '_';
                if (this.pspIds['vol']) tmpFileUniqueID += '_volID_' + this.pspIds['vol'];
                if (this.pspIds['opp']) tmpFileUniqueID += '_oppID_' + this.pspIds['opp'];
                if (this.pspIds['org']) tmpFileUniqueID += '_orgID_' + this.pspIds['org'];
                if (this.pspIds['mem']) tmpFileUniqueID += '_memID_' + this.pspIds['mem'];
                if (this.pspIds['lbe']) tmpFileUniqueID += '_lbeID_' + this.pspIds['lbe'];
                if (this.pspIds['slot']) tmpFileUniqueID += '_slotID_' + this.pspIds['slot'];
                if (this.pspIds['acc']) tmpFileUniqueID += '_accID_' + this.pspIds['acc'];
                if (this.pspIds['aff']) tmpFileUniqueID += '_affID_' + this.pspIds['aff'];
                if (this.pspIds['ae']) tmpFileUniqueID += '_aeID_' + this.pspIds['ae'];
                tmpFileUniqueID += '_empty.pdf';
                this.fileUniqueID = tmpFileUniqueID;
            }
            if (request.JsonData) {
                request.JsonData.fileUniqueID = this.fileUniqueID;
                if (request.JsonData.formDesigner) {
                    let validJson = this.prepareValidJsonToSave(request.JsonData);
                    if (validJson.formDesigner) request.JsonData.formDesigner = validJson.formDesigner;
                }
            }
        },
        ajaxRequestSuccess(event) {
            if (event && event.action === 'Load' && event.data && typeof event.data.pdfRequiredFields === 'string') {
                this.pdfRequiredFields = event.data.pdfRequiredFields.split(',');
            }
            /* if (!this || !this.$refs || !this.$refs[this.id] || !this.$refs[this.id].ej2Instances || this.useNewModules) return;
            if (event && event.action === "RenderPdfPages") {
                this.reloadUsedControls();
            } */
        },
        destroy: function () {
            if (this && this.$refs && this.$refs[this.id] && this.$refs[this.id].ej2Instances && typeof this.$refs[this.id].ej2Instances.destroy === 'function') {
                this.$refs[this.id].ej2Instances.destroy();
            }
            this.destroyed = true;
        },
        hideFormFieldNameIfValueIsSet(fromTimeout) {
            if (fromTimeout) {
                let tmpFormFieldNames = this.$el.querySelectorAll('[id$="_designer_name"]');
                for (let i in tmpFormFieldNames) {
                    if (tmpFormFieldNames?.[i]?.id) {
                        let tmpId = tmpFormFieldNames[i].id.replace('_designer_name', '');
                        if (this.$el.querySelector('input#' + tmpId) && this.$el.querySelector('input#' + tmpId).value) {
                            tmpFormFieldNames[i].classList.add('display-none');
                        } else {
                            tmpFormFieldNames[i].classList.remove('display-none');
                        }
                    }
                }
                return;
            }
            if (this.hideFormFieldNameIfValueIsSetTimeout) clearTimeout(this.hideFormFieldNameIfValueIsSetTimeout);
            this.hideFormFieldNameIfValueIsSetTimeout = setTimeout(() => {
                this.hideFormFieldNameIfValueIsSet(true);
            }, 500);
        },
        formFieldPropertiesChange(event) {
            if (this.realMode === 'editor' || this.realMode === 'editor-popup') {
                this.hideFormFieldNameIfValueIsSet();
                return;
            }
            if (!event.isValueChanged) return;
            let viewer = this.$refs[this.id].ej2Instances;
            let oldValue = typeof event.oldValue === 'string' ? event.oldValue.trim() : event.oldValue;
            let newValue = typeof event.newValue === 'string' ? event.newValue.trim() : event.newValue;
            if (this.realMode === 'viewer' && event && event.field && event.field.id && !library.isEqualObj(oldValue, newValue)) {
                this.pdfIsChanged = true;
                this.pdfFieldsChanged = true;
            }
            if (this.isPdfOnly && this.realMode === 'viewer' && event && event.field && event.field.id && !library.isEqualObj(oldValue, newValue)) {
                if (this.isInitial) {
                    /* if (newValue.indexOf('base64') !== -1) {
                        this.$el.classList.add('saved-image');
                    } else {
                        this.$el.classList.remove('saved-image');
                    } */
                    let tmpElmnts = this.$el.querySelectorAll('.is-initial [ctype="pdf"]');
                    if (tmpElmnts && tmpElmnts[0]) {
                        tmpElmnts[0].classList.remove('signature-popup-opened');
                    }
                }
                let realId = event.field.id.replace('_content', '');
                let realField = viewer.formFieldCollections.find((tmp) => tmp.id === realId);
                if (realField && (realField.type === 'SignatureField' || realField.type === 'InitialField')) {
                    this.save();
                }
            }
            this.checkNavigationFields();
        }
    },
    components: {
        "ejs-pdfviewer": defineAsyncComponent(() => import('@common/syncfusion/pdf'))
    },
    beforeUnmount() {
        if (
            this
            && this.id
            && this.$refs
            && this.$refs[this.id]
            && this.$refs[this.id].ej2Instances
            && typeof this.$refs[this.id].ej2Instances.destroy === 'function')
        {
            this.$refs[this.id].ej2Instances.destroy();
        }
    }
};

componentStore.initComponent(pdfCmp, pdfCfg, '@common/pdf.vue');
export { pdfCfg, pdfCmp };
export default pdfCmp;
</script>

<style scoped>
.pdf-templates-wrapper {
    display: flex;
}
.pdf-templates {
    flex-grow: 9;
    padding: 5px;
    overflow: hidden;
    font-weight: bold;
    cursor: pointer;
}
.pdf-templates-actions .e-control, .pdf-templates-actions .control {
    height: 100%;
}

.pdf-wrapper {
    min-height: 500px;
}

/*:deep(.e-pv-print-document-container),*/
.pdf-viewer-mode  :deep(.e-pv-formdesigner-container),
.pdf-viewer-mode  :deep(.e-pv-open-document-container),
:deep(.e-pv-comment-container),
.pdf-viewer-mode :deep(.e-pv-annotation-container),
.pdf-viewer-mode  :deep([id^="signIcon"]),
:deep(.e-pv-submit),
:deep(.e-pv-comment-container[aria-disabled="true"]),
:deep(.e-pv-annotation-container[aria-disabled="true"]),
:deep(.e-pv-comment-separator-container),
.pdf-viewer-mode :deep(.e-pv-open-separator-container),
.pdf-viewer-mode :deep(.e-pv-show-designer-name),
:deep(.e-pv-text-search-container)
{
    display: none !important;
}
:deep(.current-volunteer-email) {
    padding: 9px;
}
:deep(.e-pv-main-container) {
    min-height: 500px;
}
:deep(.e-pv-sidebar-toolbar-splitter) {
    display: none !important;
}

#pdf-area {
    display: flex;
}

#pdf-wrapper {
    flex-grow: 10;
    position: relative;
}

:deep(.hidden) {
    display: none;
}

.upload-file-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    background-color: lightgrey;
    opacity: 0.8;
    width: 100%;
    height: 100%;
    z-index: 999;
    cursor: pointer;
}

:deep(.upload-file-wrapper a) {
    display: block;
    text-decoration: none;
    text-align: center;
    font-size: 20pt;
    color: black;
    position: relative;
    top: 50%;
}

:deep(.upload-file-wrapper:hover a) {
    text-decoration: underline;
}

#fields-wrapper {
    margin-left: 10px;
    min-width: 300px;
    max-width: 500px;
    min-height: 500px;
}

:deep([name="INSERT"]) {
    text-transform: none !important;
}

:deep(.e-listview .e-list-item.e-active) {
    background-color: #e0e0e0;
}

.pdf-viewer-mode a {
    cursor: pointer;
}
/*:deep(.e-pv-download-document-container) {
    display: none !important;
}*/
:deep(.e-multi-select-wrapper) {
    height: 30px !important;
    overflow: hidden !important;
}
:deep(.pdf-only .e-pv-toolbar),
:deep(.pdf-only .e-pv-annotation-toolbar),
:deep(.pdf-only .e-pv-formdesigner-toolbar),
:deep(.pdf-only .e-pv-sidebar-toolbar),
:deep(.pdf-only .e-pv-sidebar-toolbar-splitter) {
    display: none !important;
}

:deep(.pdf-only .e-pv-viewer-container),
:deep(.pdf-only .e-pv-main-container) {
    min-height: 170px !important;
    height: 170px !important;
    min-width: 340px !important;
    background-color: transparent !important;
    border: none !important;
}
:deep(.pdf-only [ctype="pdf"]) {
    min-height: 150px !important;
    min-width: 350px;
    overflow: visible !important;
    position: relative;
}

:deep(.pdf-only [ctype="pdf"] .e-pv-main-container) {
    overflow: visible !important;
}

:deep(.pdf-only.is-initial [ctype="pdf"]) {
    min-height: 70px !important;
    min-width: 120px !important;
    height: 70px !important;
    width: 120px !important;
    margin-left: 10px !important;
    margin-right: 10px !important;
}

:deep(.display-none) {
    display: none !important;
}

:deep(.is-editor .e-pv-radiobtn-span) {
    display: none !important;
}

:deep(.pdf-only.is-initial [ctype="pdf"].signature-popup-opened) {
    min-height: 150px !important;
    min-width: 350px !important;
    height: 150px !important;
    width: 350px !important;
}

:deep(.pdf-only.is-initial [ctype="pdf"] .e-pv-page-container) {
    height: 70px !important;
}

:deep(.pdf-only.is-initial [ctype="pdf"].signature-popup-opened .e-pv-page-container) {
    height: 150px !important;
}

:deep([ctype="pdf"]) {
    max-width: 850px !important;
    max-height: 1200px !important;
}
:deep(.is-editor [ctype="pdf"]) {
    max-width: unset !important;
    max-height: unset !important;
}
:deep([ctype="pdf"] .e-dlg-container) {
    position: fixed !important;
    max-width: 100% !important;
    right: 0 !important;
    left: 0 !important;
    margin: 0 auto !important;
    padding: 0 50px;
}

:deep([ctype="pdf"] .e-dlg-container > [aria-labelledby]) {
    max-width: 700px !important;
}

:deep(.pdf-only [ctype="pdf"] .e-dlg-container) {
    position: absolute !important;
    max-width: 95% !important;
    right: 0 !important;
    left: 0 !important;
    margin: 0 auto !important;
    min-height: 260px;
}

:deep(.pdf-only.is-last-pdf [ctype="pdf"] .e-dlg-container) {
    bottom: 0 !important;
    top: unset !important;
}

:deep(.pdf-only.is-last-pdf.is-initial [ctype="pdf"] .e-dlg-container) {
    top: 0 !important;
}

:deep(.pdf-only [ctype="pdf"] .e-dlg-overlay) {
    position: fixed !important;
}

:deep(.pdf-only .e-pv-page-div) {
    position: relative !important;
    left: unset !important;
    right: unset !important;
    margin: 10px !important;
}

:deep(.pdf-only .e-pv-signature-dialog-height) {
    justify-content: center;
}

:deep(.pdf-only .e-pv-signature-window) {
    /*max-height: unset !important;*/
}
:deep(.pdf-only .e-pv-viewer-main-container) {
    background-color: transparent !important;
}
:deep(.e-pv-search-bar) {
    min-width: 350px !important;
}
:deep(.e-pv-search-input) {
    max-width: 240px !important;
}
.pdf-viewer-mode :deep(textarea) {
    resize: none !important;
}
:deep(.e-pv-signature-window .e-footer-content button:nth-child(2)) {
    display: none;
}

:deep(.e-pv-radiobtn-container),
:deep(.e-pv-checkbox-container),
:deep(.e-pv-formfield-dropdown),
:deep(.e-pv-formfield-input) {
    opacity: 1 !important;
}

:deep(.e-pv-formfield-dropdown) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 11px !important;
}

/* :deep(.e-pv-checkbox-span.e-pv-cb-checked) {
    width: 6px !important;
    height: 12px !important;
    left: 5px !important;
    top: 0 !important;
    border-width: 2px !important;
} */

/* :deep(.e-pv-radiobtn-span) {
    width: 7px !important;
    height: 7px !important;
    margin: 5px !important;
} */
:deep([name="FIELDS_LIST"] .e-content){
    height: 250px;
    overflow-y: scroll;
}

:deep([name="CONTROLS_LIST"] .e-content){
    height: 200px;
    overflow-y: scroll;
}

:deep([id^=initialIcon]),
:deep([id^=signIcon]) {
    display: none !important;
}
:deep(.e-btn.e-primary) {
    background-color: rgba(25, 118, 210, 0.04) !important;
    position: relative !important;
}
:deep(.signers-wrapper .control) {
    float: left;
}

:deep([name="SIGNERS_EDIT"]) {
    margin-top: 39px !important;
    margin-left: 5px;
}
:deep(.signers-list-main-details .control) {
    margin-right: 5px;
}
:deep(.e-delete-icon::before) {
    content: "\e94a" !important;
}
:deep(.e-icons.e-drag-and-drop-indicator:before) {
    content: "\e22d" !important;
}
:deep(.drag-and-drop-signers) {
    cursor: pointer;
    padding: 9px;
}
:deep(.sub-list-signer-item-container) {
    margin: 5px;
    border: 1px solid lightgrey;
    padding: 10px;
}
:deep(.signer-color) {
    width: 5px;
    height: 70px;
    float: left;
    margin-right: 3px;
}
:deep(.signer-current-email .signer-color) {
    height: 32px;
}
:deep(.drag-and-drop-signers) {
    height: 70px;
}
:deep(.signer-current-email .drag-and-drop-signers) {
    height: 32px;
}
:deep(.drag-and-drop-signers .e-drag-and-drop-indicator) {
    position: relative;
    top: calc(50% - 6px);
}
:deep([name="SIGNERS"]) {
    max-height: 650px;
    overflow-y: auto;
    min-width: 500px;
}
:deep(.signers-actions) {
    margin: 5px;
}
:deep(.signers-actions .control) {
    width: 49%;
}
:deep(.signers-actions .control:first-child) {
    margin-right: 2%;
}
:deep(.e-delete-icon) {
    color: #d64113;
}
:deep(.set-signing-order-checkbox) {
    margin: 5px;
}
:deep(.e-ddt .e-filter-wrap) {
    display: none !important;
}

:deep(.signer-dropdown-color) {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 8px 0 0 5px;
}
:deep(.signer-value-color) {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 0 5px 0 0;
}
:deep(.signer-value) {
    width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
}
:deep(.vue-pdf-forced-controls) {
    display: none;
}
:deep(.e-pv-signature-apperance [for="checkbox1"]),
:deep(.e-pv-signature-apperance [for="checkbox"]) {
    display: none
}

:deep(.pdf-blinking-cursor) {
    font-weight: 100;
    font-size: 24px;
    color: #2E3D48;
    -webkit-animation: 1s blink step-end infinite;
    -moz-animation: 1s blink step-end infinite;
    -ms-animation: 1s blink step-end infinite;
    -o-animation: 1s blink step-end infinite;
    animation: 1s blink step-end infinite;
}

:deep(.message-for-save-btn) {
    text-align: right;
    margin-right: 10px;
    font-size: 14px;
}

@keyframes blink {
    from, to {
        color: transparent;
    }
    50% {
        color: black;
    }
}

@-moz-keyframes blink {
    from, to {
        color: transparent;
    }
    50% {
        color: black;
    }
}

@-webkit-keyframes blink {
    from, to {
        color: transparent;
    }
    50% {
        color: black;
    }
}

@-ms-keyframes blink {
    from, to {
        color: transparent;
    }
    50% {
        color: black;
    }
}

@-o-keyframes blink {
    from, to {
        color: transparent;
    }
    50% {
        color: black;
    }
}

:deep(.e-pv-signature-window) {
    max-height: 100% !important;
    top: 5px !important;
}

:deep(.e-dlg-container.e-pv-signature-dialog-height) {
    max-height: 95% !important;
}

:deep(.e-pv-signature-window .e-dlg-header-content) {
    min-height: 35px !important;
}

:deep(.e-pv-signature-window .e-dlg-content) {
    height: calc(100vh - 65px - 65px - 16px) !important;
    max-height: 386px !important;
    padding-bottom: 0px !important;
}

:deep(.e-pv-signature-window .e-footer-content) {
    min-height: 35px !important;
}

:deep(.e-pv-signature-window .e-footer-content .e-pv-createbtn) {
    color: rgba(0, 0, 0, 0.66) !important;
}

:deep(.e-pv-signature-apperance) {
    margin-top: 0 !important;
    padding-top: 0px !important;
}

:deep(.e-pv-signature-window .e-pv-signature-uploadcanvas) {
    height: 150px !important;
    width: 100% !important;
}

:deep(.e-pv-signature-window .e-pv-sign-upload) {
    top: calc(100% - 50% - 14px) !important;
    left: calc(100% - 50% - 42px) !important
}
:deep(.e-pv-signature-window .e-pv-signature-apperance) {
    position: relative;
}

:deep(.e-pv-signature-window .e-dlg-content > .e-tab) {
    height: 100% !important;
}

:deep(.e-pv-signature-window .e-tab .e-content) {
    height: calc(100% - 52px) !important;
}

:deep(.e-pv-signature-window .e-pv-font-appearance-style) {
    width: 100% !important;
}

:deep(.e-pv-font-sign) {
    max-width: 40%;
    line-height: 68px;
    padding-top: 0px !important;
    font-size: 36px !important;
}
:deep(.e-multi-select-wrapper .e-chips .e-chips-close::before) {
    content: "\e7e9" !important;
}
:deep(.e-input-group .e-clear-icon::before),
:deep(.e-input-group.e-control-wrapper .e-clear-icon::before) {
    content: "\e932" !important;
}
:deep(.e-multi-select-wrapper .e-close-hooker::before) {
    content: "\e7a7" !important;
}
:deep(.is-initial .e-pv-canvas-signature) {
    display: none;
}
:deep(.is-initial .e-dlg-content) {
    height: unset !important;
}
:deep(.is-initial .e-dialog.e-pv-signature-dialog-height) {
    height: unset !important;
}
:deep(.is-initial .e-dialog.e-pv-signature-dialog-height .e-dlg-header-content) {
    padding: 5px 14px !important;
}
:deep(.is-initial .e-dialog.e-pv-signature-dialog-height .e-dlg-content) {
    padding: 0px 8px !important;
}
:deep(.pdf-only/*.is-initial*/ [ctype="pdf"] .e-dlg-container) {
    max-width: 350px !important;
}
:deep(.is-initial .e-float-input, .e-float-input.e-control-wrapper) {
    margin-top: 10px !important;
}
:deep(.is-initial .e-dialog.e-pv-signature-dialog-height .e-footer-content) {
    padding: 0 8px !important;
}

:deep(.is-initial .e-content > div) {
    margin: 5px 2px !important;
}
:deep(.is-initial .e-pv-page-div) {
    width: 100px !important;
    height: 50px !important;
    overflow: hidden;
}
:deep(.is-initial .e-pv-page-div div), :deep(.is-initial .e-pv-page-div img) {
    width: 100px !important;
    height: 50px !important;
}
:deep(.is-initial [id$="_content_html_element"]) {
    left: 0 !important;
    top: 0 !important;
}

/*sdaasdasd*/
:deep(.pdf-only .e-pv-font-appearance-style) {
    display: none;
}
:deep(.pdf-only .e-dlg-content) {
    height: unset !important;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height) {
    height: unset !important;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-dlg-header-content) {
    position: absolute;
    right: 0;
    padding: 2px 2px !important;
    z-index: 1;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-tab .e-tab-header) {
    height: 33px !important;
    min-height: 33px !important;
    margin-top: 3px !important;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-tab .e-tab-header .e-toolbar-items),
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-tab .e-tab-header .e-toolbar-items .e-toolbar-item),
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-tab .e-tab-header .e-toolbar-items .e-toolbar-item .e-tab-wrap),
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-tab .e-tab-header .e-toolbar-items .e-toolbar-item .e-tab-wrap .e-text-wrap)
{
    height: 30px !important;
    min-height: 30px !important;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-dlg-content) {
    padding: 0px 8px !important;
}
:deep(.pdf-only/*.is-initial*/ [ctype="pdf"] .e-dlg-container) {
    max-width: 400px !important;
}
:deep(.pdf-only .e-float-input, .e-float-input.e-control-wrapper) {
    margin-top: 10px !important;
}
:deep(.pdf-only .e-dialog.e-pv-signature-dialog-height .e-footer-content) {
    padding: 0 8px !important;
}

:deep(.pdf-only .e-content > div) {
    margin: 5px 2px !important;
}
:deep(.pdf-only .e-pv-signature-window .e-pv-sign-upload) {
    top: 64px !important;
    left: 150px !important;
}
:deep(.pdf-only [id$='tab_0'] .e-pv-signature-apperance) {
    width: 380px !important;
    height: 150px !important;
    border: 1px dotted rgb(189, 189, 189) !important;
    overflow: hidden;
}
:deep(.pdf-only .e-pv-signature-canvas) {
    border: none !important;
}
:deep(.pdf-only .e-pv-signature-dialog-height .e-dlg-header) {
    display: none;
}
:deep(.is-initial .e-pdfviewer-signatureformfields-signature) {
    background-color: transparent !important;
}
:deep(.aligment-container [disabled]) {
    color: rgba(0, 0, 0, 0.26) !important;
}
:deep(.pdf-field-multiselect) {
    border: 1px dashed;
}
:deep(.form-fields-selection) {
    position:absolute;
    background-color:rgb(218, 234, 247, 0.3);
    z-index: 99999999;
}
:deep(.pdf-viewer-mode .e-pv-page-container) {
    min-height: 160px !important;
}
:deep(.pdf-viewer-mode .foreign-object select) {
    max-width: 100%;
}
:deep(.pdf-element-hidden-visibility) {
    visibility: hidden;
}
:deep(.pdf-element-unset-background) {
    background-color: unset !important;
}
:deep(.e-toolbar-item.e-template > button), :deep(.e-pv-sidebar-toolbar) {
    display: none !important;
}
:deep(.is-editor .e-toolbar-item.e-template > button) {
    display: block !important;
}
:deep([id^=pdfControlWrapper] *) {
    touch-action: auto !important;
}
:deep(.pdf-only.is-initial .e-pv-annotation-canvas) {
    top: 0px;
    left: 0px;
}
:deep(.pdf-only .e-pv-mobilescroll-container) {
    display: none !important;
}
:deep(.is-initial [id$=_diagramAdornerLayer]) {
    display: none !important;
}
:deep(.e-pv-zoom-drop-down-container) {
    display: none !important;
}
:deep(.sticker) {
    position: absolute;
    z-index: 9999;
    top: 90px;
    width: 80px;
    height: 30px;
    padding: 4px 12px;
    line-height: 22px;
    cursor: pointer;
    font-weight: bold;
    text-transform: uppercase;
}
:deep(.sticker:before) {
    content: "";
    position: absolute;
    right: -20px;
    bottom: 0;
    width: 0;
    height: 0;
    border-left: 20px solid;
    border-top: 15px solid transparent;
    border-bottom: 15px solid transparent;
}
:deep(.signer-color-1) {
    background-color: #7bb8f6;
    box-shadow: 0px 5px 10px #7bb8f6;
}
:deep(.signer-color-1:before) {
    border-left-color: #7bb8f6;
}
:deep(.signer-color-2) {
    background-color: #ffd65b;
    box-shadow: 0px 5px 10px #ffd65b;
}
:deep(.signer-color-2:before) {
    border-left-color: #ffd65b;
}
:deep(.signer-color-3) {
    background-color: #74cde7;
    box-shadow: 0px 5px 10px #74cde7;
}
:deep(.signer-color-3:before) {
    border-left-color: #74cde7;
}
:deep(.signer-color-4) {
    background-color: #b56bd7;
    box-shadow: 0px 5px 10px #b56bd7;
}
:deep(.signer-color-4:before) {
    border-left-color: #b56bd7;
}
:deep(.signer-color-5) {
    background-color: #64c7b8;
    box-shadow: 0px 5px 10px #64c7b8;
}
:deep(.signer-color-5:before) {
    border-left-color: #64c7b8;
}
:deep(.signer-color-6) {
    background-color: #f5a47a;
    box-shadow: 0px 5px 10px #f5a47a;
}
:deep(.signer-color-6:before) {
    border-left-color: #f5a47a;
}
:deep(.signer-color-7) {
    background-color: #74ace7;
    box-shadow: 0px 5px 10px #74ace7;
}
:deep(.signer-color-7:before) {
    border-left-color: #74ace7;
}
:deep(.signer-color-8) {
    background-color: #cdd96d;
    box-shadow: 0px 5px 10px #cdd96d;
}
:deep(.signer-color-8:before) {
    border-left-color: #cdd96d;
}
:deep(.signer-color-9) {
    background-color: #ff9980;
    box-shadow: 0px 5px 10px #ff9980;
}
:deep(.signer-color-9:before) {
    border-left-color: #ff9980;
}
:deep(.signer-color-10) {
    background-color: #e573e5;
    box-shadow: 0px 5px 10px #e573e5;
}
:deep(.signer-color-10:before) {
    border-left-color: #e573e5;
}

</style>

<style>
.saved-image .pdf-only.is-initial .e-pv-annotation-canvas {
    top: -36px;
    left: -116px;
}
.pdf-signer-dd .signer-dropdown-color {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 8px 0 0 5px;
}
@media print{
    .pdf-only {
        break-inside: avoid;
    }
    .pdf-only .e-pv-page-div [id$="diagramAdornerLayer"], .pdf-only .e-pv-page-div .e-pv-page-canvas, .pdf-only .e-pv-page-div .e-pv-text-layer {
        display: none !important;
        position: relative !important;
    }
    .pdf-only .e-pv-page-div canvas{
        position: relative !important;
    }

    .pdf-viewer-mode:not(.pdf-only) {
        max-width: 700px;
    }

    .pdf-viewer-mode:not(.pdf-only) .e-pv-page-div {
        left: 0 !important;
    }

    .pdf-viewer-mode .e-pv-page-container {
        overflow: hidden !important;
        min-height: 160px !important;
    }
}
</style>
